import type {
    EnterpriseBillingAlert,
    MailboxActionSchedule,
    PaymentNoticeState,
    PlanAllowance,
    PlanLimitNeedsYouItem,
} from '@zerohuman/shared'
// Use the stored task contracts, including custom successor outcomes.
import {
    type AgentConnection,
    type AgentConnectionsResponse,
    type AgentProvider,
    type AgentSubscriptionPlan,
    type AgentUsage,
    type AgentUsageByConnection,
    type ChatStep,
    canonicalModelTier,
    type ExecutionBoard,
    type ExecutionCloseCompletedResult,
    type ExecutionCounts,
    type ExecutionDetail,
    type ExecutionResource,
    type ExecutionSummary,
    type GateChoice,
    MODEL_TIERS,
    type MustGateEntry,
    NO_OUTCOME_NAMED,
    type RunCost,
    type TaskSchedule,
    type TaskSuccessor,
    type ToolEntry,
    type ToolSlot,
    type ToolSlotAction,
    type WebhookDetail,
    type WebhookSummary,
    type WorkItemCard,
    type WorkItemOrigin,
} from '@zerohuman/shared'
import type { UiMode } from '@/composables/useUiMode'
import { clearGateDecisionDraft } from '@/lib/gateDecisionDraft'
import type { Kpi } from '@/lib/kpi'
import { onboardingSession } from '@/lib/onboardingSession'
import type { TeamSetupView } from '@/lib/teamSetup'

export type {
    AgentConnection,
    AgentConnectionsResponse,
    AgentConnectionTarget,
    AgentProvider,
    AgentSubscription,
    AgentSubscriptionPlan,
    AgentUsage,
    AgentUsageBalance,
    AgentUsageByConnection,
    AgentUsageDay,
    AgentUsageQuota,
    ExecutionBoard,
    ExecutionDetail,
    ExecutionLane,
    ExecutionResource,
    ExecutionRun,
    ExecutionStatus,
    ExecutionSummary,
    GateChoice,
    RunCost,
    TaskSchedule,
    TaskSuccessor,
    ToolEntry,
    ToolSlot,
    ToolSlotAction,
    WebhookDetail,
    WebhookStatus,
    WebhookSummary,
} from '@zerohuman/shared'
export {
    AGENT_CONNECTION_DESCRIPTION_MAX,
    AGENT_CONNECTION_NAME_MAX,
    AGENT_CONNECTION_USAGE_IDS_MAX,
    AGENT_PROVIDER_SUBSCRIPTION_PLANS,
    AGENT_SUBSCRIPTION_PLANS,
    AGENT_SUBSCRIPTION_PRICES,
} from '@zerohuman/shared'

/** One plan off the ladder in docs/pricing.md, as the header badge needs it. */
export type MeTier = { id: string; name: string; tagline: string }

/** One of the enterprises the signed-in person holds, as the picker cards and the map's enterprise circles draw it. */
export type MeEnterprise = {
    id: string
    slug: string
    name: string
    tier?: MeTier | null
    /** `/v1/enterprises/:id/avatar?v=…`, or null without a picture. Optional: an older API sends none. */
    avatarUrl?: string | null
    memberCount: number
    teamCount: number
}

/**
 * What waits on the signed-in person in one enterprise they hold (`GET /v1/enterprises/attention`), as the picker and
 * the nav's badges show it (#678): chat threads they own with unread replies, gates waiting on the CEO, plans awaiting
 * the CEO and pending recommendations, each an owner's or raised by a run the person asked for, and the mail in their
 * own inbox they have not opened yet. A count their grant there does not reach is 0.
 */
export type EnterpriseAttention = {
    enterpriseId: string
    chats: number
    gates: number
    plans: number
    recommendations: number
    /**
     * Mail in their own inbox there they have not opened yet, where an owner gave them an address: the Inbox badge's
     * count. Optional: an API older than the picker's Inbox count sends none, which reads as 0.
     */
    mail?: number
}

export type { EnterpriseBillingAlert } from '@zerohuman/shared'

/** A way into the product (#636): a provider's sign-in, or a code emailed to an address. */
export type SignInWay = 'github' | 'google' | 'microsoft' | 'email'

/** One way the signed-in person can sign in, as `GET /auth/identities` lists it: never the provider's own id. */
export type SignInIdentity = { provider: SignInWay; email: string | null }

export type Me = {
    /**
     * `avatarUrl` is their GitHub picture. Optional: an older API sends none. `githubLogin` is null for somebody who
     * signed in without GitHub (#636).
     */
    user: {
        id: string
        githubLogin: string | null
        name: string | null
        avatarUrl?: string | null
        /**
         * The Standard/Advanced mode saved on the person (#1949): null where they never chose, which reads as
         * Standard. Optional: an API older than #1949 sends none.
         */
        uiMode?: UiMode | null
        /**
         * The hours this person is emailed about Needs you in (#2006), null where they set none. Optional: an API older
         * than #2006 sends none.
         */
        workingHours?: WorkingHours | null
        /** Whether this person administers the platform itself, which is what offers them the Staff area. */
        platformAdmin?: boolean
    }
    /** The enterprise this session is signed into: the one chosen, or the oldest held until one is. */
    enterprise: { id: string; slug: string; name: string; avatarUrl?: string | null }
    /** Every enterprise the person holds. Optional: an API older than enterprise switching reports none. */
    enterprises?: MeEnterprise[]
    /** False while the person holds several and this session has not picked one, which is when the picker shows. */
    enterpriseChosen?: boolean
    /** Which plan this enterprise is on. Optional: an API older than the tier table reports none. */
    tier?: MeTier | null
    runner?: { driver: string; image: string }
    /** `pr-61` on a per-PR preview stack (infra/preview), null anywhere else. */
    preview?: string | null
    /** Product version of the API serving this session, e.g. `0.2.17`. */
    version?: string | null
    /** Commit the running build came from, when the platform exposes it. The version alone trails a merge. */
    commit?: string | null
    /**
     * What this person may reach (#622), in the same `<resource>:<level>` vocabulary the People screen shows
     * (#618) and `SessionGuard` checks every request against (#617). An owner holds the whole enterprise with
     * nothing enumerated; otherwise `scopes` is the entire reach. Both optional: an API older than #622
     * reports neither, and the portal then restricts nothing rather than hiding its own navigation.
     */
    isOwner?: boolean
    scopes?: string[] | null
    /**
     * The person's own mailbox address in this enterprise (#882), null while an owner has given them none. Optional:
     * an API older than #882 sends none.
     */
    mailAddress?: string | null
    /** #888: the OS MCP server's endpoint, the address an MCP client is given to connect to this enterprise. */
    mcpUrl?: string
    /** True while Zero Human staff have paused the enterprise this session is in: nothing runs until they unpause it. */
    enterprisePaused?: boolean
    /**
     * Who brought this person into the enterprise (#630), so they know whom to ask for more: an identity only,
     * never that person's grant. Null where nobody can be named; `inviterUnavailable` says why. Optional: an API
     * older than #630 reports none.
     */
    invitedBy?: MeInviter | null
    /**
     * Which kind of null `invitedBy` is (#630). True means an inviter is recorded but cannot be named now, because
     * they were removed from the enterprise or are no longer a person, or the lookup failed. False or absent
     * means nobody is recorded: the enterprise's creator, or a membership older than invitations.
     */
    inviterUnavailable?: boolean
    /**
     * #2218: the enterprise this session's own sign-in founded, which the first screen after it tells the person
     * about, with the invitations waiting for an address that sign-in proved. Null for every other session: an
     * existing member's, an invitation accepted, the founder's later sign-ins. Optional: an API older than #2218 sends
     * none, which reads the same as null.
     */
    foundedEnterprise?: FoundedEnterprise | null
}

/**
 * An invitation waiting for an address a founding sign-in proved (#2218): which enterprise sent it, and where it went.
 * Never a link or a token: only its hash is kept, and pointing to an invitation admits nobody.
 */
export type WaitingInvitation = { enterpriseName: string; email: string }

/** The enterprise a session's own sign-in founded (#2218), as `GET /auth/me` says it. */
export type FoundedEnterprise = { id: string; name: string; invitations: WaitingInvitation[] }

/** An inviter as `GET /auth/me` names them (#630): who they are, and nothing of what they hold. */
export type MeInviter = { name: string | null; githubLogin: string; avatarUrl?: string | null }

export type TeamCharter = {
    opportunity: string
    purpose: string
    success: string
    metric: string
    kit: string
    roles: string
    timeline: string
}

export const CHARTER_QUESTIONS: Array<{ key: keyof TeamCharter; label: string }> = [
    { key: 'opportunity', label: '1. What opportunity or challenge is this team for?' },
    { key: 'purpose', label: '2. Purpose, in one phrase or a short paragraph' },
    { key: 'success', label: '3. What does success look like?' },
    { key: 'metric', label: '4. How will we measure it? Which KPIs, each with a target?' },
    { key: 'kit', label: '5. What skills, tools, models, and assets does it need?' },
    { key: 'roles', label: '6. Which roles?' },
    { key: 'timeline', label: '7. What is the timeline?' },
]

export type Team = {
    id: string
    slug: string
    name: string
    leadMemberId: string | null
    leadName: string | null
    cooActingAsLead: boolean
    charter: TeamCharter
    status: string
    /** This team's own concurrent-run override; null (or omitted) inherits the enterprise limit. */
    concurrentRunLimit?: number | null
    /** The team's on/off switch (#1808, #1810), separate from `status`. The column defaults on, so omitted reads as on. */
    enabled?: boolean
}

/** The enterprise-wide concurrent-run limit, and this team's own override (null = inherits it). */
export type ConcurrencySetting = { enterpriseLimit: number; teamLimit: number | null }

/** The model tier chat turns run on, and the tiers there are to choose from. */
export type ChatModelTierSetting = { chatModelTier: string; tiers: string[] }

/** How much of its memory a chat reply reads, and the depths there are to choose from with the cap each sets. */
export type ChatMemoryDepthSetting = { chatMemoryDepth: string; depths: { depth: string; maxTokens: number }[] }

/**
 * How many turns in a row a chat member may take on its own, with nobody writing (#287): the limit in force, 5 when
 * the enterprise never chose, 0 when it is off.
 */
export type ChatUnpromptedTurnLimitSetting = { limit: number }

/** The company's own metrics — the numbers that belong to no single team. Teams and tasks carry theirs. */
export type EnterpriseMetricsSetting = { metrics: string[] }
export type EnterpriseKpisSetting = { kpis: Kpi[] }
/**
 * What saving a company or team KPI list answers: the list as stored, and `awaitingSignOff` naming every KPI still
 * waiting for a person's sign-off with the API's sentence about it, or null when none is.
 */
export type KpiListSaved = EnterpriseKpisSetting & { awaitingSignOff?: { kpiIds: string[]; message: string } | null }

/** The IANA zone the company runs in (#884): what a schedule that follows the enterprise fires against. */
export type EnterpriseTimezoneSetting = { timezone: string }
/** The enterprise name an owner renames it to; the slug never moves with it. */
export type EnterpriseNameSetting = { name: string }

/** The repository that hosts the company's public changelog (#1915), `null` when it has none. */
export type EnterpriseChangelogRepoSetting = { changelogRepo: string | null }
/** Whether quiet source updates apply to linked clones by themselves (#759): on unless the enterprise turned it off. */
export type EnterpriseAutoUpdateClonedTasksSetting = { autoUpdateClonedTasks: boolean }

/** A person's own working hours (#2006): 24-hour `HH:MM` times, read in `timezone`. */
export type WorkingHours = { start: string; end: string; timezone: string }

/** The enterprise's working hours (#2006), read in the enterprise timezone: both `HH:MM`, or both null when unset. */
export type EnterpriseWorkingHours = { start: string | null; end: string | null }

/** A job across the company: one per slug, held by any number of members on any number of teams. It belongs to no team. */
export type Role = {
    id: string
    slug: string
    name: string
}

export type MemberTeam = {
    id: string
    slug: string
    name: string
    lead: boolean
}

/** One vendor a member's roles' tasks need and no binding of theirs reaches (`GET /v1/members/:id/tool-gaps`). */
export type MemberToolGap = {
    /** The binding target: the vendor prefix a binding's tool type names. */
    vendor: string
    /** The declared tool names of that vendor the tasks need. */
    tools: string[]
    /** Slugs of the member's roles whose tasks need it. */
    roles: string[]
    /** Slugs of the role tasks that need it. */
    tasks: string[]
    /** The same roles by display name, what Standard mode prints (slugs are Advanced only). */
    roleNames?: string[]
    /** The same tasks by display name, what Standard mode prints. */
    taskNames?: string[]
}

/** One missing-binding notice in Needs you (`GET /v1/gates/tool-not-bound`): a member's runs parked for one vendor. */
export type ToolNotBoundNeedsYouItem = {
    enterpriseId: string
    memberId: string
    memberName: string
    vendor: string
    tools: string[]
    sentence: string
    buttonLabel: string
    buttonPath: string
    since: string
}

/**
 * One owner's KPIs waiting for sign-off in Needs you (`GET /v1/gates/kpi-sign-offs`): the company's, a team's, a role's
 * or a task's, with the scheduled tasks that record nothing for them meanwhile and where to sign them off.
 */
export type KpiSignOffNeedsYouItem = {
    key: string
    enterpriseId: string
    layer: 'company' | 'team' | 'role' | 'task'
    ownerId: string | null
    ownerName: string
    kpis: { id: string; name: string }[]
    scheduledSources: string[]
    sentence: string
    buttonLabel: string
    buttonPath: string
}

/** One item a run proposed, as its batch lists it in Needs you and in chat (#849). */
export type ProposedWorkItem = {
    id: string
    teamId: string
    title: string
    kind: string
    priority: string | null
    body: string
    createdAt: string
}

/**
 * One run's proposed work items still waiting on a person (`GET /v1/gates/proposed-work`, #849): one decision however
 * many items it holds, keyed by the run. Gone once its last item is decided, wherever that happened.
 */
export type ProposedWorkBatch = {
    runId: string
    enterpriseId: string
    teamId: string
    teamName: string | null
    leadMemberId: string | null
    leadName: string | null
    taskId: string | null
    taskName: string | null
    runStatus: string | null
    items: ProposedWorkItem[]
    since: string
}

/** A person's answer to a proposed item: it moves to `ready`, `held` or `dropped`. */
export type ProposedWorkChoice = 'approve' | 'hold' | 'drop'

export type Member = {
    id: string
    name: string
    /**
     * What to show this member as: their name, or the title of a role they hold while they have none (#1815: the COO
     * an enterprise is made with is unnamed and shows as "COO"). Label a member with `memberLabel`, not `name`.
     */
    displayName?: string
    email: string
    persona: string | null
    /** Public copy for the About page, or null when none is set. Separate from `persona`, which is never published. */
    description?: string | null
    avatarUrl: string | null
    roles: Role[]
    leadOf: string[]
    teams?: MemberTeam[]
    /** Every task of a role this member holds, whatever team they sit on: the tasks whose runs can go to them. */
    ownedTasks?: TaskLink[]
    /** This member's most recently started run still in `ACTIVE_RUN_STATUSES`, or null when idle. */
    activeRun?: MemberActiveRun | null
    /** How many tasks this member may run at once, as set. Optional: an older API sends none. */
    concurrencyCap?: number
    /** How many they run at once now: the setting, held to the plan's figure. */
    effectiveConcurrencyCap?: number
    /**
     * The most the setting may be: the plan's figure for one member, held to the enterprise's concurrent runs. 1 on
     * every plan that lets a member run one task at a time, and then the page offers no setting.
     */
    maxConcurrencyCap?: number
}

/** A member's active run, as the actor-card member view shows it: which task, and its status. */
export type MemberActiveRun = { id: string; taskId: string; taskName: string; status: string }

/** One task circle on the org map. */
export type OrgMapTask = { id: string; slug: string; name: string; status: string }

/** One role circle: the tasks that role runs on its team. */
export type OrgMapRole = { id: string; slug: string; name: string; tasks: OrgMapTask[] }

/**
 * One member circle: the roles that member holds on the team whose circle contains them. `displayName` is what to
 * label it (#1815: the unnamed COO reads "COO"); optional, since an older API sends none. Label with `memberLabel`.
 */
export type OrgMapMember = {
    id: string
    name: string
    displayName?: string
    avatarUrl: string | null
    roles: OrgMapRole[]
}

export type OrgMapTeam = {
    id: string
    slug: string
    name: string
    leadMemberId: string | null
    members: OrgMapMember[]
}

/** The whole company in one call, as `GET /orgs` returns it: enterprise → teams → members → roles → tasks. */
export type OrgMap = {
    enterprise: { id: string; slug: string; name: string; avatarUrl?: string | null }
    teams: OrgMapTeam[]
    unplaced?: {
        /** Members on no team at all, with the roles they hold: the map draws them at enterprise level. */
        members: Array<OrgMapMember & { reason: 'no_team' }>
        roles: Array<{ id: string; name: string; reason: 'no_member' }>
        /** A task belongs to a role, not a team: it is placed once anyone holds its role, on any team or none. */
        tasks: Array<{
            id: string
            name: string
            reason: 'no_role' | 'no_such_role' | 'role_not_held'
        }>
    }
}

export type SpendSnapshot = {
    caps: Array<{
        id: string
        layer: string
        layerId: string
        moneyCentsDay: number | null
        moneyCentsMonth: number | null
        moneyCentsRun?: number | null
        runCountDay: number | null
        maxModelTier: string | null
    }>
    /** False when the Spend page switch is off: caps are stored but not enforced. */
    enabled: boolean
    spentTodayCents: number
    tightestDayCapCents: number | null
    usedRatio: number
    warning80: boolean
    runsToday: number
    tightestRunCountDay: number | null
    tightestMaxModelTier: string | null
    capChangeGated: boolean
}

export type TaskProposal = {
    id: string
    taskId: string
    sourceRunId: string
    siRunId: string
    status: 'pending' | 'blocked_widen' | 'applied' | 'rejected'
    checks: { ok: boolean; reasons: string[] }
    proposedTools: string[]
    proposedGates: Array<{ tool: string; owner: string; approval: string }>
    proposedSpend: { moneyUsdPerRun?: number; maxModelTier?: string }
    proposedSkillMd?: string
    /**
     * Tool types `proposedTools` needs that have no `ToolBinding` anywhere in the enterprise (os#231):
     * computed live on every read, so it clears itself once the CEO connects the tool, without the
     * proposal being regenerated. Approving is still allowed while this is non-empty.
     */
    missingConnections: string[]
}

export type TaskRecommendation = {
    canImplement?: boolean
    id: string
    taskId: string
    sourceRunId: string
    siRunId: string
    title: string
    summary: string
    details: string
    rationale: string
    confidence: number
    status: 'pending' | 'implementing' | 'complete' | 'error' | 'declined' | 'actioned'
    implementRunId: string | null
    errorMessage: string | null
    createdAt: string
    decidedAt: string | null
    /** A person's id when decided in the portal; null when a task run decided it (`decidedRunId`). */
    decidedBy?: string | null
    decidedRunId?: string | null
    /** Why it was declined, or what the action was. */
    decisionNote?: string | null
    issueUrl?: string | null
    lane?: 'os' | 'repo-commands' | 'local-stack' | 'task-record' | null
}

/** A pending recommendation as the Self-Improvement page lists it: the task it is for comes with the row. */
export type PendingRecommendation = TaskRecommendation & {
    taskName: string | null
    taskSlug: string | null
}

export type Blockers = {
    /**
     * Waiting gates left this payload when gates moved to their own page: the current API sends none of
     * these three, and nothing here counts or renders them. They stay declared, and optional, because an API
     * older than that move still sends them — a portal deployed ahead of the API reads that body too.
     */
    waitingGates?: GateRow[]
    waitingOverHour?: GateRow[]
    agedGates?: GateRow[]
    /** One page of uncleared failed runs; `failedTotal` counts them all. */
    failed: RunRow[]
    failedTotal: number
    failedPage: number
    stalled: RunRow[]
    /** Rows per page for `failed`. */
    pageSize: number
    paused: RunRow[]
    /** False when Spend is switched off: paused spend is not a blocker. */
    spendEnabled?: boolean
    /** Parked on something outside the task; `blockClass` says whether it resumes by itself. */
    blocked: RunRow[]
    /** `pnpm drain` is in effect: nothing new starts until `pnpm undrain`. */
    draining?: boolean
    /** Pipelines (issue/PR) whose newest run failed and nothing picked the work up again. */
    stopped: RunRow[]
    models?: ModelCheck[]
    /** One enterprise-level condition, not one row per stalled run. */
    capacity?: CapacityBlocker[]
    taskProblems?: TaskProblem[]
    missingLead: Team[]
    /**
     * Deprecated: the free-text "measures nothing" row, replaced by `kpiHealth` (#899). The current API no
     * longer sends it, and nothing here counts or renders it. It is kept, as optional, for the same reason as
     * `waitingGates`: an older API may still send it.
     */
    missingMetric?: Team[]
    /** Teams whose weekly `team-sprint-retro` is more than a cadence plus the grace window overdue (os#373). */
    overdueRetro?: Team[]
    /**
     * AI account connections that were dropped or revoked (#1719), or that a provider refusal blocked (#624): one row
     * each, with a Reconnect action. Only a blocked one carries the last four fields: whose it is (`layer`, the
     * enterprise's shared connection or a member's own), the closed reason code, that reason in plain words and the
     * step that clears it.
     */
    lostConnections?: {
        connectionId: string
        provider?: string
        name: string
        ownerName: string
        lostAt: string
        layer?: string
        reasonCode?: string
        reason?: string
        step?: string
    }[]
    /** KPIs that need attention: no key KPI or target, overdue for a reading, or AMBER/RED with no pending proposal. */
    kpiHealth?: KpiHealthRow[]
    /** #431: findings raised to a person because no single team holds the function they were sent to; absent when none. */
    raised?: RaisedWorkItem[]
    runs: RunRow[]
    /**
     * The waiting gate a person decides for each escalated run, by run id: the one its escalating run opened, or one
     * asked about an earlier run of its pipeline. Absent for a run no gate holds a decision for, and from an older API.
     */
    decisionGates?: Record<string, DecisionGate>
}

/** A waiting gate a Blockers row links to: the gate, the run that opened it (whose page decides it) and its title. */
export type DecisionGate = { gateId: string; runId: string; title: string | null }

/**
 * One row of the Waiting list (#1457), as `GET /v1/waiting` sends it: a piece of open work, keyed on its issue/PR
 * pipeline, whose last run has ended with nothing live or queued after it, no decision waiting, nothing on Blockers
 * and no finish recorded. Rows do not age off while the work is open.
 */
export type WaitingRow = {
    /** The pipeline key: `owner/name#<number>` for an issue or pull request, `work:<id>` for a work item. */
    pipeline: string
    repo: string | null
    number: number | null
    /** The work item the work is keyed on, and its title, or null for an issue or pull request. */
    workItem: string | null
    workItemTitle: string | null
    /** The newest run on the work, and the task that ran it. */
    runId: string
    taskId: string
    /** How that run ended: its terminal status (`succeeded`, `failed`, `cancelled`) or `decision_rejected`. */
    endedAs: string
    /** The reason it gave, and what the work waits on: each null when it named none. */
    reason: string | null
    waitsOn: string | null
    /** When the last run ended (ISO), and how long ago that was when the API answered. */
    since: string
    waitedMs: number
}

/** One item of a team's work list, as `GET /v1/work-items/:id` answers it (#1827, #1830). */
export type WorkItemRow = {
    id: string
    teamId: string
    title: string
    kind: string
    state: WorkItemCard['state']
    priority: WorkItemCard['priority']
    position: number
    body: string
    links: string[]
    /** Who or what added it: `{ kind: 'member' | 'person' | 'api_key' | 'run', ... }`. */
    addedBy: Record<string, unknown>
    createdAt: string
    updatedAt: string
    closedAt: string | null
    closeReason: string | null
    /** The run holding it and since when, or null; absent on an answer recorded before claims existed. */
    claimedByRunId?: string | null
    claimedAt?: string | null
    /** The parent item it sits under (one level only), or null; absent on an answer recorded before parents existed. */
    parentId?: string | null
    /** A rollup of its children, closed ones included: how many, and how many in each state. */
    children?: { total: number; byState: Partial<Record<WorkItemRow['state'], number>> }
    /** The status of the run holding it, as it is now, or null when no run holds it. */
    claimedByRunStatus?: string | null
    /** #431: the team and run that sent it, for an item another team's run added; absent or null otherwise. */
    origin?: WorkItemOrigin | null
}

/**
 * #431: a finding a run sent to another team by business function that no single team could take, raised to a
 * person on Blockers: the whole finding, what was asked for, the teams that matched, and the team and run it came from.
 */
export type RaisedWorkItem = {
    id: string
    title: string
    kind: string
    body: string
    links: string[]
    forFunction: string
    reason: 'no_matching_team' | 'several_matching_teams'
    candidates: { teamId: string; name: string }[]
    from: WorkItemOrigin
    raisedAt: string
}

/** A page of a team work list, as GET /v1/teams/:teamId/work-items answers it. */
export type WorkItemListPage = { data: WorkItemRow[]; total: number; page: number; limit: number; pages: number }

/** One change to a work item, as `GET /v1/work-items/:id/history` lists them, oldest first. */
export type WorkItemHistoryEntry = {
    id: string
    actor: Record<string, unknown>
    at: string
    changes: Record<string, { before: unknown; after: unknown }>
    /** #431: on the first entry of an item another team's run sent only: the team and run it came from. */
    origin?: WorkItemOrigin
}

/** `GET /v1/waiting`: the rows, and the count the sidebar shows beside Blockers and Gates, from the same answer. */
export type WaitingList = { rows: WaitingRow[]; total: number }

/** One KPI blocker row, as `GET /v1/blockers` sends it. */
export type KpiHealthRow = {
    /** `awaiting_decision`: a KPI proposal waiting on the CEO (#902), whatever the KPI's own condition. */
    condition: 'no_key_kpi' | 'no_target' | 'overdue' | 'light' | 'awaiting_decision'
    /** Null on a company proposal's row (#366): a company KPI belongs to no team. */
    team: Team | null
    kpi: { id: string; name: string } | null
    layer: string | null
    light: 'green' | 'amber' | 'red' | null
    trend: { direction: 'up' | 'down' | 'flat'; percent: number } | null
    owner: { role: 'team_lead'; memberId: string } | { role: 'coo' } | { role: 'ceo' }
}

export type ModelCheck = { tier: string; provider: string; model: string; state: 'ok' | 'missing' | 'unknown' }

/** Which limit a connection ran out of: a rate-limit window, a periodic spend limit, or a prepaid balance. */
export type CapacityLimitKind = 'rate_limit' | 'spend_limit' | 'balance'

/**
 * A bound LLM connection whose own usage endpoint reports no capacity left: which limit ran out, its own label, when
 * it resets (null for a balance, which does not), the connection's own name and, for a key, the amount left on it
 * (#1772). Everything after `resetsAt` is absent from an API older than the fields, which reads as a rate limit.
 */
export type CapacityBlocker = {
    connectionId: string
    provider: string
    resetsAt: string | null
    kind?: CapacityLimitKind
    label?: string
    period?: 'daily' | 'weekly' | 'monthly' | null
    connectionName?: string | null
    remainingCents?: number | null
}

export type DefinitionIssue = {
    severity: 'error' | 'warning'
    code: string
    message: string
    /** The slug of the task the issue names that this enterprise does not have, where it names one. */
    missingTask?: string
}

export type TaskProblem = { id: string; slug: string; name: string; issues: DefinitionIssue[] }

export type TaskInputField = {
    key: string
    label: string
    /**
     * `file`: the run-start form offers a file picker and sends the file as a `data:` URL (os#679). `link`: a web
     * address, offered as a URL field and sent as text; the API refuses one that is not an absolute http(s) URL (#1754).
     */
    type: 'string' | 'number' | 'boolean' | 'file' | 'link'
    /** Persistent setup: shown as Needs setup until its requirement is filled. */
    configuration?: boolean
    /** At least one field of this group must be filled; independent of `required`. */
    requiredGroup?: string
    /** Comma-separated key/value entries; the entry key must match another input at start. */
    entries?: { keyInput: string; separator: string }
    required?: boolean
    placeholder?: string
    default?: string | number | boolean
}

/** In `taskId`, the missing tool vendor `from` is replaced by the connected vendor `to` (C9). */
export type CatalogToolSwap = { taskId: string; from: string; to: string }
/**
 * One tool type a template's tasks need, as the clone review reports it (#1843): its display name, whether the
 * company has it connected for the team (`connected`) or not (`not_connected`), the same for every reader, and the
 * tasks that need it. The server works the status out; the clone screen only shows it.
 */
export type CatalogReviewTool = {
    toolType: string
    name: string
    /** `connected` or `not_connected`; the clone screen reads anything else as not connected. */
    status: string
    taskIds: string[]
}
/** A connection the company holds that could fill a tool slot, as the clone review offers it. */
export type CatalogSlotBinding = {
    bindingId: string
    toolType: string
    name: string
    slots: string[]
    /** The action names its map says which of its tools they are (#957). */
    actions?: string[]
    /** Of the actions the slot's tasks declare, the ones its map leaves out (#957). Absent from an older API. */
    missingActions?: string[]
}
/**
 * One tool slot a template's tasks declare, as the clone review reports it: the tasks that declare it, the connection
 * that already fills it for the company (null when none does), and every connection that could.
 */
export type CatalogReviewSlot = {
    key: string
    label: string
    description: string | null
    taskIds: string[]
    /** The actions the tasks name under it, each once with the first description given (#957). */
    actions?: ToolSlotAction[]
    filledBy: CatalogSlotBinding | null
    candidates: CatalogSlotBinding[]
}
/** One chosen fill: the connection `bindingId` fills the slot `slot` from now on. */
export type CatalogSlotFill = { slot: string; bindingId: string }
/** The review read (C9): per task, missing and candidate tools, and (#1843) the tools grouped by type. */
export type CatalogCloneReview = {
    tasks: Array<{
        taskId: string
        missing: string[]
        candidates: string[]
        /** A team review's layers above `task` the task's current version may teach (#1841). */
        memoryLayers?: Array<'team' | 'enterprise'>
        /** A team review's word on whether the company already has a task of this one's slug (#1842). */
        exists?: boolean
        /** The company's own task the clone reuses for this one, or null when it has none and the clone copies it. */
        heldTaskId?: string | null
    }>
    /** Absent from an API older than #1843. */
    tools?: CatalogReviewTool[]
    /** The tool slots the template's tasks declare, each with what could fill it. Absent from an older API. */
    slots?: CatalogReviewSlot[]
}
/** The caller's schedule allowance on the plan, read with a team review (#1840). `limit` is null when unlimited. */
export type CatalogScheduleAllowance = { used: number; limit: number | null; plan: string }
/**
 * What the company already holds of one role of a team template (#1842): a role of its slug (the clone reuses it, since
 * a role is one job across the company), how many members hold it, and how many of those sit on the destination team.
 */
export type CatalogHeldRole = {
    roleId: string
    slug: string
    exists: boolean
    holders: number
    holdersOnTeam: number
}
/**
 * A team template's review: the role review's tool rows, the caller's schedule allowance (#1840), and what the company
 * already holds of the template's roles (#1842). An API older than #1840 sends no allowance, which the clone screen
 * reads as unknown; one older than #1842 sends no roles, and the screen then says nothing of what exists.
 */
export type CatalogTeamCloneReview = CatalogCloneReview & {
    schedules?: CatalogScheduleAllowance
    roles?: CatalogHeldRole[]
    /** Whether the caller's plan includes the team's size, as the list reports it (os#1624). */
    planSize?: CatalogTeamPlanSize | null
}
export type CatalogRole = Role & { tasks: CatalogTask[] }
/** How one of the team's tasks stands with the catalog (#1844): there already, not yet, or never (built-in, retired). */
export type TeamShareTaskState = 'public' | 'private' | 'built_in' | 'deprecated'

/**
 * What the team page reads before, and after, sharing the team to the catalog (#1844): the entry it has, its roles
 * and whether the catalog holds each, its tasks and how each stands, and how many KPIs and schedules would travel.
 */
export type TeamShareReview = {
    team: { id: string; slug: string; name: string; goal: string }
    /** The catalog entry this team publishes, or null until it is shared. */
    shared: { id: string; slug: string; name: string; createdAt: string } | null
    /**
     * `heldElsewhere`: a public role of this slug belongs to another publisher, which blocks the share. `included`:
     * the role goes with the team (on its entry once shared, else not left out last time). `tasks`, `strays` and
     * `changes` are the role's own listing, as its page reviews it (`RoleShareReview`). Optional on an older API.
     */
    roles: Array<{
        id: string
        slug: string
        name: string
        public: boolean
        heldElsewhere: boolean
        included?: boolean
        tasks?: RoleShareTask[]
        strays?: RoleShareStray[]
        changes?: number
    }>
    /** Roles the entry still names that the team no longer has: updating the entry drops them. */
    removedRoles?: Array<{ slug: string; name: string }>
    /** How far the entry is behind the team, once shared; 0 until then. */
    changes?: number
    tasks: Array<{
        id: string
        slug: string
        name: string
        roleSlug: string | null
        currentVersionId: string | null
        state: TeamShareTaskState
    }>
    kpis: number
    schedules: number
    /** The kinds of work catalog teams already declare, offered as chips. */
    kindsOfWork: string[]
    /** Whether the plan includes catalog publishing. */
    canPublish: boolean
}

/**
 * How one of a role's tasks stands with the role's catalog listing: `listed` (public, on the role, current), `outdated`
 * (public and on the role, with a newer version since), `elsewhere` (public, but not on this role), `unlisted` (private,
 * not decided on), `left_out` (private, left out by its owner), or never published from here.
 */
export type RoleShareTaskState =
    | 'listed'
    | 'outdated'
    | 'elsewhere'
    | 'unlisted'
    | 'left_out'
    | 'no_version'
    | 'built_in'
    | 'deprecated'

export type RoleShareTask = {
    id: string
    slug: string
    name: string
    currentVersionId: string | null
    version: number | null
    /** The task's version the catalog holds, while it is public. */
    catalogVersion: number | null
    state: RoleShareTaskState
}

/** An entry still on the role whose task now runs as another role (`moved`), was retired, or is gone. */
export type RoleShareStray = {
    entryId: string
    slug: string
    name: string
    reason: 'moved' | 'retired' | 'gone'
    sourceTaskId: string | null
    sourceVersionId: string | null
}

/** What a role's page reads before publishing the role or updating its listing, and again after. */
export type RoleShareReview = {
    role: { id: string; slug: string; name: string }
    catalog: { public: boolean; heldElsewhere: boolean }
    tasks: RoleShareTask[]
    strays: RoleShareStray[]
    /** What the listing is behind on, for a role this company has in the catalog; 0 otherwise. */
    changes: number
    canPublish: boolean
}

/** The words and filing a share sends (#1844); everything else is read off the team. */
export type TeamShareInput = {
    /** The roles that go; the rest are left out. Absent keeps what was left out last time. */
    roleSlugs?: string[]
    name?: string
    description?: string
    kindOfWork?: string | null
    businessFunction?: string | null
}

export type CatalogTeam = {
    id: string
    slug: string
    name: string
    description: string
    charter: TeamCharter
    teammates: Array<{ roleIds: string[] }>
    roles: CatalogRole[]
    tasks: CatalogTask[]
    /** Whether the publisher's own team behind this template is on (derived on read, never stored). Omitted reads as on. */
    enabled?: boolean
    /** At most one suggested cadence per task (#1840), each only the task and its cron. */
    schedules?: Array<{ taskId: string; cron: string }>
    /** The team KPIs the template carries (#1839). Omitted when it carries none. */
    kpis?: Kpi[]
    /**
     * Whether the reader's plan includes this Zero Human team's size (os#1624); null for a community team or the
     * reader's own, and omitted by an API older than it, both read as no size limit.
     */
    planSize?: CatalogTeamPlanSize | null
}
/** A Zero Human team's teammates plus the COO (`membersNeeded`) against the AI members the plan includes (os#1624). */
export type CatalogTeamPlanSize = {
    fits: boolean
    planName: string
    includedMembers: number
    membersNeeded: number
}
/** The sentence a team size the plan does not include is explained with, as the API's refusal words it. */
export function teamSizeSentence(size: Pick<CatalogTeamPlanSize, 'planName' | 'includedMembers' | 'membersNeeded'>) {
    return `Your ${size.planName} plan includes ${size.includedMembers} AI members, counting your COO. This team needs ${size.membersNeeded}.`
}
/**
 * One KPI a team clone created (#1839): the KPI as set, the layer and owner it sits on, its source task's name, and,
 * when its source task was not cloned, that task's name (`sourceNotCloned`), so the clone screen can say why it has no
 * source. None is signed off until a person signs it off.
 */
export type CatalogClonedKpi = Kpi & {
    layer: 'team' | 'role' | 'task'
    ownerId: string
    sourceName: string | null
    sourceNotCloned: string | null
}
/** A target and AMBER band edit to one team or role KPI, applied before the team's KPIs are signed off (#1839). */
export type TeamKpiSignOffEdit = {
    id: string
    targetValue?: number | null
    targetDirection?: Kpi['targetDirection']
    amberBand?: number | null
    amberBandType?: Kpi['amberBandType']
}
/** One layer's memory grant on a team: who gave it and when, and who withdrew it and when. */
export type TeamMemoryLayerGrant = {
    granted: boolean
    grantedBy: string | null
    grantedAt: string | null
    withdrawnBy: string | null
    withdrawnAt: string | null
}
export type TeamMemoryGrants = { team: TeamMemoryLayerGrant; enterprise: TeamMemoryLayerGrant }
export type CatalogTeamCloneInput = {
    name: string
    slug: string
    teammateNames?: string[]
    /** True when a saved teammate or COO name is exactly as "Pick names for me" filled it (setup.names_auto_assigned). */
    namesAutoAssigned?: boolean
    taskIds?: string[]
    moneyCentsDay?: number
    toolSwaps?: CatalogToolSwap[]
    /** The tool slots the cloner chose a connection for; each adds the slot to that connection's slots. */
    slotFills?: CatalogSlotFill[]
    /** The suggested schedules the cloner confirmed (#1840); absent sets none. */
    schedules?: Array<{ taskId: string; enabled: boolean; cron?: string }>
    /** The memory grant the cloner ticked: whether this team's tasks may teach the team and enterprise layers. Absent grants none. */
    memoryGrant?: { team: boolean; enterprise: boolean }
}
export type CatalogRoleCloneInput = {
    teamId: string
    createTeammate?: boolean
    teammateName?: string
    /** A member the company already has to hold the role instead of a new teammate: joins the team, keeps their cap. */
    memberId?: string
    namesAutoAssigned?: boolean
    taskIds?: string[]
    moneyCentsDay?: number
    toolSwaps?: CatalogToolSwap[]
    /** The tool slots the cloner chose a connection for; each adds the slot to that connection's slots. */
    slotFills?: CatalogSlotFill[]
}

export type CatalogTask = {
    id: string
    slug: string
    name?: string
    ownerEnterpriseId?: string | null
    canEditDefinition?: boolean
    /**
     * Whether the caller's enterprise owns this definition. True for an OS task its owner cannot edit in place
     * (`canEditDefinition` stays false there). The catalog hides Sync and the clone's version line for that caller.
     */
    ownedByMe?: boolean
    origin?: 'tenant' | 'os'
    /** The role a clone or copy of this template runs as, unless the copy picks another. */
    assigneeRole?: string | null
    visibility: string
    provenance: string
    status?: string
    currentVersionId?: string | null
    /**
     * The catalog names a definition's declared scope before anyone clones it (#410): `tools` are
     * the tool types it may call, `gates` the approval classes that govern them. The API already
     * sends both on every *list* row, not just on the detail response: GET /catalog/tasks →
     * CatalogService.queryPublic → withVersions(), the same serializer GET /catalog/tasks/:id uses,
     * and it spreads the whole current TaskVersion row (tools_json / gates_json are columns on it).
     * Pinned by the "names each public row's declared tools and gate classes in the list payload"
     * test in apps/api/src/catalog/catalog.service.spec.ts, so this stays a read-side shape rather
     * than a new payload — and cannot silently regress behind the portal's mocked component tests.
     */
    currentVersion: {
        goal: string
        /** The entry's published version number: the one a clone that is behind could Sync to. */
        version?: number
        /**
         * The owner's task version this entry version was published from (#1183): the number a clone shows. Null or
         * absent on an entry version from before publication was recorded, which stands for its own `version`.
         */
        sourceVersion?: number | null
        /** Each a tool's name or an object naming it (os#1687): read through `toolEntryName`. */
        tools?: ToolEntry[]
        inputs?: TaskInputField[] | null
        gates?: TaskGateSummary[]
        spend?: { moneyUsdPerRun?: number }
        /** Where a mailbox offers the task as a one-click button; null or absent offers none. */
        mailboxAction?: MailboxAction | null
    } | null
    /**
     * Social proof on a public entry (#413), sent on list rows and the single entry alike: how many enterprises
     * hold a linked clone of it and how many starred it, plus whether the caller's enterprise did. Counts only;
     * the API never says which enterprises. Optional so an older API reads as 0 / not starred.
     */
    cloneCount?: number
    starCount?: number
    starredByMe?: boolean
    /** Whether the caller's own business already holds a linked clone of this entry. A boolean only, never which business. */
    clonedByMe?: boolean
    /** ...and the source has published a newer version than the one that clone was snapshotted from. */
    cloneStale?: boolean
    /**
     * The owner's version the caller's clone is on (#1260); the entry's current version is then the one available,
     * read through `entryOwnerVersion`.
     * Null/absent when the caller holds no clone, or one with no record of the version it was taken from.
     */
    cloneOnVersion?: number | null
    /**
     * The display name of the enterprise that owns the entry's definition, which a clone's version is named for
     * (#1260): "Zero Human's eng-implement-issue, version 7". Null/absent when no owner name resolves.
     */
    ownerName?: string | null
}

/**
 * A declared gate as the catalog and a task's read-only definition both report it: which tool it guards, who signs off,
 * how specific that sign-off must be, and the `outcome` it decides on when the gate is tagged with one (os#675). The
 * API sends a gate's `outcome` with the rest of it; optional because most gates carry none.
 */
export type TaskGateSummary = { tool: string; owner: string; approval: string; outcome?: string | null }

/**
 * Whether a task may start new runs (#1314): the off switch. Its own field, independent of the definition lifecycle
 * (`status`: active | deprecated) and of how the last run ended (`lastRunStatus`). A task never switched reads `active`.
 */
export type TaskRunStatus = 'active' | 'disabled'

/** A team that inherits a task, because a member who sits on it or leads it holds the task's role. */
export type TaskTeam = { id: string; slug: string; name: string }

export type TaskRow = {
    configuration?: { missingInputs: TaskInputField[] }
    builtInRole?: string | null
    id: string
    slug: string
    currentVersionId?: string | null
    name?: string
    /** The role whose holders run it, on whatever team they sit. A tenant task with no role cannot run. */
    assigneeRole?: string | null
    assigneeRoleId?: string | null
    assigneeRoleName?: string | null
    /** The input keys that split the task's work into lanes; runs in different lanes run at once. Null: one lane. */
    serializeOn?: string[] | null
    /**
     * The teams that inherit the task: each team where a member, seated or as lead, holds its role. Derived, never
     * set: a task belongs to a role, not a team. Absent from an API older than the field.
     */
    teams?: TaskTeam[]
    status?: string
    kind: string
    origin?: 'tenant' | 'os'
    clonedFromVersionId?: string | null
    ownerEnterpriseId?: string | null
    canEditDefinition?: boolean
    pendingRecommendationCount?: number
    /** The newer source version a linked clone could pull, or null/absent when it is current, unlinked or an OS copy. */
    sourceUpdate?: TaskSourceUpdate | null
    /**
     * The owner's version a linked clone is on (#1260): its headline version, where `currentVersion.version` is only
     * the clone's own counter. Null/absent for a task that is not a linked clone, or one with no record of it.
     */
    ownerVersion?: TaskOwnerVersion | null
    /** Outcome of this task's most recent run; null when it has never run. */
    lastRunStatus?: string | null
    /** Why that last run stopped its pipeline though it succeeded (`RunRow.stoppedReason`), else null or absent. */
    lastRunStoppedReason?: string | null
    /** Who is currently running or last ran this task — the member a role-dispatched run went to, not a pin. */
    lastRunAssigneeName?: string | null
    /** Whether the task may start new runs: `active` or `disabled`. Not `lastRunStatus`, and not the lifecycle `status`. */
    runStatus?: TaskRunStatus
    schedule?: TaskSchedule | null
    scheduleLastRunAt?: string | null
    scheduleNextRunAt?: string | null
    /** The firing instant of the last scheduled start that was refused (#1136); null once a start succeeds. */
    scheduleRefusedAt?: string | null
    /**
     * Why it was refused: `task_not_runnable`, `task_disabled`, `overlap`, `member_busy`, `no_enterprise`, or another
     * start code.
     */
    scheduleRefusalReason?: string | null
    /** Firings refused in a row as not runnable, not busy, paused or a codeless `start_failed`. A success resets it. */
    scheduleNotRunnableStreak?: number
    /** Successful runs in a row that stopped their pipeline on an outcome the task's successors have no edge for. */
    deadEndStreak?: number
    deadEndOutcome?: string | null
    /** Firings skipped because the task or its member was busy: expected for an overlap-skipping task. */
    scheduleBusySkips?: number
    /**
     * This enterprise's own values for the task's inputs (F2), keyed by input key, or null/absent for none. Every run
     * takes them under what its start gives and over the version's defaults.
     */
    inputValues?: TaskInputValues | null
    /** The "When a release goes live" trigger, or null/absent when the task has none. */
    releaseTrigger?: {
        enabled: boolean
        input: Record<string, unknown> | null
        lastFiring: { version: string; at: string } | null
    } | null
    currentVersion: {
        version?: number
        goal: string
        metric?: { text: string }
        guardrails?: string[]
        /** Each a tool's name or an object naming it (os#1687): read through `toolEntryName`. */
        tools?: ToolEntry[]
        inputs?: TaskInputField[] | null
        gates?: TaskGateSummary[]
        spend?: TaskSpend
        workspace?: { writeAllow?: string[]; writeDeny?: string[]; appendOnly?: string[] } | null
        successors?: TaskSuccessor[] | null
        /** The outcomes a run ends on that finish the work, so it is not listed as waiting. */
        finishesOn?: string[] | null
        /** The tool slots its tools may be named by (`codehost`); null or absent declares none. */
        toolSlots?: ToolSlot[] | null
        /** Where a mailbox offers the task as a one-click button; null or absent offers none. */
        mailboxAction?: MailboxAction | null
    } | null
}

/**
 * What a task version may spend and on what. `model` (#664 item 3) pins it to one exact model instead of
 * whatever `maxModelTier` resolves to for this tenant. The tier applies only while `model` is unset.
 */
export type TaskSpend = { moneyUsdPerRun?: number; maxModelTier?: string; model?: string | null }

export type ChatMember = {
    id: string
    name: string
    /** What to show them as (#1815: the unnamed COO reads "COO"); optional, since an older API sends none. */
    displayName?: string
    avatarUrl: string | null
    persona: string | null
}

/** Who wrote a person's message: someone signed in to the portal, or an API token. A token has no picture. */
export type ChatSender = { kind: 'human' | 'token'; name: string; avatarUrl: string | null }

/** The run a tool row started, as the chat read serves it now (#1761): its task, and its status at this moment. */
export type ChatRunState = { taskName: string | null; taskSlug: string | null; status: string }

export type ChatMessageRow = {
    /** Served on a tool row that started a run; absent on any other row, and from an older API. */
    run?: ChatRunState | null
    id: string
    sessionId: string
    role: 'user' | 'assistant' | 'tool' | 'system'
    content: string | null
    toolName: string | null
    toolCallId: string | null
    payload: Record<string, unknown> | null
    createdAt: string
    /**
     * Who wrote a `user` message, since several people can share a thread. Null for the member's own messages
     * and for any written before it was recorded; optional, since an older API sends none.
     */
    sender?: ChatSender | null
    /** A message you sent while the member was replying, which it has yet to read. Optional: an older API sends none. */
    waiting?: boolean
    /** A waiting message the reader may withdraw before the member reads it. Optional: an older API sends none. */
    withdrawable?: boolean
    /** The files the message carried (#1732); absent on a message that carried none. */
    files?: ChatAttachedFile[]
}

/**
 * A file attached to a chat message (#1732): `type` is its kind (`pdf`, `png`, `word`…) from the shared contract.
 * `costNote` (#1736) is on an unsent file only, as the attach answer and the thread's `pendingFiles` carry it: the
 * plain-words cost of reading a large file, billed to your own AI provider, or null when the file is too small to
 * matter. Absent from an older API, and from every file a message carried.
 */
export type ChatAttachedFile = {
    id: string
    filename: string
    sizeBytes: number
    type: string | null
    costNote?: string | null
}

/** A file's bytes as base64, for the attach route's JSON body. In slices, so a large file does not overflow the stack. */
export async function fileBase64(file: Blob): Promise<string> {
    const bytes = new Uint8Array(await file.arrayBuffer())
    let binary = ''
    for (let at = 0; at < bytes.length; at += 0x8000) {
        binary += String.fromCharCode(...bytes.subarray(at, at + 0x8000))
    }
    return btoa(binary)
}

export type ChatSessionRow = {
    id: string
    memberId: string
    status: 'idle' | 'replying' | 'closed'
    error: string | null
    createdAt: string
    updatedAt: string
    /** When you last opened the thread. The API works out `unread` from it, so the list need not compare. */
    lastReadAt: string | null
    unread: boolean
    /** How many of the member's replies landed after `lastReadAt`: every one, in a thread never opened. */
    unreadCount: number
    member: ChatMember | null
    lastMessage: { role: string; content: string | null; createdAt: string } | null
    /** Who owns the thread (`human:<id>` or `token:<id>`); null for a legacy thread nobody owns. Optional, since an older API sends none. */
    ownerPrincipal?: string | null
    /**
     * The one job a conversation was opened for (#1203), such as setting up the company (#1738); null for an
     * everyday thread. Optional, since an older API sends none.
     */
    purpose?: string | null
}

export type ChatDetail = ChatSessionRow & {
    member: ChatMember
    messages: ChatMessageRow[]
    /**
     * The files you attached in this chat and have not sent yet (#1732): your draft, which the composer restores when
     * it opens the chat. On the read of the chat only; optional, since an older API sends none.
     */
    pendingFiles?: ChatAttachedFile[]
    /** Whether this chat's teammate is shown images, by the check its turns use (#2085); optional, since an older API sends none. */
    teammateSeesImages?: boolean
    /**
     * Every gate the thread carries, as it stands now (#1981): raised by a run it started, or named by a message in it.
     * A decided one keeps its card in the thread, showing how it ended. Optional, since an older API sends none.
     */
    gates?: ChatGateState[]
    /** Who stopped the thread, as it names senders (#281); null or absent when nobody has, or the next message lifted it. */
    stoppedBy?: string | null
    /**
     * The member's own step list for the thread's current request (#286, `os.set_steps`), in order, each step's title
     * and state. None, or null, for a thread with no list. Optional, since an older API sends none.
     */
    steps?: ChatStep[] | null
}

/**
 * A gate a chat thread carries, waiting or decided, wherever it was decided (#1981). `choiceLabel` is the choice it
 * was decided through in its button's words, `answers` the replies to a question, and `createdAt` when it was
 * raised: its card stays at that point in the thread. `status` is the gate's own.
 */
export type ChatGateState = {
    id: string
    toolName: string
    status: GateRow['status']
    choiceLabel: string | null
    decisionNote: string | null
    answers: CeoAnswerLine[] | null
    decidedAt: string | null
    createdAt: string
    /** The task the gate's run belongs to, as the waiting card heads it. Null or absent when it is gone. */
    taskName?: string | null
    taskSlug?: string | null
}

/** One reply on an answered question, joined to the question it answers, as a decided gate stores it. */
export type CeoAnswerLine = CeoQuestionAnswer & { question?: string }

/** Your answer to a connected tool call waiting on you in chat. `always` stops it asking you again for that tool. */
export type ChatToolCallDecision = 'allow' | 'always' | 'deny'

/** One "Always allow": this member may call this tool in your chats without asking you. */
export type ChatToolGrantRow = {
    id: string
    memberId: string
    tool: string
    createdAt: string
}

export type TaskLink = {
    id: string | null
    slug: string
    name: string
}

/** One way a successor starts: the outcome it waits for, and anything that narrows or delays it. */
export type SuccessorRoute = {
    on: string
    when?: string
    delayMinutes?: number
    maxRunsPerIssue?: number
}

/** A task that runs after this one, listed once with every outcome that leads to it. */
export type SuccessorLink = TaskLink & { routes: SuccessorRoute[] }

/** Where a third party posts to start this task. The secret lives in Settings → Webhooks, never here. */
export type TaskWebhook = {
    url: string
    header: string
}

/** The enterprise's webhook secret as Settings shows it: which one is in use, never the secret. */
/** The base resources a token can be scoped to. Child endpoints inherit their resource's level. */
export const API_TOKEN_RESOURCES = [
    'billing',
    'blockers',
    'catalog',
    'chats',
    'enterprise',
    'executions',
    'gates',
    'kpis',
    'llm',
    'mail',
    'members',
    'memory',
    'orgs',
    'plans',
    'proposals',
    'recommendations',
    'roles',
    'runs',
    'spend',
    'tasks',
    'teams',
    'tools',
    'users',
    'webhooks',
] as const

/**
 * Scopes only a signed-in person may hold (os#1615), mirroring the API's rule: an API token or a connected app is
 * refused them, so a picker granting one of those never offers them.
 */
export const PERSON_ONLY_SCOPES: readonly string[] = ['billing:write']

/**
 * Resources that are a person's own rather than the enterprise's (#883): `mail` is the signed-in person's mailbox.
 * Only a connected app is ever granted one, by the person's consent; a person reaches their own whatever their grant,
 * so the People picker never offers it, and an API token is nobody, so the tokens picker never does either.
 */
export const CONNECTION_ONLY_RESOURCES: readonly string[] = ['mail']

/** An API token as the portal may see it: never anything that could authenticate. */
export type ApiTokenRow = {
    id: string
    name: string
    hint: string
    scopes: string[]
    createdAt: string
    lastUsedAt: string | null
    revokedAt: string | null
}

/** What creating or rotating returns — the only time the token itself is ever sent to the portal. */
export type IssuedApiToken = ApiTokenRow & { token: string }

/**
 * A connected app (#333): one external agent's MCP connection to this enterprise, as
 * `ConnectionsService.list` returns it. Everything chosen at consent and nothing that could authenticate —
 * the access token itself never leaves the API.
 */
export type ConnectionRow = {
    id: string
    clientId: string
    /** What the client called itself at consent. Null when it gave no name: the client id is shown instead. */
    clientName: string | null
    /** The person who approved it, or null once their account is gone. */
    approvedBy: { id: string; name: string | null } | null
    /** The AI team member it acts as, or null once that member is gone. */
    member: { id: string; name: string } | null
    scopes: string[]
    createdAt: string
    lastUsedAt: string | null
    revokedAt: string | null
}

/**
 * What happens to mail on an attached domain (#422), chosen by the owner when attaching it and changeable either
 * way afterwards: `migrate` moves members' email to `{name}@domain` (once the extra DNS is published), `keep`
 * leaves it on `{slug}.zerohuman.com`.
 */
export type DomainMailMode = 'migrate' | 'receive' | 'keep'

/** One DNS record the owner publishes in their own zone; ZeroHuman only ever reads it. */
export type DomainDnsCheck = DomainDnsRecord & { published: boolean; found: string[] }
export type DomainVerification = EnterpriseDomainRow & { checks: DomainDnsCheck[] }

export type DomainDnsRecord = {
    type: 'TXT' | 'MX' | 'CNAME'
    name: string
    value: string
    /** `ownership` proves the domain is theirs; `mail` records are only asked for in `migrate` mode. */
    purpose: 'ownership' | 'mail'
    priority?: number
}

/** An attached domain as `GET /v1/enterprise/domains` returns it (`EnterpriseDomainsService`'s view). */
export type EnterpriseDomainRow = {
    domain: string
    mailMode: DomainMailMode
    /** unverified → nothing granted; incomplete → owned, mail records missing; verified → owned, mail kept; active → mail moved. */
    status: 'unverified' | 'incomplete' | 'verified' | 'active'
    ownershipVerified: boolean
    mailReady: boolean
    receivingReady: boolean
    sendingReady: boolean
    /**
     * In `migrate` mode, what the sending provider still waited on at its last check, as codes
     * (`domainSendingWaitText` reads them): empty once mail can leave, null where sending is not checked yet.
     */
    sendingWaitingOn?: string[] | null
    /** When the sending provider was last asked. */
    sendingCheckedAt?: string | null
    /** What is still to publish for the mode chosen. Empty once nothing is. */
    requiredRecords: DomainDnsRecord[]
}

export type WebhookSecretStatus = {
    hint: string
    /** Null until someone rotates it: the secret made with the enterprise has never been seen by anyone. */
    rotatedAt: string | null
}

/** What rotating returns — the only time the secret itself is ever sent to the portal. */
export type RotatedWebhookSecret = {
    secret: string
    hint: string
    rotatedAt: string
}

export type TaskDetail = TaskRow & {
    skillMd: string
    versions: Array<{
        id: string
        version: number
        createdAt: string
        runCount?: number
        /** Who published the version when a writer that is not a person did: `auto-update` for the automatic pass. */
        publishedBy?: string | null
        /** The owner's version a linked clone's version was made from (#1260), or null. */
        sourceVersion?: number | null
    }>
    predecessors?: TaskLink[]
    successorTasks?: SuccessorLink[]
    definitionIssues?: DefinitionIssue[]
    webhook?: TaskWebhook
    /** False when no process on this deployment has ticked the schedule loop recently, so no cadence fires. */
    schedulesEnabled?: boolean
    /**
     * The zone this enterprise runs in (#884), so a schedule that follows the enterprise is described and
     * previewed in the zone it actually fires in. Optional: an API older than #884 sends none, and the shared
     * helpers then fall back to UTC rather than showing nothing.
     */
    enterpriseTimezone?: string
    /** Keys of saved input values the current version does not take (no longer declared, or another type). */
    ignoredInputValues?: string[]
    /** The owner's version an unlinked copy was copied from. Null/absent for anything else. */
    copiedFromVersion?: number | null
    /**
     * The task's public catalog entry, for a task this company owns: the entry's own version, the task version it
     * holds (`sourceVersion`), and the public role it is listed on. Null while the task is private.
     */
    catalogPublication?: TaskCatalogPublication | null
}

export type TaskCatalogPublication = {
    id: string
    version: number
    sourceVersion: number | null
    assigneeRole?: string | null
}

/**
 * What a version save did to the task's catalog entry. Once a task is public, every version its owner saves is the
 * entry's next one too; `updated` is false when this save did not write the entry, and `refusal` then says why the
 * entry kept the version it has (`sourceVersion`), or is null when the save made no new version.
 */
export type SavedCatalogPublication = {
    id: string
    visibility: string
    version: number
    sourceVersion: number | null
    updated: boolean
    refusal: { error: string; message: string } | null
}

/** Whose task a linked clone is, the owner's version it is on, and the owner's newer one when there is one. */
export type TaskOwnerVersion = RunOwnerVersion & { availableVersion: number | null }

/**
 * What pulling its source's newer version would change for a linked clone: the declared tools and gates it adds, and
 * the ones it drops (the update takes the newer version's whole).
 */
export type TaskSourceUpdate = {
    sourceVersionId: string
    sourceVersion: number
    addedTools: string[]
    addedGates: TaskGateSummary[]
    /** Optional: an API from before removals were reported sends none, and the panel then shows no removals. */
    removedTools?: string[]
    removedGates?: TaskGateSummary[]
    /**
     * The inputs the newer version requires that neither it nor this enterprise's own values fill, where the current
     * version did not leave them so (F2). Optional: an API from before input values sends none.
     */
    missingInputs?: Pick<TaskInputField, 'key' | 'label' | 'type'>[]
    /**
     * True when the newer version changes the task's workspace: the credentials its repository commands get, or what it
     * may write and push. Left out when it does not, and by an API from before it was reported.
     */
    workspaceChanged?: boolean
    /** Why this update is waiting rather than applied automatically; null when it is not held. */
    held?: SourceUpdateHeld | null
}

/** The causes that keep a source update from applying by itself, in the order the server lists them. */
export type SourceUpdateHeldReason = 'crafted_not_in_plan' | 'auto_update_off' | 'needs_review'

/** A held source update: `message` and `upgradePath` come with `crafted_not_in_plan` only. */
export type SourceUpdateHeld = {
    reasons: SourceUpdateHeldReason[]
    message?: string
    upgradePath?: string
}

/** An enterprise's own values for a task's inputs (F2), keyed by input key. */
export type TaskInputValues = Record<string, string | number | boolean>

/** A copy names no team: a task belongs to a role, and every team where a member holds that role runs it. */
export type TaskCopyInput = {
    name: string
    slug: string
    /** The role the copy runs as, for its holders on whatever team they sit; left out, it keeps the source's role. */
    assigneeRole?: string
}

/** A task version as it was, for version history. */
export type TaskVersionDetail = {
    id: string
    version: number
    createdAt: string
    runCount: number
    current: boolean
    goal: string
    metric?: { text: string } | null
    guardrails?: string[]
    /** Each a tool's name or an object naming it (os#1687): read through `toolEntryName`. */
    tools?: ToolEntry[]
    successors?: TaskSuccessor[] | null
    finishesOn?: string[] | null
    /** The tool slots its tools may be named by; null or absent declares none. */
    toolSlots?: ToolSlot[] | null
    /** Where a mailbox offers the task as a one-click button; null or absent offers none. */
    mailboxAction?: MailboxAction | null
    spend?: TaskSpend | null
    skillMd: string
}

export type MemberEmailRow = {
    deliveryStatus?:
        | 'submitting'
        | 'accepted'
        | 'delivered'
        | 'delayed'
        | 'bounced'
        | 'complained'
        | 'failed'
        | 'unknown'
        | null
    deliveryUpdatedAt?: string | null
    spamScore: number | null
    spamFlag: boolean | null
    id: string
    fromAddress: string
    fromName: string | null
    subject: string
    snippet: string
    receivedAt: string
    readAt: string | null
    hasAttachments: boolean
    /** The original message's `To` and `Cc` lists (os#909); empty for mail stored before they were kept. */
    to: string[]
    cc: string[]
    /** Who a mail the mailbox sent went to unseen (#1119); empty on everything that arrived. */
    bcc?: string[]
    /** The conversation this mail belongs to; null only for mail not yet threaded. */
    threadId: string | null
    /** Whether the mail arrived (`inbound`) or the mailbox sent it (`outbound`). */
    direction?: MemberEmailDirection
    /** Free-text labels triage gave the mail. */
    labels: string[]
    handledState: MemberEmailHandledState
    /** When a `snoozed` mail comes back; null in every other state. */
    snoozedUntil: string | null
}

export type MemberEmailHandledState = 'unhandled' | 'replied' | 'skipped' | 'snoozed' | 'escalated'

export type MemberEmailDirection = 'inbound' | 'outbound'

/** One message of an opened mail's conversation: who wrote to whom and when, without its body. */
export type MemberEmailThreadMessage = {
    id: string
    direction: MemberEmailDirection
    fromAddress: string
    fromName: string | null
    envelopeTo: string
    to: string[]
    subject: string
    snippet: string
    receivedAt: string
    readAt: string | null
    deliveryStatus: MemberEmailRow['deliveryStatus']
}

export type MemberEmailDetail = MemberEmailRow & {
    /** The `Message-ID` it arrived with, or the provider's own id for a message the mailbox sent. */
    messageId: string
    envelopeTo: string
    textBody: string
    htmlBody: string | null
    attachments: Array<{ id: string; filename: string; contentType: string; sizeBytes: number }>
    /** Every message of this mailbox in the mail's conversation, oldest first, this one among them. */
    thread: MemberEmailThreadMessage[]
}

export type MemberEmailList = ListResponse<MemberEmailRow> & {
    /**
     * Counts across the whole mailbox, independent of the selected tab and page. Inbox and the labels count
     * open mail only; everything closed that arrived (answered or left on purpose) is the archive, and everything
     * the mailbox sent is Sent.
     */
    unlabelledCount: number
    archivedCount: number
    sentCount?: number
    labelCounts: Array<{ label: string; count: number }>
}

/** What a send, reply or forward came back as: `sent`, or `send_failed` with the provider's reason. */
export type MailSendOutcome = { status: string; id: string | null; error?: string }

/** One file a sent mail carries, as the API takes it: its bytes base64-encoded. */
export type MailAttachmentUpload = { filename: string; contentType: string; base64: string }

/**
 * `signature: false` sends without the mailbox's signature (#1115), and `quote: false` without the mail it answers
 * quoted below it (#1116); left out, the OS adds each.
 */
export type MailReplyBody = MailReplyDraft & { attachments?: MailAttachmentUpload[] }
/**
 * Who a reply goes to (#1119): the sender of the mail it answers, or `to` when the writer named others in their place;
 * `replyAll: true` copies in everyone else the mail went to, but the mailbox itself and anyone in `to`, and `cc` and
 * `bcc` add more.
 */
export type MailReplyRecipients = { to?: string[]; replyAll?: boolean; cc?: string[]; bcc?: string[] }
/** What a reply written in the portal says, and who else it goes to; `from` names the address it leaves from. */
export type MailReplyDraft = MailBody & MailReplyRecipients & { from?: string }
/** `to`, `cc` and `bcc` each one address or several (#1119), at most 50 in all. */
export type MailForwardBody = {
    to: string | string[]
    cc?: string[]
    bcc?: string[]
    text?: string
    html?: string
    attachments?: MailAttachmentUpload[]
    signature?: boolean
}
export type MailSendBody = {
    to: string | string[]
    cc?: string[]
    bcc?: string[]
    subject: string
    text: string
    html?: string
    /** The address it leaves from, in place of the mailbox's own. */
    from?: string
    attachments?: MailAttachmentUpload[]
    signature?: boolean
}
/**
 * A mailbox in the enterprise a mail can be moved to: a person's who has an address (`id` is their account), or a
 * member's.
 */
export type MailboxDestination = { kind: 'member' | 'person'; id: string; name: string; address: string; self?: true }
/** Where a mail is moved: a member's mailbox or a person's, one of the two. */
export type MailMoveTarget = { memberId: string } | { userId: string }
/** A move: the mail, how many messages of its conversation went with it, and where. */
export type MailMoveResult = { id: string; moved: number; to: MailboxDestination }
/**
 * What a mail written in the portal says: its plain text, and its HTML when it was written formatted (#1113),
 * `signature: false` when the writer left the mailbox's signature off (#1115), and, on a reply alone, `quote: false`
 * when the writer removed the quoted original (#1116).
 */
export type MailBody = { text: string; html?: string; signature?: boolean; quote?: boolean }

/**
 * A mailbox's signature (#1115), which the OS adds to everything the mailbox sends: its HTML, sanitised by the API, and
 * its plain text. Both empty, and `updatedAt` null, when the mailbox has none. `useTemplate` says whether the
 * enterprise's signature template (#1118) signs the mailbox's mail while it has none of its own, and `template` is that
 * template filled in for this mailbox, null when the enterprise has none.
 */
export type MailSignature = {
    html: string
    text: string
    updatedAt: string | null
    useTemplate: boolean
    template: { html: string; text: string } | null
}

/** What a signature is saved from: the signature itself, the template switch, or both. */
export type MailSignatureSave = { html?: string; text?: string; useTemplate?: boolean }

/**
 * The enterprise's signature template (#1118), kept by an owner: its HTML and text with their placeholders (`{name}`,
 * `{role}`, `{address}`, `{enterprise}`) as written. Both empty, and `updatedAt` null, when there is none.
 */
export type MailSignatureTemplate = { html: string; text: string; updatedAt: string | null }

/**
 * One contact a mailbox keeps (#1114): saved by a send that left it (`auto`) or added by hand (`manual`). With
 * `directory`, a list also offers the enterprise's own people and members who have an address (`directory`), which
 * have no `id` until they are saved.
 */
export type MailContact = {
    id: string | null
    address: string
    name: string
    source: 'auto' | 'manual' | 'directory'
    nameByHand: boolean
    mailCount: number
    lastMailedAt: string | null
}
export type MailContactQuery = { search?: string; limit?: number; directory?: boolean }

/** The two kinds of file a mailbox's contacts are imported from and exported as (#1120). */
export type MailContactsFileFormat = 'csv' | 'vcard'

/**
 * What a contacts import did (#1120): addresses `added`, contacts given a name they lacked (`updated`), those already
 * there and left as they were (`unchanged`), and entries `skipped` for having no valid address, the first bad values
 * in `invalid`.
 */
export type MailContactsImportResult = {
    added: number
    updated: number
    unchanged: number
    skipped: number
    invalid: string[]
}

function contactQuery(query: MailContactQuery): Record<string, string | number | undefined> {
    return {
        search: query.search?.trim() || undefined,
        limit: query.limit,
        directory: query.directory ? 'true' : undefined,
    }
}

/** Where a mailbox offers a task as a one-click button, as the task's version declares it (`mailboxAction`). */
export type MailboxActionSurface = 'mailbox' | 'mail'
export type MailboxAction = { on: MailboxActionSurface; label: string }

/**
 * One button a mailbox offers (`GET /v1/mail/actions`, `GET /v1/members/:id/mailbox-actions`): a task of the enterprise
 * that declares a mailbox action, which the button starts (`taskId`), or a public template that declares one and the
 * enterprise holds no clone of, which the button offers to clone (`catalogTaskId`). Exactly one of the two is set.
 */
export type MailboxActionOffer = {
    taskId: string | null
    catalogTaskId: string | null
    slug: string
    name: string
    label: string
    on: MailboxActionSurface
    /** On a person's own inbox: their own cadence for the button, or null when they press it only by hand. */
    schedule?: MailboxActionSchedule | null
}

/**
 * Who receives and may act as one member's exact mail address. `signature` is the address's own (#1122), which mail
 * that leaves from it carries in place of the mailbox's; null while it signs as its mailbox does.
 */
export type MailAliasRow = {
    id: string
    address: string
    replyFromAlias: boolean
    signature: { html: string; text: string } | null
}
export type MailAliasesView = { aliases: MailAliasRow[]; domains: string[] }
/** What an extra address's signature is saved from (#1122): its HTML and text, as the editor writes them. */
export type MailAliasSignatureSave = { html?: string; text?: string }

export type MailboxOwnership = {
    memberId: string
    address: string
    owner: { id: string; name: string }
    delegated: boolean
    /** Messages for this exact address currently held by its owner. */
    existingMessages: number
}

export type MailboxOwnershipChange = MailboxOwnership & {
    transferredMessages: number
    alreadyPresentMessages: number
}

export type ListQuery = {
    page?: number
    limit?: number
    search?: string
    sort?: string
    order?: 'ASC' | 'DESC'
    [key: string]: string | number | undefined
}

export type ListResponse<T> = {
    data: T[]
    total: number
    page: number
    limit: number
    pages: number
}

/**
 * Read a list endpoint's answer as one page, whether it already pages server-side or still
 * answers with a plain array (an older server, or a stub). Keeps a view's paging code the same
 * either way.
 */
export function asListResponse<T>(answer: ListResponse<T> | T[], query: ListQuery = {}): ListResponse<T> {
    if (!Array.isArray(answer)) {
        return answer
    }
    const parsedLimit = Number(query.limit)
    const limit = Math.max(1, Number.isFinite(parsedLimit) && parsedLimit > 0 ? parsedLimit : answer.length)
    return {
        data: answer,
        total: answer.length,
        page: Math.max(1, Number(query.page) || 1),
        limit,
        pages: Math.max(1, Math.ceil(answer.length / limit)),
    }
}

/**
 * Who last switched a team on or off, and when (#1812): a person (a connected app counts as its approving person) or
 * an API token, named as they were at the time.
 */
export type TeamStatusChange = {
    enabled: boolean
    actorKind: 'person' | 'api_token'
    actorId: string
    actorName: string
    changedAt: string
}

/** A team with the tasks it owns, for the successor graph on the team page. */
export type TeamDetail = Team & {
    tasks: TaskRow[]
    relatedTasks?: TaskRow[]
    members?: Member[]
    /** The latest on/off change, from the team read and the switch's answer. Null (or omitted) when never switched. */
    lastStatusChange?: TeamStatusChange | null
}

/**
 * How a role stands with the catalog (#1844), by the rule the team share review reads: `public` when the catalog holds
 * a public role of its slug, `heldElsewhere` when another publisher holds that one, which blocks publishing it.
 */
export type RoleCatalogStanding = {
    public: boolean
    heldElsewhere: boolean
    /** What the role's listing is behind on, for a role this company has in the catalog. Absent on an older API. */
    changes?: number
}

/** A team a role is held on, with the holders who sit there. */
export type RoleTeam = { id: string; slug: string; name: string; holders: Array<{ id: string; name: string }> }

export type RoleDetail = Role & {
    members: Member[]
    /** Where the role is held: each team one of its holders sits on. Absent on an older API. */
    teams?: RoleTeam[]
    tasks: TaskRow[]
    relatedTasks?: TaskRow[]
    /** The role's standing with the catalog, from `GET /v1/roles/:id`. */
    catalog?: RoleCatalogStanding
}

export type MemoryEntry = {
    id: string
    body: string
    layer: string
    tombstonedAt: string | null
    createdAt: string
}

export type RunRow = {
    id: string
    taskId: string
    status: string
    createdAt: string
    startedAt?: string | null
    notBefore?: string | null
    finishedAt: string | null
    input?: Record<string, unknown> | null
    logArtifactKey?: string | null
    /** The model the run used, as the provider that answered spells it. Null until a model run starts. */
    model?: string | null
    /** What the run has cost so far, in cents (fractional), priced by the API's shared spend calculation. */
    costUsdCents?: number
    blockedReason?: string | null
    /**
     * Blockers: the connection a `tool_action_unmapped:<slot.action,…>` block is about, the binding that fills the
     * slot on the run's cascade, most specific layer first. Null or absent when no connection can be resolved.
     */
    blockedBinding?: { id: string; name: string } | null
    blockClass?: 'task' | 'model' | 'infra' | 'credentials' | 'action_map' | 'definition' | 'escalation' | 'capacity'
    /**
     * Why a run that did not fail still ended its chain: `outcome_not_declared:<outcome>`, a `succeeded` run whose
     * task has no next step for the outcome it ended on. Sent on Blockers' stopped rows, run detail and `GET /runs`;
     * null (or absent, from an older API) for every other run. The run's `status` stays `succeeded`.
     */
    stoppedReason?: string | null
    /** Blockers: the reason a failed run's last `run.failed` event recorded; null when it recorded none. */
    failedReason?: string | null
    /** The task the run belongs to, joined by `GET /runs` so a row reads as work. Null if the task was deleted. */
    taskName?: string | null
    taskSlug?: string | null
    taskOrigin?: 'tenant' | 'os' | null
    /** The member the run runs as; null when the task has no assignee (OS tasks), or the member is gone. */
    assigneeName?: string | null
    assigneeAvatarUrl?: string | null
    /** Blockers: the run's team, named in place of a teammate when the run worked as no member (#1772). */
    teamName?: string | null
    /**
     * The person who asked for the run's work (#1373), or null when nobody did: a schedule, a webhook, an API token.
     * Absent from an API older than the field, which reads as nobody too.
     */
    requestedBy?: { id: string; name: string } | null
}

export type GateDecision = 'approve' | 'reject' | 'request_changes'

/**
 * Who a gate's run is asking on behalf of: the member it was assigned to and the role its task runs as, by the
 * names the enterprise gave them, and the team that role sits in (else the task's). Any may be null when not known.
 */
export type GateAsker = { memberId: string | null; name: string | null; role: string | null; team?: string | null }

export type GateRow = {
    id: string
    runId: string
    /** Who is asking. Null or absent when the run has no assignee and its task names no role. */
    askedBy?: GateAsker | null
    toolName: string
    status: 'waiting' | 'approved' | 'rejected' | 'changes_requested' | 'cancelled'
    owner: string
    payload: unknown
    /** What the run said this gate decides on (`os.set_gate_summary`), copied when the gate opened. */
    summary?: string | null
    /** The card's own words the run wrote with its summary: what approving does, and why it waits for a person. */
    title?: string | null
    why?: string | null
    /** An https address to look at before deciding (a pull request, a preview deploy, a draft), shown as Preview. */
    previewUrl?: string | null
    timeoutAt: string
    createdAt: string
    ageAt?: string
    /** The task the gate's run belongs to, so the inbox reads as work. Null if the task was deleted. */
    taskId?: string | null
    taskSlug?: string | null
    taskName?: string | null
    /** The execution the gate's run belongs to. Null when the run is standalone or gone. */
    executionId?: string | null
    executionName?: string | null
    /** The CEO can send the run back with feedback (its task routes `ceo_changes_requested`). */
    requestChanges?: boolean
    /**
     * The payload fields this gate's tool lets the CEO reword before approving it (os#911), from the one
     * server-side registry the decision endpoint enforces. Empty or absent for a gate with nothing to edit.
     */
    editableFields?: readonly string[]
    /**
     * The choices this gate offers in place of Approve and Reject, from the registry the decision endpoint
     * enforces. Empty or absent for a gate that asks permission for a call.
     */
    choices?: readonly GateChoice[]
    /** The server flags a question whose answer changes nothing in the OS (#1760): such a gate is drawn as a question. */
    changesNothing?: boolean
    /** The payload as the CEO approved it, when they changed it; the original stays in `payload`. */
    editedPayload?: unknown
    /** Who made that edit. Null when the gate was approved exactly as the run drafted it. */
    editedByHumanId?: string | null
}

/** A gate is loud once it is past `ageAt` (24h), and warm once it has waited an hour. */
export const GATE_HOUR_MS = 3_600_000

/** How long ago a row's `createdAt` was, floored at zero. Shared by anything with a wait to show —
 *  a gate, a plan awaiting a decision — so each does not restate the same one-line subtraction. */
export function waitedMs(row: { createdAt: string }, now: number): number {
    return Math.max(0, now - new Date(row.createdAt).getTime())
}

export function gateAged(gate: GateRow, now: number): boolean {
    return gate.ageAt ? new Date(gate.ageAt).getTime() <= now : false
}

export function gateOverHour(gate: GateRow, now: number): boolean {
    return !gateAged(gate, now) && waitedMs(gate, now) >= GATE_HOUR_MS
}

/** The words on one of a gate's choices, by its key; undefined for a key the gate does not offer. */
export function gateChoiceLabel(gate: Pick<GateRow, 'choices'>, key: string | undefined): string | undefined {
    return gate.choices?.find(choice => choice.key === key)?.label
}

/** What a gate is for: the task it belongs to, falling back to the tool when the task is gone. */
export function gateTaskLabel(gate: Pick<GateRow, 'taskName' | 'taskSlug'>): string {
    return gate.taskName || gate.taskSlug || 'Task unknown'
}

/** One item inside an `os_change` plan payload: create/update/delete of a task, role, team or member. */
export type PlanItem = {
    type: string
    handle: string
} & Record<string, unknown>

/** A plan in front of the CEO, waiting on a decision (docs/plans.md). The one status the Plans nav
 *  badge and the `/plans` list both scope to — an approved or rejected plan is history, not a queue. */
export const PLAN_STATUS_AWAITING_CEO = 'awaiting_ceo'

/** A plan as `GET /plans` and `GET /plans/:id` return it, before `GET /plans/:id`'s extra fields. */
export type PlanRow = {
    id: string
    kind: string
    status: string
    /** The chat session the plan came from; null for one proposed with no chat session (#1579). */
    chatSessionId: string | null
    /** The conversation key of a plan with no chat session: the first proposing run's id (#1579). Null otherwise. */
    planKey?: string | null
    /** The run that produced the plan — what a run page matches on to link to its plan (#227). */
    producedByRunId: string
    ceoIntent: string
    summary: string
    payload: unknown
    decidedBy: string | null
    decidedAt: string | null
    createdAt: string
    updatedAt: string
}

/** `GET /plans/:id`: a plan with its `os_change` items resolved (empty for any other `kind`), its producing run and its gates (the decision history). */
export type PlanDetail = PlanRow & {
    items: PlanItem[]
    run: RunRow | null
    gates: GateRow[]
    /** The apply's ordered result per item. Absent until the plan has been applied. */
    itemResults?: PlanItemResult[] | null
    /**
     * The decision open on this plan (#1984): the producing run's newest gate still waiting on the CEO, shaped like its
     * `/gates` inbox row. Null, or absent from an API older than the field, when nothing is open to decide: the plan
     * page then offers no decision.
     */
    openGate?: GateRow | null
}

/** What applying one item of an `os_change` plan came to: `failed` carries the error that stopped the apply. */
export type PlanItemResult = {
    handle: string
    type: string
    outcome: string
    id?: string
    error?: string
}

export type ToolLayer = 'enterprise' | 'team' | 'member' | 'role' | 'task'

/**
 * Which kind a binding is (#673, #1406): a server, a browser, or a credential the `web.*` tools add to requests to
 * the hosts it names.
 */
export type BindingKind = 'mcp' | 'computer-use' | 'web-credential' | 'secret'

/** How a host credential is sent: `Bearer <secret>`, or the secret alone (#1406). */
export type CredentialScheme = 'bearer' | 'raw'

/**
 * A binding is one kind or another (#673). `mcp` carries the server and its auth; `computer-use`
 * carries the sites a browser may open and where it starts, and has no server, auth or token at all.
 * `web-credential` (#1406) carries the hosts it is sent to, the header and how it is sent; its secret
 * never comes back, and it has no server (`mcpUrl` is empty or absent). `secret` is a value for the repository
 * commands a task version names in its `workspace.credentials`, with custom props beside it and nothing else. A row
 * saved before the kind existed reads as `mcp`.
 */
export type ToolBindingRow = {
    oauthScopes?: string[]
    authorizeParams?: Record<string, string>
    /** Tools a task reaching them must gate: a tool name, or `{ tool, match }` for only the calls carrying `match`. */
    mustGate?: MustGateEntry[]
    imageUrl?: string
    id: string
    toolType: string
    displayName: string
    layer: ToolLayer
    layerId: string
    mcpUrl: string
    /** What the MCP server calls itself, when it said. The Tools page titles a server's connections by it. */
    serverName?: string
    toolsets: string
    authKind: string
    boundByHumanId: string | null
    connected: boolean
    kind?: BindingKind
    sites?: string[]
    startUrl?: string
    /** A host credential's exact hosts (#1406). */
    hosts?: string[]
    /** The request header a host credential is sent in (#1406). */
    header?: string
    /** How a host credential is sent (#1406). */
    scheme?: CredentialScheme
    /**
     * Custom props: the key → value settings typed behind Custom props on Tools, which each tool's docs page lists.
     * A code host's `git.*` ones are what a workspace clones and pushes with (#1025). Never a token.
     */
    props?: Record<string, string>
    /** The `git.*` values this binding's tool type offers to write when it lacks them (its setup preset). */
    propsPreset?: Record<string, string>
    /**
     * Its action map: for each action a task may name under a tool slot this connection fills (`codehost.pr.merge`),
     * the tool it is on this server. Seeded from the tool type's directory entry; whoever may edit the connection may
     * change it. Empty when it maps none.
     */
    slotActions?: Record<string, string>
    /**
     * The actions its tool type's directory entry maps and this connection does not, with the tool the entry maps each
     * to: what "Add missing actions" writes. Only on a connection on the entry's own server; absent when it lacks none.
     */
    missingSlotActions?: Record<string, string>
    /** The Express tool this connection was made through. Absent on one set up by hand. */
    expressToolId?: string
    /**
     * The tool slots this connection fills (`codehost`): a task that names its tools by one of these keys uses this
     * connection for it. Optional, since an older API sends none.
     */
    slots?: string[]
}

/** An Express tool as the directory shows it: a system set up by the platform, connected by signing in. */
export type ExpressTool = {
    id: string
    slug: string
    name: string
    description: string
    imageUrl?: string
    docsUrl?: string
    /** The directory section it is listed under. */
    category?: string
    /** Its place in the directory: higher first, and above zero is featured. */
    rank?: number
    /** Extra words a search for it should match. */
    keywords?: string
    /** A request a person could give to make their first task with it. */
    exampleTask?: string
    /** False only on a draft, which only a platform admin is shown. */
    live: boolean
}

/**
 * How a person signs in to an Express tool: on the sign-in app the platform registered for it, or on a client the
 * tool's server registers for each connection, which needs no app.
 */
export type ExpressSignIn = 'platform_client' | 'registered_client'

/** How a Staff enterprise's plan was set (#394): FloPay's subscription, by hand, or not at all (Free). */
export type StaffPlanSource = 'flopay' | 'manual' | 'none'

/** The billing state the Staff area shows, in one word (#394). */
export type StaffBillingState = 'none' | 'active' | 'past_due' | 'expired' | 'comped' | 'trial' | 'quoted'

/** One figure of this period's usage against the plan's limit; a null limit is unlimited. */
export type StaffUsageFigure = { used: number; limit: number | null }

/** One enterprise as the Staff list shows it (#394): never a run, memory, mail, secret or member's content. */
export type StaffEnterpriseRow = {
    id: string
    name: string
    plan: { id: string; name: string }
    planSource: StaffPlanSource
    billing: { state: StaffBillingState; trialEndsAt: string | null; quotedMonthlyPriceCents: number | null }
    usage: {
        portalRuns: StaffUsageFigure
        runnerMinutes: StaffUsageFigure
        members: StaffUsageFigure
        storage: StaffUsageFigure
    }
    paused: boolean
    closed: { closedAt: string; purgeAt: string | null } | null
}

/** One staff action on an enterprise, as its Staff page lists them. */
export type StaffActionRow = {
    id: string
    actorHumanUserId: string
    action: string
    before: Record<string, unknown> | null
    after: Record<string, unknown> | null
    reason: string | null
    createdAt: string
}

/** One closed period and unit of one enterprise: what was recorded against what FloPay was sent. */
export type StaffReconciliationRow = Record<string, unknown> & {
    enterpriseId: string
    unit: string
    status: string
}

/** A spread of waits, in minutes, over the runs that first started in a Performance window. */
export type StaffPerformanceSpread = { p50Minutes: number; p90Minutes: number; maxMinutes: number }

/** One enterprise on the Staff Performance page: its slot use against its limit, waits, commands and failures. */
export type StaffPerformanceEnterprise = {
    id: string
    name: string
    limit: number
    slotHoursUsed: number
    slotHoursAvailable: number
    percentUsed: number
    hourly: number[]
    parkedHours: number
    queueWait: {
        runs: number
        total: StaffPerformanceSpread
        slot: StaffPerformanceSpread
        member: StaffPerformanceSpread
    }
    idleWhileQueued: { hours: number; slotHours: number }
    commands: {
        count: number
        hours: number
        share: number
        timeouts: number
        timeoutHours: number
        top: { label: string; count: number; hours: number; timeouts: number }[]
    }
    failed: number
    queuedNow: { total: number; longWaiting: number; longWaitingWithFreeSlot: number; runningNow: number }
}

/** `GET /v1/staff/performance`: run capacity and pipeline health across every enterprise, for one window. */
export type StaffPerformanceReport = {
    window: '24h' | '7d'
    from: string
    to: string
    generatedAt: string
    cacheSeconds: number
    hours: string[]
    enterprises: StaffPerformanceEnterprise[]
    lostWork: {
        byReason: { reason: string; runs: number; slotHours: number }[]
        retirementsByDay: { day: string; count: number }[]
    }
    selected: {
        enterpriseId: string
        name: string
        tasks: {
            taskId: string
            name: string
            runs: number
            slotHours: number
            minutesPerRun: number
            parkedHours: number
        }[]
        members: { memberId: string; name: string; cap: number; slotHours: number; percentOfCap: number }[]
        pipelines: {
            count: number
            runs: number
            top: { pipelineKey: string; runs: number; stages: number }[]
            reruns: { taskId: string; name: string; pipelines: number; reruns: number }[]
        }
    } | null
}

/** One enterprise's Staff page: the list row, its FloPay subscription and invoices, reconciliation and history. */
export type StaffEnterpriseDetail = StaffEnterpriseRow & {
    subscription: { id: string; status: string } | null
    /** `'unavailable'` when FloPay did not answer: the rest still loads. */
    invoices: { id: string; amountCents: number; status: string }[] | 'unavailable'
    reconciliation: StaffReconciliationRow[]
    actions: StaffActionRow[]
    /** The coupons on its FloPay subscription (#399): `[]` with no live one, `'unavailable'` when FloPay did not answer. */
    coupons?: StaffCoupon[] | 'unavailable'
    /** The one-off credits on its FloPay customer, as `coupons` is. */
    credits?: StaffCredit[] | 'unavailable'
}

/** One FloPay coupon on an enterprise's subscription: the discount as FloPay words it, and its end or null. */
export type StaffCoupon = { id: string; name: string; discount: string; endsAt: string | null }

/** One one-off credit on an enterprise's FloPay customer, in cents: what was credited and what is left of it. */
export type StaffCredit = { id: string; amountCents: number; remainingCents: number; createdAt: string }

/** What setting a plan by hand or through FloPay sends (#394). */
export type StaffPlanInput = {
    planId: string
    kind: 'comped' | 'trial' | 'quoted' | 'flopay'
    quotedMonthlyPriceCents?: number
    endsAt?: string
    reason?: string
}

/** An Express tool as the Staff area reads it. The client secret never comes back, only whether one is set. */
export type StaffExpressTool = ExpressTool & {
    mcpUrl: string
    signIn: ExpressSignIn
    oauthScopes: string[]
    authorizeParams: Record<string, string>
    /** Null on a tool that needs no client, and on one whose client has not been typed yet. */
    clientId: string | null
    hasClientSecret: boolean
    /** What a member can do with it, and what to know before connecting it: the text of its public page. */
    pageCanDo?: string
    pageNote?: string
    /**
     * Its binding template (#587): what a binding of its tool type starts with when the person binding it leaves a
     * field out. Each left out when the tool has none.
     */
    toolsets?: string
    mustGate?: MustGateEntry[]
    propsPreset?: Record<string, string>
    slotActions?: Record<string, string>
    /** How many connections, across every enterprise, were made through it. */
    connections: number
    updatedAt: string
}

/** One tool of a list as the Staff area imports and exports it. It carries no client and nothing about being live. */
export type ExpressToolEntry = {
    slug: string
    name?: string
    description?: string
    signIn?: ExpressSignIn
    mcpUrl?: string
    oauthScopes?: string[]
    authorizeParams?: Record<string, string>
    category?: string
    rank?: number
    keywords?: string
    docsUrl?: string
    pageCanDo?: string
    pageNote?: string
    exampleTask?: string
    imageUrl?: string
    toolsets?: string
    mustGate?: MustGateEntry[]
    propsPreset?: Record<string, string>
    slotActions?: Record<string, string>
}

export type ExpressImportEntry = {
    slug: string
    action: 'create' | 'update' | 'unchanged' | 'refused'
    /** What an update changes. A logo is named without its contents. */
    changes?: Array<{ field: string; from?: unknown; to?: unknown }>
    error?: string
}

/** What an import did, or on a dry run would do. Nothing is written when any entry is refused. */
export type ExpressImportResult = {
    dryRun: boolean
    applied: boolean
    created: number
    updated: number
    unchanged: number
    refused: number
    entries: ExpressImportEntry[]
}

/** What the Staff area sends to add or change an Express tool. A blank `clientSecret` keeps the saved one. */
export type StaffExpressToolInput = {
    slug?: string
    name?: string
    description?: string
    imageUrl?: string
    mcpUrl?: string
    signIn?: ExpressSignIn
    oauthScopes?: string[] | string
    authorizeParams?: Record<string, string>
    clientId?: string
    clientSecret?: string | null
    docsUrl?: string
    category?: string
    rank?: number
    keywords?: string
    pageCanDo?: string
    pageNote?: string
    exampleTask?: string
    /** The binding template: blank or empty clears each. */
    toolsets?: string
    mustGate?: MustGateEntry[]
    propsPreset?: Record<string, string>
    slotActions?: Record<string, string>
    live?: boolean
}

/**
 * A saved computer-use sign-in (#676) as the portal sees it: which member signed in, on which site, and
 * when. The replayable session never leaves the API — there is nothing here to show or to leak.
 */
export type SavedSignInRow = {
    memberId: string
    site: string
    savedAt: string
}

export type { BoardLane, WorkItemCard } from '@zerohuman/shared'
export { canonicalModelTier, MODEL_TIERS } from '@zerohuman/shared'

/** What each tier means to a person. Wire value and label are the same word since the #664 rename. */
export const MODEL_TIER_LABELS: Record<(typeof MODEL_TIERS)[number], string> = {
    economical: 'economical',
    mid: 'mid',
    frontier: 'frontier',
}

export const MODEL_TIER_HINTS: Record<(typeof MODEL_TIERS)[number], string> = {
    economical: 'cheapest model, fine for API and data work',
    mid: 'good mid-priced model',
    frontier: 'most capable, most expensive',
}

export function modelTierLabel(tier: string | null | undefined): string {
    const canonical = canonicalModelTier(tier)
    return canonical ? MODEL_TIER_LABELS[canonical] : (tier ?? '')
}

/**
 * `GET /v1/llm/tiers` (#664 item 1): what a tier resolves to for one of this tenant's connected providers,
 * so a tier picker can show the model behind it instead of a bare label.
 */
export type LlmTierEntry = {
    tier: string
    provider: AgentProvider
    model: string
    pricing?: { inputPerMillionUsd: number; outputPerMillionUsd: number } | null
}

/** One model from `GET /v1/llm/models`, the provider directory a task can hand-pick from. */
export type LlmDirectoryModel = {
    id: string
    name: string
    contextLength: number | null
    promptPerMillionUsd: number | null
    completionPerMillionUsd: number | null
    free: boolean
}

export type LlmDirectory = {
    defaultModel: string | null
    models: LlmDirectoryModel[]
}

/** The task (and version) a run belongs to; null if the task was deleted. */
export type RunTaskLabel = {
    id: string
    slug: string
    name: string
    versionId: string
    version: number | null
    current: boolean
    /**
     * The owner's version a run of a linked clone ran, named for its owner (#1260); `version` is the clone's own
     * counter. Absent for a task that is its own definition, null for a clone with no record of its source version.
     */
    ownerVersion?: RunOwnerVersion | null
}

/** The owner of a linked clone's definition, and the owner's version a run of it ran. */
export type RunOwnerVersion = { ownerName: string | null; version: number }

/** One neighbouring node in a run's workflow: the run that started it, or a successor it started. */
export type RunFlowLink = {
    /** Null when the successor never started; `reason` says why. */
    runId: string | null
    slug: string | null
    task: { id: string; slug: string; name: string } | null
    status: string | null
    notBefore: string | null
    startedAt: string | null
    finishedAt: string | null
    /** Why a pending successor waits: `delayed`, `overlap` or `member_busy`. */
    waitingReason: string | null
    reason: string | null
    /** Outcomes this successor edge fires on (`succeeded`, `terminal`, `ci_red`, …). Empty for a predecessor. */
    on?: string[]
    /** Linked by timing because neither run recorded the hop (older runs, or a worker running older code). */
    inferred?: boolean
}

/** One action a person takes on a run's live browser (#678): a verb, and what it acts on. */
export type ComputerUseAction = {
    kind: 'navigate' | 'click' | 'type' | 'extract' | 'screenshot'
    url?: string
    selector?: string
    /** What a `type` puts in the field. Sent once and never kept: the run's record and its model never see it. */
    text?: string
}

/** Where the browser is after a person's action, and the page text an `extract` read, cleaned of credentials. */
export type ComputerUseActionResult = { kind: string; url: string; text?: string }

export type RunDetail = {
    task?: RunTaskLabel | null
    run: RunRow
    events: Array<{ id: string; type: string; payload: Record<string, unknown> | null; createdAt: string }>
    gates: GateRow[]
    flow?: { previous: RunFlowLink | null; next: RunFlowLink[] }
    execution?: { id: string; name: string; resources: ExecutionResource[] } | null
    /**
     * The input fields a retry of this run is held to — those its task's current version declares, the version a
     * retry runs — so Retry can offer a correction form for them.
     */
    inputs?: TaskInputField[] | null
    /** The run's cost split by how it was paid for: per token, or as a share of a subscription seat. */
    cost?: RunCost
    /**
     * The model the task named, when the run used another: the connection that answered does not offer it, so the
     * run used the closest model that connection has. Absent when the run used the model its task named, or none.
     */
    modelStandsInFor?: string | null
}

export function emptyCharter(): TeamCharter {
    return {
        opportunity: '',
        purpose: '',
        success: '',
        metric: '',
        kit: '',
        roles: '',
        timeline: '',
    }
}

export function formatUsdCents(cents: number | null | undefined): string {
    if (cents === null || cents === undefined) {
        return 'unlimited'
    }
    return `$${(cents / 100).toFixed(2)}`
}

/**
 * The shared validity rule for a concurrent-run limit, enterprise-wide or per-team: a whole number of at
 * least 1. Used both here (where blank is also allowed, meaning "inherit") and by the enterprise-wide
 * field on `HomeView.vue` (where it is not), so the two never drift apart on what counts as valid.
 */
export function isValidConcurrentRunLimit(value: number): boolean {
    return Number.isInteger(value) && value >= 1
}

/**
 * A concurrent-runs field out of its input: blank means "inherit the enterprise limit" (`null`), never `0`.
 * A non-empty value that isn't a whole number of at least 1 (including `NaN`/`Infinity` from a malformed
 * string) throws instead of silently falling through — `JSON.stringify` turns both of those into `null` on
 * the wire, which would otherwise clear the team's override rather than surface a validation error.
 *
 * Takes `string | number` because Vue's `v-model` on `<input type="number">` assigns a `number` straight
 * to the bound ref (not a string) once the field holds a parseable value, regardless of the ref's own
 * declared/initial type — only an empty or not-yet-parseable field keeps the ref as a string.
 */
export function parseConcurrentRunLimit(input: string | number): number | null {
    const trimmed = String(input).trim()
    if (trimmed === '') {
        return null
    }
    const parsed = Number(trimmed)
    if (!isValidConcurrentRunLimit(parsed)) {
        throw new Error('Enter a whole number of at least 1, or leave it blank to inherit the enterprise limit.')
    }
    return parsed
}

/** The inverse of `parseConcurrentRunLimit`: a team's own override reads back blank, not `0`. */
export function formatConcurrentRunLimit(limit: number | null | undefined): string {
    return limit == null ? '' : String(limit)
}

/**
 * A block whose reason carries the marker `builtinMcpHttpError` writes (apps/api/src/tools/mcp-client.ts,
 * issue #336): the OS's own MCP server refused the call, not a vendor connection somebody bound. Read here
 * because the wording below would otherwise tell a reader to rebind something — the harness credential that
 * connection uses is minted and rotated by the OS itself (#328), so there is nothing for anyone to do but
 * wait out the restart. Spelled as a literal rather than imported: the portal reads every other reason shape
 * (`loop_cap:…` above) the same way, having no import path into the API's source.
 */
const BUILTIN_MCP_BLOCK = /^mcp_http_\d+:builtin(:|$)/

/**
 * A `tool_action_unmapped:<slot.action,…>` block in plain words: which slot's connection does not say which of its
 * tools to use, and for which actions. Not a credential: the connection's token is fine, its action map lacks them.
 */
export function actionMapWords(reason: string): string {
    const names = [
        ...new Set(
            reason
                .replace(/^tool_action_unmapped:/, '')
                .split(',')
                .map(name => name.trim())
                .filter(Boolean),
        ),
    ]
    const slots = [...new Set(names.map(name => name.split('.')[0] ?? name))]
    const actions = names.map(name => name.slice(name.indexOf('.') + 1))
    const slot = slots.length === 1 ? `its ${slots[0]} slot` : 'its tool slots'
    const listed = actions.length > 1 ? `${actions.slice(0, -1).join(', ')} and ${actions.at(-1)}` : actions.join('')
    return `The connection filling ${slot} doesn't say which of its tools to use for ${listed}.`
}

/** Human reading of a `blocked` run: what stopped it and what happens next. */
export function blockedReason(run: RunRow): string | null {
    if (run.status !== 'blocked') {
        return null
    }
    const reason = run.blockedReason ?? 'unknown'
    const cap = /^loop_cap:([^:]+):(\d+)\/(\d+)$/.exec(reason)
    if (cap) {
        return `${cap[1]} has already run ${cap[2]} times on this issue (cap ${cap[3]}). Retry starts this issue's loop caps again.`
    }
    if (BUILTIN_MCP_BLOCK.test(reason)) {
        return `The OS MCP server refused this call (${reason}). Nothing for anyone to rebind — that server is the OS's own plumbing, and the run resumes automatically with backoff as soon as it answers again.`
    }
    switch (run.blockClass) {
        case 'model':
            return `Model unavailable (${reason}). Resumes when the model answers or the tier is pointed at another model.`
        case 'definition':
            return `Task definition cannot run (${reason}). Resumes after the task is edited.`
        case 'credentials':
            return `A credential was refused or is missing (${reason}). Rebind the tool or key; resumes as soon as credentials change.`
        case 'action_map':
            return `${actionMapWords(reason)} Resumes as soon as the connection's action map names it.`
        case 'infra':
            return `${reason}. Resumes automatically with backoff, or as soon as credentials change.`
        default:
            return reason
    }
}

/**
 * Human reading of a run that ended its pipeline without failing: it succeeded on an outcome its own task
 * graph has no edge for, or named none where its graph goes on only from a named one, so only `terminal`
 * successors fired and the chain stopped.
 */
export function stoppedReason(run: RunRow): string | null {
    const outcome = deadEndOutcome(run.stoppedReason)
    if (outcome === null) {
        return null
    }
    return outcome === NO_OUTCOME_NAMED
        ? 'Succeeded without naming an outcome, so its task has no next step to take.'
        : `Succeeded with outcome "${outcome}": this task's successors have no edge for it, so nothing started.`
}

/**
 * The outcome a `succeeded` run dead-ended on, or null when it did not: recorded as `outcome_not_declared:<outcome>`,
 * or as `No route for outcome: <outcome>` when an execution's stop was read from the `successor.dead_end` event alone.
 */
function deadEndOutcome(reason: string | null | undefined): string | null {
    const text = reason ?? ''
    return (/^outcome_not_declared:(.+)$/.exec(text) ?? /^No route for outcome: (.+)$/.exec(text))?.[1] ?? null
}

/**
 * A recorded stop that is a dead end, said as the run's own page says it ("Pipeline stopped: …"), or null for any
 * other reason, which the caller shows as it came. Read by `runStoppedLine` and an execution's attention note.
 */
export function pipelineStopLine(reason: string | null | undefined): string | null {
    const outcome = deadEndOutcome(reason)
    if (outcome === null) {
        return null
    }
    return outcome === NO_OUTCOME_NAMED
        ? 'Pipeline stopped: the run named no outcome, so its task has no next step to take.'
        : `Pipeline stopped: the run ended on "${outcome}", which its task has no next step for.`
}

/**
 * The one line a run that dead-ended shows where its status is read: run detail under its title, the runs list and
 * the task page as the chip's tooltip. Null for a run that did not. A run that named no outcome on a graph that
 * branches is said as that, not as an outcome called "(no outcome)".
 */
export function runStoppedLine(run: Pick<RunRow, 'status' | 'stoppedReason'>): string | null {
    return run.status === 'succeeded' ? pipelineStopLine(run.stoppedReason) : null
}

/**
 * The one ladder every wait and age reads on: `42 s` under a minute, `6 min` under an hour, `2 h` under a day,
 * then `1 day` / `3 days`. Always a whole number, truncated, so a wait under an hour never reads `1 h`. Only the
 * day rung is pluralised. Zero, a negative input (clock skew) or a non-finite one reads `0 s`.
 */
/**
 * How long a decision has waited, in words, as its card says it: "42 seconds", "20 minutes", "2 hours", "1 day".
 * The card reads it after "Waiting", so it is spelled out where a table's `formatDuration` is short.
 */
export function formatWaited(ms: number): string {
    const seconds = Number.isFinite(ms) ? Math.floor(Math.max(0, ms) / 1000) : 0
    const counted = (count: number, unit: string): string => `${count} ${unit}${count === 1 ? '' : 's'}`
    if (seconds < 60) {
        return counted(seconds, 'second')
    }
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) {
        return counted(minutes, 'minute')
    }
    const hours = Math.floor(minutes / 60)
    if (hours < 24) {
        return counted(hours, 'hour')
    }
    return counted(Math.floor(hours / 24), 'day')
}

export function formatDuration(ms: number): string {
    const seconds = Number.isFinite(ms) ? Math.floor(Math.max(0, ms) / 1000) : 0
    if (seconds < 60) {
        return `${seconds} s`
    }
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) {
        return `${minutes} min`
    }
    const hours = Math.floor(minutes / 60)
    if (hours < 24) {
        return `${hours} h`
    }
    const days = Math.floor(hours / 24)
    return `${days} ${days === 1 ? 'day' : 'days'}`
}

/** An age: the same ladder as {@link formatDuration}, followed by ` Ago` (`42 s Ago`, `6 min Ago`, `3 days Ago`). */
export function formatAgo(ms: number): string {
    return `${formatDuration(ms)} Ago`
}

export function skipReason(run: RunRow): string | null {
    if (run.status !== 'skipped_overlap') {
        return null
    }
    const reason = run.input && typeof run.input.reason === 'string' ? run.input.reason : 'overlap'
    if (reason === 'member_busy') {
        return 'member already has an active run'
    }
    return 'task already has an active run'
}

/**
 * Human reading of a `failed` run: the reason the worker recorded on the run's own
 * terminal `run.failed` event.
 *
 * Only `run.failed` counts. A run's event log can carry other `*.failed`-suffixed
 * types (`run.recovery_failed`, `run.resume_failed`) that describe a step which was
 * retried or recovered, not why the run finally stopped, so matching on the suffix
 * could surface an unrelated earlier reason. If the terminal event carries no reason
 * we fall back to the run's own input rather than to an older event.
 */
export function failReason(run: RunRow, events: RunDetail['events'] = []): string | null {
    if (run.status !== 'failed') {
        return null
    }
    for (let index = events.length - 1; index >= 0; index -= 1) {
        const event = events[index]
        if (event.type !== 'run.failed') {
            continue
        }
        const reason = event.payload?.reason
        if (typeof reason === 'string' && reason) {
            return reason
        }
        break
    }
    const input = run.input?.reason
    if (typeof input === 'string' && input) {
        return input
    }
    return 'unknown'
}

/** What a screen does with a decision the API refused: the sentence it shows, and whether to read the thread again. */
export type DecisionRefusal = { message: string; reread: boolean }

/**
 * Human reading of a decision the API refused in chat (#1256): a tool-call card's Allow / Always allow / Deny, or a
 * gate decided from the thread. `request()` throws an Error whose message is the API's code, so the cause is named
 * rather than guessed. Anything else — a network failure, a 5xx, `invalid_decision` (only ever a portal bug, which
 * nobody can act on), a code this does not know — says only that the decision was not recorded: guessing "already
 * decided" sent the 2026-10-01 incident, a thread stuck replying, the wrong way. The fallback does not tell the
 * person to retry outright either: for some of those causes (`invalid_decision`, a gate decided elsewhere) a retry
 * can never succeed, so it points them at the thread and only to try again if the decision is still waiting there.
 * `reread` is true only when the thread already holds what became of the call, so the screen reads it again and
 * the card shows that instead.
 */
export function decisionRefusal(caught: unknown, memberName = 'The member'): DecisionRefusal {
    const code = caught instanceof Error ? caught.message : ''
    switch (code) {
        case 'chat_replying':
            return {
                message: `${memberName} is still replying, so your decision was not recorded. Try again once the reply is in.`,
                reread: false,
            }
        case 'not_awaiting_confirmation':
            return {
                message:
                    'That is no longer waiting on you, so your decision was not recorded. The thread shows what happened to it.',
                reread: true,
            }
        case 'chat_closed':
            return {
                message: 'This chat is closed, so your decision was not recorded. Start a new chat to carry on.',
                reread: false,
            }
        case 'chat_not_found':
            return { message: 'This chat is no longer available, so your decision was not recorded.', reread: false }
        default:
            return {
                message: 'Could not record that. Check the thread and try again if it is still waiting.',
                reread: false,
            }
    }
}

/**
 * One decision on a question gate, with the answer the asking member recommends (`os.ask_ceo` `questions[]`). The
 * field is called `ctoRecommendation` whoever asked: it is the tool's own name for it, not who a person is shown.
 */
export type CeoQuestion = { number: number; question: string; context?: string; ctoRecommendation: string }

/** The reply to one question: an answer (the person's own, or the recommendation accepted), or a request for more detail. */
export type CeoQuestionAnswer = { number: number; answer: string; requiresMoreDetail: boolean }

/**
 * The questions on a CEO question gate (`os.ask_ceo`), or null for a tool gate. Answered one by one on the run
 * page, not with a click. The harness refuses an ask without them, so a question gate always has at least one.
 */
export function ceoQuestions(gate: Pick<GateRow, 'toolName' | 'payload'>): CeoQuestion[] | null {
    if (gate.toolName !== 'os.ask_ceo') {
        return null
    }
    const payload = gate.payload && typeof gate.payload === 'object' ? (gate.payload as Record<string, unknown>) : {}
    const rows = Array.isArray(payload['questions']) ? payload['questions'] : []
    return rows.flatMap((row: unknown): CeoQuestion[] => {
        const item = row && typeof row === 'object' ? (row as Record<string, unknown>) : {}
        if (typeof item['number'] !== 'number' || typeof item['question'] !== 'string') {
            return []
        }
        const context = typeof item['context'] === 'string' && item['context'].trim() ? item['context'] : undefined
        return [
            {
                number: item['number'],
                question: item['question'],
                ctoRecommendation: typeof item['ctoRecommendation'] === 'string' ? item['ctoRecommendation'] : '',
                ...(context ? { context } : {}),
            },
        ]
    })
}

export function formatGatePayload(payload: unknown): { preview: string; json: string } {
    const json = JSON.stringify(payload ?? null, null, 2)
    if (payload && typeof payload === 'object') {
        const record = payload as Record<string, unknown>
        if (typeof record.body === 'string') {
            return { preview: record.body, json }
        }
        if (typeof record.text === 'string') {
            return { preview: record.text, json }
        }
    }
    return { preview: json, json }
}

/**
 * What a refusal from `request` says about the request itself, beside the message a screen shows (#1677): the path
 * it asked for and the HTTP status. The path stops before its query string, which can carry filters and ids, but it
 * keeps any id in its segments (`/v1/runs/run-1/retry`). `reportHandledError` in `sentry.ts` replaces those with
 * `:id` before it reports (a uuid, a numeric id, a tool-call id, a domain or an encoded value; not a slug, which no
 * portal request path carries), so a handled failure goes to Sentry without the response body or the ids in its path.
 *
 * `upstreamStatus` is the status another server answered the API with, when the refusal names one (#1936): a refused
 * OAuth registration is our 400 carrying the MCP server's 403. Only an integer is kept.
 */
export interface RequestFailure {
    path: string
    status: number
    upstreamStatus?: number
}

/** A request path without its query string or fragment: `/v1/runs?live=true` is `/v1/runs`. */
function pathWithoutQuery(path: string): string {
    return path.replace(/[?#].*$/, '')
}

/**
 * The path and status a refusal from `request` carries, or null for any other failure: a network error, a body
 * that would not parse, or a fault in the screen's own code.
 */
export function requestFailure(error: unknown): RequestFailure | null {
    if (!(error instanceof Error)) {
        return null
    }
    const { path, status, upstreamStatus } = error as Error & Partial<Record<keyof RequestFailure, unknown>>
    if (typeof path !== 'string' || typeof status !== 'number') {
        return null
    }
    return Number.isInteger(upstreamStatus)
        ? { path, status, upstreamStatus: upstreamStatus as number }
        : { path, status }
}

/**
 * The error code a refusal from `request` was sent with (the body's `error`, such as `catalog_crafted_not_in_plan`),
 * or null when it named none or the failure was not a refusal. The message stays the server's sentence; this lets a
 * screen choose its words by the code rather than by the HTTP status alone (#449).
 */
export function refusalCode(error: unknown): string | null {
    if (!(error instanceof Error)) {
        return null
    }
    const { refusal } = error as Error & { refusal?: unknown }
    return typeof refusal === 'string' ? refusal : null
}

/**
 * The code the API refuses a Zero Human role or task with on a plan that does not include them (#449). The code keeps
 * the stored provenance's name (`zerohuman-crafted`); what a person reads says "Zero Human". Every plan includes
 * them today, so no plan as shipped is refused.
 */
export const CRAFTED_NOT_IN_PLAN = 'catalog_crafted_not_in_plan'

/** What every screen says when the plan refuses Zero Human content, beside a link to Billing (#449). */
export const CRAFTED_NOT_IN_PLAN_MESSAGE = 'Your plan does not include Zero Human roles and tasks.'

/** Whether `error` is the API's refusal of Zero Human content on the caller's plan, read from its code (#449). */
export function isCraftedRefusal(error: unknown): boolean {
    return refusalCode(error) === CRAFTED_NOT_IN_PLAN
}

/** The code the API refuses a Zero Human team size the caller's plan does not include with (os#1624). */
export const TEAM_SIZE_NOT_IN_PLAN = 'catalog_team_size_not_in_plan'

/** Whether `error` is the API's refusal of a team size the caller's plan does not include, read from its code. */
export function isTeamSizeRefusal(error: unknown): boolean {
    return refusalCode(error) === TEAM_SIZE_NOT_IN_PLAN
}

/**
 * The plan unit a refusal from `request` names (the body's `unit`: `member`, `schedule`), or null when it names none
 * (#1840). A `plan_limit_reached` refusal is one sentence per unit, so a screen chooses its words by this.
 */
export function refusalUnit(error: unknown): string | null {
    if (!(error instanceof Error)) {
        return null
    }
    const { unit } = error as Error & { unit?: unknown }
    return typeof unit === 'string' ? unit : null
}

/**
 * Note on the refusal parser below: when a body carries both a string `message` and a string `error`, this one
 * prefers `message` (the server's own sentence, which these screens show verbatim). `api-users.ts`'s
 * `errorCode()` deliberately prefers `error` instead, because the People screen switches on the code. The two
 * are not interchangeable — changing the order here does not change it there.
 *
 * A refusal also carries its `RequestFailure` (path and status), read through `requestFailure`. The message
 * stays the server's own words, as before.
 */
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(path, {
        ...init,
        headers: { 'Content-Type': 'application/json', ...init?.headers },
    })
    if (!response.ok) {
        throw await refusalOf(path, response)
    }
    if (response.status === 204) {
        return undefined as T
    }
    return (await response.json()) as T
}

/**
 * The owner deletes the enterprise from the Danger zone (#72), confirming with its name typed exactly. A refusal (the
 * wrong name, somebody who is not the owner, or a subscription FloPay did not cancel) is thrown with the server's own
 * sentence, as every `request` refusal is.
 */
export async function closeEnterprise(enterpriseId: string, confirmName: string): Promise<void> {
    await request<unknown>(`/v1/enterprises/${encodeURIComponent(enterpriseId)}/close`, {
        method: 'POST',
        body: JSON.stringify({ confirmName }),
    })
}

/**
 * A response's body as a Blob (#1734), for bytes a screen shows or saves rather than reads: a chat file. Asked for
 * the way `request` asks, with the same session, and refused the same way, so a refusal carries the server's code
 * (`chat_not_found`) and its `RequestFailure`.
 */
export async function requestBlob(path: string): Promise<Blob> {
    const response = await fetch(path)
    if (!response.ok) {
        throw await refusalOf(path, response)
    }
    return response.blob()
}

/** The error a refused response is thrown as, by `request` and `requestBlob` alike: the server's words and code. */
async function refusalOf(path: string, response: Response): Promise<Error> {
    let code = String(response.status)
    // A field-level refusal (`input_required`, `input_undeclared`, `input_invalid`) names the input it is about, so a form can
    // show it against that field (#1132).
    let field: string | undefined
    // The refusal's own code, kept beside the server's sentence so a screen can choose what to say by it (#449).
    let refusal: string | undefined
    // The plan unit a `plan_limit_reached` refusal is about (#1840), so a screen can say which limit was met.
    let unit: string | undefined
    // The status another server answered the API with, when the refusal names one (#1936).
    let upstreamStatus: number | undefined
    // The terms version a `billing_terms_not_accepted` refusal names (os#1618), so Billing can ask for exactly it.
    let termsVersion: string | undefined
    try {
        const body = (await response.json()) as {
            error?: unknown
            message?: unknown
            field?: unknown
            unit?: unknown
            upstreamStatus?: unknown
            termsVersion?: unknown
        }
        if (typeof body.field === 'string') {
            field = body.field
        }
        if (typeof body.termsVersion === 'string' && body.termsVersion !== '') {
            termsVersion = body.termsVersion
        }
        if (typeof body.upstreamStatus === 'number' && Number.isInteger(body.upstreamStatus)) {
            upstreamStatus = body.upstreamStatus
        }
        if (typeof body.error === 'string') {
            refusal = body.error
        }
        if (typeof body.unit === 'string') {
            unit = body.unit
        }
        if (body.message && typeof body.message === 'object' && body.message !== null && 'error' in body.message) {
            const nested = (body.message as { error?: unknown }).error
            if (typeof nested === 'string') {
                code = nested
            }
        } else if (typeof body.message === 'string') {
            code = body.message
        } else if (typeof body.error === 'string') {
            code = body.error
        }
    } catch {
        // keep status code
    }
    const failure: RequestFailure = {
        path: pathWithoutQuery(path),
        status: response.status,
        ...(upstreamStatus === undefined ? {} : { upstreamStatus }),
    }
    return Object.assign(
        new Error(code),
        failure,
        field === undefined ? {} : { field },
        refusal === undefined ? {} : { refusal },
        unit === undefined ? {} : { unit },
        termsVersion === undefined ? {} : { termsVersion },
    )
}

/** The kinds of key the onboarding card offers (#1716): an Anthropic, OpenAI, xAI or OpenRouter one. */
export type AiAccountProvider = 'anthropic' | 'openai' | 'xai' | 'openrouter'

/**
 * Why a key was not connected, as the server classified the provider's answer. `workspace_scope` is an Anthropic key
 * tied to the organization rather than a workspace, which Anthropic will not serve a completion on (#1800).
 *
 * `authorization` is the Claude Pro/Max option's own kind (#1719): the pop-up was blocked, or starting or completing
 * Anthropic's authorization failed. The key endpoint never sends it; the step view sets it.
 */
export type AiAccountFailureReason =
    | 'invalid_key'
    | 'no_credit'
    | 'rate_limited'
    | 'workspace_scope'
    | 'unavailable'
    | 'authorization'

export type AiAccountFailure = { reason: AiAccountFailureReason; message: string }

export type AiAccountResult = { ok: true } | ({ ok: false } & AiAccountFailure)

const AI_ACCOUNT_REASONS: readonly string[] = [
    'invalid_key',
    'no_credit',
    'rate_limited',
    'workspace_scope',
    'unavailable',
]

/**
 * What the card says when the answer was not one the server gave in plain words (a network error, an outage). The
 * check is ours, so the problem is put on our side and never on the key: the same sentence the server sends (#1800).
 */
export const AI_ACCOUNT_UNREACHABLE: AiAccountFailure = {
    reason: 'unavailable',
    message: "Our check didn't go through. Your key may be fine. Try again, or use the other option.",
}

/**
 * `POST /v1/llm/ai-account`. A refusal is an answer here, not an error: the server sends its reason and plain words
 * with a 4xx, and the card shows them. Anything else (a network error, an answer with neither) is the generic
 * our-side `unavailable` failure, so it never reads as the user's mistake. The key goes in the body and nowhere else.
 */
export async function connectAiAccount(account: {
    provider: AiAccountProvider
    apiKey: string
}): Promise<AiAccountResult> {
    type Answer = { ok?: unknown; reason?: unknown; message?: unknown } | null
    let body: Answer
    try {
        const response = await fetch('/v1/llm/ai-account', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(account),
        })
        body = (await response.json()) as Answer
    } catch {
        body = null
    }
    if (body?.ok === true) {
        return { ok: true }
    }
    if (
        body?.ok === false &&
        typeof body.reason === 'string' &&
        AI_ACCOUNT_REASONS.includes(body.reason) &&
        typeof body.message === 'string'
    ) {
        return { ok: false, reason: body.reason as AiAccountFailureReason, message: body.message }
    }
    return { ok: false, ...AI_ACCOUNT_UNREACHABLE }
}

/**
 * `PUT /v1/enterprise/onboarding/ai-account/reached` (#1718): tells the server the signed-in person reached the AI
 * account step in this tab, so leaving it is remembered and they get one reminder if no account is connected a day
 * later. The step's screen is shared by both setup paths and does not know which one the person is on, so it names
 * no path and the server keeps the one it has. Never throws: the step opens and works whether or not the server
 * heard.
 */
export async function reachAiAccountStep(session: string): Promise<void> {
    try {
        await request<unknown>('/v1/enterprise/onboarding/ai-account/reached', {
            method: 'PUT',
            body: JSON.stringify({ session }),
        })
    } catch {
        // Progress is best effort here: a person who may not write setup state still connects their account.
    }
}

/**
 * Why a founder leaving the AI account step says they are going (#1770): the step's closed choice, never free text.
 * The server stores anything outside it as `other` too, so the two can never count a reason differently.
 */
export type AiAccountExitReason = 'no_account' | 'thought_model_included' | 'other'

/**
 * `POST /v1/llm/ai-account/exit` (#1770): the founder leaving the AI account step says why, from the step's closed
 * choice. The server records the first exit per person and ignores the rest. Telemetry never gets in the founder's way,
 * so this never throws: it answers whether the server took the reason, and false for anything else.
 */
export async function leaveAiAccountStep(reason: AiAccountExitReason): Promise<boolean> {
    try {
        await request<void>('/v1/llm/ai-account/exit', { method: 'POST', body: JSON.stringify({ reason }) })
        return true
    } catch {
        return false
    }
}

/** The two ways a founder sets up (#1737): the chat with the COO, or the step-by-step screens. */
export type OnboardingPath = 'chat' | 'guided'

/** The enterprise's one setup state as the server serves it to either path (#1737). */
export interface OnboardingStateView {
    /** `skipped` (#1742): left for the dashboard in Advanced mode, every answer kept, until a save or a switch. */
    status: 'not_started' | 'in_progress' | 'complete' | 'skipped'
    steps: string[]
    currentStep: string | null
    completedSteps: string[]
    answers: Record<string, unknown>
    path: OnboardingPath | null
    /** From `team_setup` on: what the team cloned during setup still needs, worked out by the server (#1739). */
    teamSetup?: TeamSetupView
    /**
     * At `work_profile`: the kinds of work the Catalog offers, the list the setup conversation's greeting is built from
     * (#1742). The guided Kind of work screen offers them when it was not opened from that conversation.
     */
    kindsOfWork?: string[]
    /**
     * At `team_proposal`, and at `names` so the choice can be gone back to: the catalog teams setup proposes for the
     * saved kind of work (#2216), the same teams the setup conversation's COO presents. A choice is saved as
     * `{ catalogTeamId }`. Absent at every other step, and when the saved profile holds no kind of work to propose
     * from.
     */
    teamProposal?: TeamProposalView
    /**
     * At `names`: the proposed team's template, as the naming form needs it (#2216). Absent at every other step, and
     * when the proposal names no catalog team.
     */
    namesFor?: NamesForView
    /**
     * Whether the signed-in person reading has ever reached the AI account step (#1796, from #1718's record). Absent
     * when the server could not say, or for a caller that is no person.
     */
    aiAccountStepReached?: boolean
    /**
     * The enterprise's assembly (#1743): started on the server when `names` is saved, so both paths follow the same
     * log of real setup events. Absent before it has started.
     */
    assemblyId?: string
    /**
     * The optional `more_tools` step (#1942): offered after `team_setup` is saved, until it is skipped or done. `needs`
     * is every tool type the cloned team needs, with the same name and status Team setup gives it. Absent when it is
     * not offered.
     */
    moreTools?: { needs: ToolNeed[] }
}

/** One catalog team setup offers at `team_proposal` (#2216): what a person needs to pick it, never its owner. */
export interface CatalogTeamOffer {
    id: string
    slug: string
    name: string
    description: string
    kindOfWork: string | null
    /** How many teammates the template assembles. */
    teammates: number
    /** The titles of the roles its teammates hold, each once, in the template's order. */
    roles: string[]
}

/**
 * The proposal at `team_proposal` (#2216): the Catalog's teams for the saved kind of work, the first recommended, or
 * every team as a candidate for work the Catalog has no kind for. `tellYourCoo` offers "Tell your COO what you need".
 */
export interface TeamProposalView {
    recommended: CatalogTeamOffer[]
    candidates: CatalogTeamOffer[]
    tellYourCoo: boolean
}

/** The proposed template at `names` (#2216): one entry per teammate to name, with the role titles it goes by unnamed. */
export interface NamesForView {
    template: NamesTemplate
}

export interface NamesTemplate {
    id: string
    name: string
    slug: string
    teammates: Array<{ roles: string[] }>
}

/** One tool type the cloned team needs (#1942): display name, tool type and its one status on both screens. */
export interface ToolNeed {
    name: string
    connect: string
    status: 'connected' | 'not_connected'
}

/** The tone of one line of the assembly log (#1743). */
export type AssemblyTone = 'pending' | 'done' | 'failed'

/** One line of the assembly log, recorded by the server once the step it reports had committed (#1743). */
export interface AssemblyEvent {
    /** Its place in the log, from 1. */
    seq: number
    text: string
    tone: AssemblyTone
    /** The given name of the teammate the line is about, when it is about one. */
    teammate?: string
}

/** The assembly's log as the server serves it (#1743): the events after a cursor, and where the work stands. */
export interface AssemblyView {
    id: string
    /** `complete`: every teammate is ready; `partial`: the team is online without some; `failed`: none is ready. */
    status: 'running' | 'complete' | 'partial' | 'failed'
    /** The `seq` of the newest event the server holds: ask for what is after it next. */
    cursor: number
    events: AssemblyEvent[]
    ready: number
    total: number
    /** The teammates whose automatic retries ran out: each offers Retry. */
    retryable: string[]
}

function qs(query: Record<string, string | number | undefined> = {}): string {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === '') {
            continue
        }
        params.set(key, String(value))
    }
    const text = params.toString()
    return text ? `?${text}` : ''
}

/**
 * The decision request body shared by `decideGate` and `decideChatGate` (#229): a decision, a trimmed
 * note when one was left, and answers when the gate asked questions. `gateId` is included only for the
 * chat route, which resolves the gate from the thread rather than the URL.
 */
function decisionBody(
    decision: GateDecision,
    note?: string,
    answers?: CeoQuestionAnswer[],
    gateId?: string,
    /** The whole edited payload, only when the CEO changed one of the gate's editable fields (os#911). */
    edit?: Record<string, unknown>,
    /** The key of the choice the decision was made through, only on a gate that offers choices. */
    choice?: string,
) {
    return {
        ...(gateId ? { gateId } : {}),
        decision,
        ...(note?.trim() ? { note: note.trim() } : {}),
        ...(answers ? { answers } : {}),
        ...(edit ? { edit } : {}),
        ...(choice ? { choice } : {}),
    }
}

/**
 * The IANA zone this browser is in, or undefined where `Intl` cannot say. Undefined rather than a guessed
 * `UTC`: the server then falls back for itself rather than being told something that is not true.
 */
function browserTimezone(): string | undefined {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return zone || undefined
}

/**
 * The KPI read contract HQ, the team page and the teams list will read (#898): every team's key
 * KPI, and the company's own KPIs, each with its light, trend and when it was last read. #895 and
 * #896 land the model and the reading itself; this is the shape those endpoints hand back.
 */
export type KpiRag = 'red' | 'amber' | 'green'

/** One KPI's current reading, as HQ, a team page and the teams list all read it. */
export interface KpiReadingView {
    id: string
    name: string
    /** The light off the latest reading, or null when there is none to judge: no reading yet, or no target. */
    rag: KpiRag | null
    trend: 'up' | 'down' | 'flat'
    /** ISO 8601, or null when the KPI has never been read: HQ calls that overdue. */
    lastReadAt: string | null
    /** How long a reading holds before it counts as overdue, in days. */
    cadenceDays: number
    /** Stale past its own cadence, or never read at all. */
    overdue: boolean
    /**
     * Waiting for a person to sign it off, on a team page's tree only (the board never lists one): no run sees it or
     * records a reading for it until then, so it has no light and is never overdue.
     */
    pending?: boolean
}

/** One reading of a KPI as `GET /v1/kpis/:kpiId/readings` lists it: the value, when it was taken, and by whom. */
export interface KpiReadingRecord {
    id: string
    kpiId: string
    value: number
    /** ISO 8601. */
    takenAt: string
    recordedBy: string
}

/** One team's row on HQ and the teams list: its key KPI, or none set yet. */
export interface TeamKpiBoardRow {
    teamId: string
    teamName: string
    key: KpiReadingView | null
}

/** What HQ's board (and the teams list) reads for the whole enterprise. */
export interface KpiBoard {
    enterprise: KpiReadingView[]
    teams: TeamKpiBoardRow[]
}

/** One task's KPIs, nested under its role on the team page. */
export interface TaskKpiView {
    taskId: string
    taskName: string
    kpis: KpiReadingView[]
}

/** One role's KPIs, and its tasks' KPIs nested beneath it, on the team page. */
export interface RoleKpiView {
    roleId: string
    roleName: string
    kpis: KpiReadingView[]
    tasks: TaskKpiView[]
}

/** A team's page: all of its own KPIs, with its role and task KPIs nested beneath them. */
export interface TeamKpiTree {
    kpis: KpiReadingView[]
    roles: RoleKpiView[]
}

/** How often a paid plan is billed (os#1615). */
export type BillingInterval = 'monthly' | 'annual'

/**
 * One plan on offer. Everything past `selfServe` is optional: an API older than the plan cards sends only the prices,
 * and a plan with `selfServe: false` is then "talk to us".
 */
export type BillingPlan = {
    planId: string
    /** Whole USD per month and per year, each null where the plan has no self-serve price at that interval. */
    monthly: number | null
    annual: number | null
    selfServe: boolean
    /** The plan's product name and who it is for. */
    name?: string
    tagline?: string
    /** A plan priced by hand per contract ("talk to us"), from `fromMonthly` USD a month. */
    custom?: boolean
    fromMonthly?: number | null
    included?: {
        members: number
        runnerMinutes: number
        portalRuns: number
        concurrentRuns: number
        storageGb: number
    }
    /**
     * FloPay's hosted checkout page for the plan per interval, opened for this enterprise (its signed reference is
     * already in the link), where the deployment configures one.
     */
    checkoutLinks?: Record<BillingInterval, string | null>
}

/** The Billing page's overview: the plan, its period, this period's usage per billed unit, and the plans on offer. */
export type BillingOverview = {
    plan: string
    /** How often the subscription is billed. Null (or absent) without a live subscription, as on Free. */
    interval?: BillingInterval | null
    periodStart: string | null
    periodEnd: string | null
    pastDue: boolean
    /** When a cancelled subscription ends; null (or absent, from an older API) for one nobody cancelled. */
    cancelAt?: string | null
    /** `included` is null where the plan includes an unlimited amount. */
    units: { unit: string; used: number; included: number | null }[]
    /** The AI members the enterprise has now. Optional: an API older than the plan cards sends none. */
    members?: number
    /**
     * The most runs that ran at once this period, held against the concurrent runs ceiling. Optional: an API older
     * than this sends none, and Billing then shows no concurrent runs meter.
     */
    concurrentRunsPeak?: number
    /**
     * What the page may offer to do with a card: add one, or choose which is charged. Optional: an API older than
     * this sends none, and both are then offered as they were.
     */
    cards?: { canAdd: boolean; canMakeDefault: boolean }
    plans: BillingPlan[]
    /** Whether a FloPay subscription exists: the first checkout is offered only until it does. */
    subscribed?: boolean
    /** The product terms version a sign-up accepts, or null while none is configured. */
    termsVersion?: string | null
}

/** The business details on file, read back from FloPay's customer: the OS keeps no copy (CTO decision 4). */
export type BillingBusinessDetails = {
    legalName: string
    address: string | null
    country: string | null
    taxId: string | null
}

export type BillingInvoice = {
    id: string
    number: string
    date: string
    total: number
    status: string
    /** Where the OS hands the document over (`/v1/billing/invoices/<id>/document`), or null where there is none. */
    documentUrl: string | null
}

export type BillingPaymentMethod = {
    id: string
    brand: string
    last4: string
    expMonth: number
    expYear: number
    isDefault: boolean
}

/** One closed period's recorded usage of a unit against what was reported to FloPay (#448). */
export type BillingReconciliationRow = {
    planId: string
    periodStart: string
    periodEnd: string
    unit: string
    recorded: number
    included: number | null
    overage: number
    reported: number | null
    status: string
}

/**
 * Where a closed period's usage CSV is downloaded (#398): the period start's ISO string, as the reconciliation read
 * gives it, escaped so its colons cannot break the query. Followed as a link, like the invoice document.
 */
export function billingUsageExportUrl(periodStart: string): string {
    return `/v1/billing/exports/usage.csv?period=${encodeURIComponent(periodStart)}`
}

/** Where the invoices CSV is downloaded (#398): narrowed to a closed period by its start, or the whole history. */
export function billingInvoicesExportUrl(periodStart?: string | null): string {
    const path = '/v1/billing/exports/invoices.csv'
    return periodStart ? `${path}?period=${encodeURIComponent(periodStart)}` : path
}

/** A first checkout: the invoice details, the plan and interval, and the card FloPay's fields saved. */
export type BillingCheckoutBody = {
    planId: string
    interval: BillingInterval
    legalName: string
    address: string
    country: string
    taxId?: string
    paymentMethodId: string
}

/** The enterprise's storage now (#1621): bytes held, the same in GB to 2 places, and the GB its plan includes. */
export interface StorageUsage {
    heldBytes: number
    heldGb: number
    includedGb: number
}

/** One member mailbox's inbound messages since 00:00 UTC, against the plan's daily cap (null: no daily limit). */
export interface MailboxUsage {
    memberId: string
    memberName: string
    inboundToday: number
    dailyCap: number | null
}

/** The enterprise's mail against its plan (#1623): the plan's name and every member mailbox's count today. */
export interface MailUsage {
    planName: string
    /** Days the plan keeps a member's mail, received and sent alike, before it is deleted. */
    mailRetentionDays: number
    mailboxes: MailboxUsage[]
}

/**
 * The enterprise's concurrent run slots (#1622): the ceiling (the plan's figure plus the slots bought), the plan's
 * price per slot (null where the plan sells none), and a reduction FloPay applies at the period end.
 */
export interface ConcurrentSlots {
    planId: string
    planName: string
    planConcurrentRuns: number
    slots: number
    ceiling: number
    slotPriceUsd: number | null
    canBuy: boolean
    maxSlots: number
    /** `from` is the period end, as an ISO date string; null when the period is not known yet. */
    scheduled: { slots: number; from: string | null } | null
    /**
     * Slots asked to apply now that FloPay already bills but has not yet reported, when they differ from `slots`: the
     * ceiling moves to the plan's figure plus these once FloPay confirms. Null when nothing asked is awaiting FloPay.
     */
    requested: number | null
}

/** A downgrade FloPay applies at the period end (os#1616): the plan, from the period end as an ISO date string. */
export interface PendingPlanChange {
    planId: string
    from: string | null
}

/** One thing a lower plan cannot hold as the enterprise stands, and what happens to it when the plan moves. */
export interface PlanChangePreviewItem {
    unit: string
    used: number
    limit: number
    over: number
    outcome: 'keeps_running' | 'paused' | 'ends' | 'waits' | 'removed'
    /** The sentence Billing shows for it. */
    detail: string
}

/** Everything over a target plan's limits and what happens to each, from the period end the move takes effect. */
export interface PlanChangePreview {
    targetPlanId: string
    effectiveAt: string | null
    items: PlanChangePreviewItem[]
}

/**
 * Who stays active once a plan shrinks to Free (os#1624): the owner's pick as stored (the COO may be in it or not),
 * the members the plan holds paused now, and the members the plan includes, counting the COO.
 */
export interface KeptMembers {
    memberIds: string[]
    pausedMemberIds: string[]
    includedMembers: number
    /**
     * The member the plan always keeps active as the COO (the longest-serving `coo` holder), or null when nobody holds
     * it; omitted by an API older than it.
     */
    cooMemberId?: string | null
}

/** What a plan change did: asked of FloPay now (upgrade, withdraw), at the period end (downgrade), or nothing. */
export interface PlanChangeResult {
    accepted: boolean
    planId: string
    effective: 'now' | 'period_end' | 'unchanged'
}

/** What saving the slots did: asked of FloPay now, at the period end, or nothing because the quantity held. */
export interface ConcurrentSlotChange {
    accepted: boolean
    slots: number
    effective: 'now' | 'period_end' | 'unchanged'
}

export const api = {
    me: () => fetch('/v1/auth/me').then(async response => (response.ok ? ((await response.json()) as Me) : null)),
    logout: () => fetch('/v1/auth/logout', { method: 'POST' }),
    /** The ways in this deployment offers (#636), so the sign-in screen shows a button for each and no others. */
    signInWays: () => request<{ ways: SignInWay[] }>('/v1/auth/providers'),
    /** Emails a six-digit sign-in code to `email` (#636). Refused with the reason when it was not sent. */
    requestSignInCode: (email: string) =>
        request<{ sent: true }>('/v1/auth/email/code', { method: 'POST', body: JSON.stringify({ email }) }),
    /** Signs in with an emailed code (#636). The session cookie comes back with the answer. */
    verifySignInCode: (email: string, code: string) =>
        request<{ signedIn: true }>('/v1/auth/email/verify', { method: 'POST', body: JSON.stringify({ email, code }) }),
    /** The ways the signed-in person can sign in, and the ways this deployment offers to add (#636). */
    identities: () => request<{ identities: SignInIdentity[]; ways: SignInWay[] }>('/v1/auth/identities'),
    /** Starts adding a provider to the signed-in person's account (#636): the browser is sent to `url`. */
    linkProvider: (provider: Exclude<SignInWay, 'email'>) =>
        request<{ url: string }>(`/v1/auth/identities/${provider}`, { method: 'POST' }),
    /** Emails a code to an address the signed-in person is adding as a way in (#636). */
    requestLinkCode: (email: string) =>
        request<{ sent: true }>('/v1/auth/identities/email/code', { method: 'POST', body: JSON.stringify({ email }) }),
    /** Adds the address a correct code proves to the signed-in person's account (#636). */
    linkEmail: (email: string, code: string) =>
        request<void>('/v1/auth/identities/email', { method: 'POST', body: JSON.stringify({ email, code }) }),
    /**
     * Saves the signed-in person's Standard/Advanced mode on their account (#1949). The route answers 204, with no
     * body, exactly when it saved (`request` resolves to nothing only then), so any other answer, a 200 included, is
     * a save nobody confirmed and rejects as a refusal does.
     */
    saveUiMode: async (mode: UiMode): Promise<void> => {
        const answer = await request<unknown>('/v1/auth/me/ui-mode', { method: 'PUT', body: JSON.stringify({ mode }) })
        if (answer !== undefined) {
            throw new Error('ui_mode_not_saved')
        }
    },
    /** Saves the hours the signed-in person is emailed about Needs you in (#2006). A refusal rejects with its reason. */
    saveWorkingHours: (hours: WorkingHours) =>
        request<void>('/v1/auth/me/working-hours', { method: 'PUT', body: JSON.stringify(hours) }),
    /** Clears the signed-in person's own working hours: the enterprise's, or 8:00am to 8:00pm, apply (#2006). */
    clearWorkingHours: () => request<void>('/v1/auth/me/working-hours', { method: 'DELETE' }),
    /** Sign this session into another enterprise the person holds. The caller reloads afterwards. */
    selectEnterprise: (enterpriseId: string) =>
        request<void>('/v1/auth/enterprise', { method: 'POST', body: JSON.stringify({ enterpriseId }) }),
    /**
     * A further enterprise for the signed-in CEO; the session is signed into it on return.
     *
     * The company runs in the zone of whoever creates it (#884): this browser's zone goes with the name, so
     * a schedule that follows the enterprise and a skill that talks about local time both read it instead of
     * naming one. The CEO can change it afterwards.
     */
    createEnterprise: (body: { name: string; slug?: string }) =>
        request<{ id: string; slug: string; name: string; timezone: string }>('/v1/enterprises', {
            method: 'POST',
            body: JSON.stringify({ ...body, timezone: browserTimezone() }),
        }),
    apiTokens: (signal?: AbortSignal) => request<ApiTokenRow[]>('/v1/enterprise/api-tokens', { signal }),
    createApiToken: (body: { name: string; scopes: string[] }) =>
        request<IssuedApiToken>('/v1/enterprise/api-tokens', { method: 'POST', body: JSON.stringify(body) }),
    rotateApiToken: (id: string) =>
        request<IssuedApiToken>(`/v1/enterprise/api-tokens/${id}/rotate`, { method: 'POST' }),
    /** Change a live token's scopes in place: same secret, new reach. */
    updateApiTokenScopes: (id: string, scopes: string[]) =>
        request<ApiTokenRow>(`/v1/enterprise/api-tokens/${id}`, { method: 'PATCH', body: JSON.stringify({ scopes }) }),
    revokeApiToken: (id: string) => request<ApiTokenRow>(`/v1/enterprise/api-tokens/${id}`, { method: 'DELETE' }),
    /** Every external agent connected to this enterprise, revoked ones included, newest first (#333). */
    connections: (signal?: AbortSignal) => request<ConnectionRow[]>('/v1/enterprise/connections', { signal }),
    /** Cuts a connection off from its next request. The row stays, and stays listed, as revoked. */
    revokeConnection: (id: string) => request<ConnectionRow>(`/v1/enterprise/connections/${id}`, { method: 'DELETE' }),
    /** The enterprise's attached domains and what each still needs published (#422). Owner-only. */
    mailAliases: (memberId: string) => request<MailAliasesView>(`/v1/members/${memberId}/mail-aliases`),
    addMailAlias: (memberId: string, address: string, replyFromAlias: boolean) =>
        request<MailAliasesView>(`/v1/members/${memberId}/mail-aliases`, {
            method: 'POST',
            body: JSON.stringify({ address, replyFromAlias }),
        }),
    updateMailAlias: (memberId: string, aliasId: string, replyFromAlias: boolean) =>
        request<MailAliasesView>(`/v1/members/${memberId}/mail-aliases/${aliasId}`, {
            method: 'PATCH',
            body: JSON.stringify({ replyFromAlias }),
        }),
    removeMailAlias: (memberId: string, aliasId: string) =>
        request<MailAliasesView>(`/v1/members/${memberId}/mail-aliases/${aliasId}`, { method: 'DELETE' }),
    /** #1122: an address's own signature, which a member's mail from it carries. Owner-only. */
    saveMailAliasSignature: (memberId: string, aliasId: string, body: MailAliasSignatureSave) =>
        request<MailAliasesView>(`/v1/members/${memberId}/mail-aliases/${aliasId}/signature`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
    clearMailAliasSignature: (memberId: string, aliasId: string) =>
        request<MailAliasesView>(`/v1/members/${memberId}/mail-aliases/${aliasId}/signature`, { method: 'DELETE' }),
    /** A person's extra addresses (#886), the owner's control on People: the same shape a member's have. */
    personMailAliases: (userId: string) => request<MailAliasesView>(`/v1/users/${userId}/mail-aliases`),
    addPersonMailAlias: (userId: string, address: string, replyFromAlias: boolean) =>
        request<MailAliasesView>(`/v1/users/${userId}/mail-aliases`, {
            method: 'POST',
            body: JSON.stringify({ address, replyFromAlias }),
        }),
    updatePersonMailAlias: (userId: string, aliasId: string, replyFromAlias: boolean) =>
        request<MailAliasesView>(`/v1/users/${userId}/mail-aliases/${aliasId}`, {
            method: 'PATCH',
            body: JSON.stringify({ replyFromAlias }),
        }),
    removePersonMailAlias: (userId: string, aliasId: string) =>
        request<MailAliasesView>(`/v1/users/${userId}/mail-aliases/${aliasId}`, { method: 'DELETE' }),
    savePersonMailAliasSignature: (userId: string, aliasId: string, body: MailAliasSignatureSave) =>
        request<MailAliasesView>(`/v1/users/${userId}/mail-aliases/${aliasId}/signature`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
    clearPersonMailAliasSignature: (userId: string, aliasId: string) =>
        request<MailAliasesView>(`/v1/users/${userId}/mail-aliases/${aliasId}/signature`, { method: 'DELETE' }),
    /**
     * The signed-in person's own mailbox (#883, #887): the same rows and facets a member's mailbox lists, reached by the
     * person's session alone. Opening a mail marks it read, as it does for a member.
     */
    myMail: (query: ListQuery = {}) => request<MemberEmailList>(`/v1/mail${qs(query)}`),
    myMailMessage: (emailId: string) => request<MemberEmailDetail>(`/v1/mail/${emailId}`),
    myMailAttachmentUrl: (emailId: string, attachmentId: string) => `/v1/mail/${emailId}/attachments/${attachmentId}`,
    /** How many open mails the person has not opened yet: the Inbox badge in the nav. One row is read, for its total. */
    myMailUnreadCount: async (signal?: AbortSignal) =>
        (await request<MemberEmailList>(`/v1/mail${qs({ unreadOnly: 'true', open: 'true', limit: 1 })}`, { signal }))
            .total,
    /**
     * The personal inbox's own commands, each as the person: a reply and a forward leave in the call and file their
     * copy in the mail's thread; a label or handled state is recorded; a delete is for good. None is gated.
     */
    myMailReply: (emailId: string, body: MailReplyBody) =>
        request<MailSendOutcome>(`/v1/mail/${emailId}/reply`, { method: 'POST', body: JSON.stringify(body) }),
    myMailForward: (emailId: string, body: MailForwardBody) =>
        request<MailSendOutcome>(`/v1/mail/${emailId}/forward`, { method: 'POST', body: JSON.stringify(body) }),
    myMailSend: (body: MailSendBody) =>
        request<MailSendOutcome>('/v1/mail', { method: 'POST', body: JSON.stringify(body) }),
    myMailSetLabels: (emailId: string, labels: string[]) =>
        request<{ id: string; labels: string[] }>(`/v1/mail/${emailId}/labels`, {
            method: 'PATCH',
            body: JSON.stringify({ labels }),
        }),
    myMailSetHandled: (emailId: string, handledState: MemberEmailHandledState, snoozedUntil?: string) =>
        request<{ id: string; handledState: MemberEmailHandledState }>(`/v1/mail/${emailId}/handled`, {
            method: 'PATCH',
            body: JSON.stringify({ handledState, ...(snoozedUntil === undefined ? {} : { snoozedUntil }) }),
        }),
    myMailDelete: (emailId: string) => request<{ id: string }>(`/v1/mail/${emailId}`, { method: 'DELETE' }),
    /** Moves one of the person's own mails, and the rest of its conversation, to another mailbox in the enterprise. */
    myMailMove: (emailId: string, target: MailMoveTarget) =>
        request<MailMoveResult>(`/v1/mail/${emailId}/move`, { method: 'POST', body: JSON.stringify(target) }),
    /** Every mailbox in the enterprise a mail can be moved to: its people with an address, then its members. */
    mailboxDestinations: async () =>
        (await request<{ mailboxes: MailboxDestination[] }>('/v1/mail/mailboxes')).mailboxes,
    /**
     * The person's own contacts (#1114): who they wrote to, saved as each send left, and who they added. Theirs alone,
     * as their mail is. `directory` adds the enterprise's own people and members who have an address.
     */
    myMailContacts: async (query: MailContactQuery = {}) =>
        (await request<{ data: MailContact[] }>(`/v1/mail/contacts${qs(contactQuery(query))}`)).data,
    saveMyMailContact: (body: { address: string; name?: string }) =>
        request<MailContact>('/v1/mail/contacts', { method: 'POST', body: JSON.stringify(body) }),
    renameMyMailContact: (id: string, name: string) =>
        request<MailContact>(`/v1/mail/contacts/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify({ name }),
        }),
    removeMyMailContact: (id: string) =>
        request<{ id: string }>(`/v1/mail/contacts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    /** Imports a CSV or vCard file into the person's own contacts (#1120), merged by address. */
    importMyMailContacts: (file: MailAttachmentUpload) =>
        request<MailContactsImportResult>('/v1/mail/contacts/import', { method: 'POST', body: JSON.stringify(file) }),
    /** Where the person's own contacts download as a file (#1120), read with their session as an attachment is. */
    myMailContactsExportUrl: (format: MailContactsFileFormat) => `/v1/mail/contacts/export?format=${format}`,
    /** The person's own signature (#1115), which the OS adds to every mail they send. Theirs alone, as their mail is. */
    myMailSignature: () => request<MailSignature>('/v1/mail/signature'),
    saveMyMailSignature: (body: MailSignatureSave) =>
        request<MailSignature>('/v1/mail/signature', { method: 'PUT', body: JSON.stringify(body) }),
    clearMyMailSignature: () => request<MailSignature>('/v1/mail/signature', { method: 'DELETE' }),
    /**
     * The person's own extra addresses (#1122), each with its own signature, which they set themselves; an owner gives
     * and withdraws the addresses on People.
     */
    myMailAliases: () => request<{ aliases: MailAliasRow[] }>('/v1/mail/aliases'),
    saveMyMailAliasSignature: (aliasId: string, body: MailAliasSignatureSave) =>
        request<{ aliases: MailAliasRow[] }>(`/v1/mail/aliases/${encodeURIComponent(aliasId)}/signature`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
    clearMyMailAliasSignature: (aliasId: string) =>
        request<{ aliases: MailAliasRow[] }>(`/v1/mail/aliases/${encodeURIComponent(aliasId)}/signature`, {
            method: 'DELETE',
        }),
    /**
     * The one-click buttons the person's inbox offers: the tasks that declare a mailbox action, and the templates to
     * clone. Pressing one starts the task over the person's own mailbox, with the mail's id for a button on one mail.
     */
    myMailActions: async () => (await request<{ actions: MailboxActionOffer[] }>('/v1/mail/actions')).actions,
    startMyMailAction: (taskId: string, emailId?: string) =>
        request<RunRow>(`/v1/mail/actions/${encodeURIComponent(taskId)}/runs`, {
            method: 'POST',
            body: JSON.stringify(emailId === undefined ? {} : { emailId }),
        }),
    /** Presses one of the person's buttons on a cadence of their own, over their own mailbox; or stops that cadence. */
    scheduleMyMailAction: (taskId: string, body: { cron: string; timezone?: string; enabled?: boolean }) =>
        request<MailboxActionSchedule>(`/v1/mail/actions/${encodeURIComponent(taskId)}/schedule`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
    unscheduleMyMailAction: (taskId: string) =>
        request<{ cleared: boolean }>(`/v1/mail/actions/${encodeURIComponent(taskId)}/schedule`, { method: 'DELETE' }),
    /**
     * A person's own tokens (#890): mail-only credentials for a script or a client that cannot sign in. Theirs alone:
     * the enterprise's token page never lists them, and they stop when revoked here or when the person leaves.
     */
    myTokens: () => request<ApiTokenRow[]>('/v1/mail/tokens'),
    createMyToken: (body: { name: string; scopes: string[] }) =>
        request<IssuedApiToken>('/v1/mail/tokens', { method: 'POST', body: JSON.stringify(body) }),
    revokeMyToken: (id: string) => request<ApiTokenRow>(`/v1/mail/tokens/${id}`, { method: 'DELETE' }),

    enterpriseDomains: () => request<EnterpriseDomainRow[]>('/v1/enterprise/domains'),
    /** Attaches a domain, unverified, in the mail mode the owner chose. */
    attachDomain: (domain: string, mailMode: DomainMailMode) =>
        request<EnterpriseDomainRow>('/v1/enterprise/domains', {
            method: 'POST',
            body: JSON.stringify({ domain, mailMode }),
        }),
    /** Reads the published records now and returns what they prove. */
    verifyDomain: (domain: string) =>
        request<DomainVerification>(`/v1/enterprise/domains/${encodeURIComponent(domain)}/verify`, { method: 'POST' }),
    setDomainMailMode: (domain: string, mailMode: DomainMailMode) =>
        request<EnterpriseDomainRow>(`/v1/enterprise/domains/${encodeURIComponent(domain)}`, {
            method: 'PATCH',
            body: JSON.stringify({ mailMode }),
        }),
    detachDomain: (domain: string) =>
        request<void>(`/v1/enterprise/domains/${encodeURIComponent(domain)}`, { method: 'DELETE' }),
    /** The enterprise-wide concurrent-run limit, and this team's own override. */
    concurrency: () => request<ConcurrencySetting>('/v1/enterprise/concurrency'),
    chatModelTier: () => request<ChatModelTierSetting>('/v1/enterprise/chat-model-tier'),
    setChatModelTier: (chatModelTier: string) =>
        request<ChatModelTierSetting>('/v1/enterprise/chat-model-tier', {
            method: 'PATCH',
            body: JSON.stringify({ chatModelTier }),
        }),
    /** How much of the enterprise's memory a chat reply reads: the cap on one recall for one message. */
    chatMemoryDepth: () => request<ChatMemoryDepthSetting>('/v1/enterprise/chat-memory-depth'),
    setChatMemoryDepth: (chatMemoryDepth: string) =>
        request<ChatMemoryDepthSetting>('/v1/enterprise/chat-memory-depth', {
            method: 'PATCH',
            body: JSON.stringify({ chatMemoryDepth }),
        }),
    /** How many turns in a row a chat member may take on its own after work it started ends (#287). */
    chatUnpromptedTurnLimit: () => request<ChatUnpromptedTurnLimitSetting>('/v1/enterprise/chat-unprompted-turn-limit'),
    /** Change it: a whole number from 0 (off) to 20. Anything else is refused and changes nothing. */
    setChatUnpromptedTurnLimit: (limit: number) =>
        request<ChatUnpromptedTurnLimitSetting>('/v1/enterprise/chat-unprompted-turn-limit', {
            method: 'PATCH',
            body: JSON.stringify({ limit }),
        }),
    /** The company's own metrics, in the order they were written. */
    enterpriseMetrics: () => request<EnterpriseMetricsSetting>('/v1/enterprise/metrics'),
    setEnterpriseMetrics: (metrics: string[]) =>
        request<EnterpriseMetricsSetting>('/v1/enterprise/metrics', {
            method: 'PATCH',
            body: JSON.stringify({ metrics }),
        }),
    enterpriseKpis: () => request<EnterpriseKpisSetting>('/v1/enterprise/kpi-targets'),
    setEnterpriseKpis: (kpis: Kpi[]) =>
        request<KpiListSaved>('/v1/enterprise/kpi-targets', {
            method: 'PATCH',
            body: JSON.stringify({ kpis }),
        }),
    teamKpiTargets: (teamId: string) => request<EnterpriseKpisSetting>(`/v1/teams/${teamId}/kpi-targets`),
    setTeamKpiTargets: (teamId: string, kpis: Kpi[]) =>
        request<KpiListSaved>(`/v1/teams/${teamId}/kpi-targets`, {
            method: 'PATCH',
            body: JSON.stringify({ kpis }),
        }),
    /**
     * The outcomes a successor may wait on, `succeeded` first (#1754): the set the API's successor validator accepts,
     * which the task form's "When this finishes with" picker offers. The portal keeps no copy of it.
     */
    successorOutcomes: () =>
        request<{ outcomes: string[] }>('/v1/enterprise/successor-outcomes').then(body => body.outcomes),
    /** The zone the company runs in (#884). */
    enterpriseTimezone: () => request<EnterpriseTimezoneSetting>('/v1/enterprise/timezone'),
    /**
     * Move the company to another zone. Creation takes it from the creating CEO's browser; this is how the CEO
     * changes it afterwards, which moves the next fire of every schedule that follows the enterprise.
     */
    setEnterpriseTimezone: (timezone: string) =>
        request<EnterpriseTimezoneSetting>('/v1/enterprise/timezone', {
            method: 'PATCH',
            body: JSON.stringify({ timezone }),
        }),
    /** The enterprise's signature template (#1118), for an owner alone. */
    mailSignatureTemplate: () => request<MailSignatureTemplate>('/v1/enterprise/mail/signature-template'),
    /** Set it (`{ html }` or `{ text }`, with placeholders); one with nothing in it clears it. */
    saveMailSignatureTemplate: (body: { html?: string; text?: string }) =>
        request<MailSignatureTemplate>('/v1/enterprise/mail/signature-template', {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
    clearMailSignatureTemplate: () =>
        request<MailSignatureTemplate>('/v1/enterprise/mail/signature-template', { method: 'DELETE' }),
    /** An owner renames the enterprise. Answers with the name as stored (trimmed). */
    setEnterpriseName: (name: string) =>
        request<EnterpriseNameSetting>('/v1/enterprise/name', {
            method: 'PATCH',
            body: JSON.stringify({ name }),
        }),
    /** The repository that hosts the company's public changelog (#1915), `null` when it has none. */
    enterpriseChangelogRepo: () => request<EnterpriseChangelogRepoSetting>('/v1/enterprise/changelog-repo'),
    /** Name, change or clear it: an empty value or `null` means no public changelog. A refusal names `changelogRepo`. */
    setEnterpriseChangelogRepo: (changelogRepo: string | null) =>
        request<EnterpriseChangelogRepoSetting>('/v1/enterprise/changelog-repo', {
            method: 'PATCH',
            body: JSON.stringify({ changelogRepo }),
        }),
    /** Whether quiet source updates apply to the company's linked clones by themselves (#759); on by default. */
    enterpriseAutoUpdateClonedTasks: () =>
        request<EnterpriseAutoUpdateClonedTasksSetting>('/v1/enterprise/auto-update-cloned-tasks'),
    /** Turn it on or off. Answers with what is now stored; a refusal names `autoUpdateClonedTasks`. */
    setEnterpriseAutoUpdateClonedTasks: (autoUpdateClonedTasks: boolean) =>
        request<EnterpriseAutoUpdateClonedTasksSetting>('/v1/enterprise/auto-update-cloned-tasks', {
            method: 'PATCH',
            body: JSON.stringify({ autoUpdateClonedTasks }),
        }),
    /** The hours the Needs you email may go to owners with none of their own, in the enterprise timezone (#2006). */
    enterpriseWorkingHours: () => request<EnterpriseWorkingHours>('/v1/enterprise/working-hours'),
    setEnterpriseWorkingHours: (hours: { start: string; end: string }) =>
        request<EnterpriseWorkingHours>('/v1/enterprise/working-hours', {
            method: 'PUT',
            body: JSON.stringify(hours),
        }),
    clearEnterpriseWorkingHours: () =>
        request<EnterpriseWorkingHours>('/v1/enterprise/working-hours', { method: 'DELETE' }),
    /** Raise or lower the enterprise-wide concurrent-run limit. */
    setConcurrency: (concurrentRunLimit: number) =>
        request<ConcurrencySetting>('/v1/enterprise/concurrency', {
            method: 'PATCH',
            body: JSON.stringify({ concurrentRunLimit }),
        }),
    webhookSecret: () => request<WebhookSecretStatus>('/v1/enterprise/webhook-secret'),
    rotateWebhookSecret: () =>
        request<RotatedWebhookSecret>('/v1/enterprise/webhook-secret/rotate', { method: 'POST' }),
    webhooks: (query: ListQuery = {}, signal?: AbortSignal) =>
        request<ListResponse<WebhookSummary>>(`/v1/webhooks${qs(query)}`, { signal }),
    webhook: (id: string, signal?: AbortSignal) =>
        request<WebhookDetail>(`/v1/webhooks/${encodeURIComponent(id)}`, { signal }),
    replayWebhook: (id: string, idempotencyKey: string) =>
        request<WebhookDetail>(`/v1/webhooks/${encodeURIComponent(id)}/replay`, {
            method: 'POST',
            body: JSON.stringify({ idempotencyKey }),
        }),
    /** The whole org tree the Map draws, in one call. */
    orgMap: () => request<OrgMap>('/v1/orgs'),
    teams: () => request<Team[]>('/v1/teams'),
    createTeam: (body: {
        name: string
        slug?: string
        leadMemberId?: string | null
        charter?: TeamCharter
        concurrentRunLimit?: number | null
    }) => request<TeamDetail>('/v1/teams', { method: 'POST', body: JSON.stringify(body) }),
    /**
     * Drafts the seven charter answers from one sentence of purpose (FX8 of #1697): one POST beside create-team,
     * answered `{ charter }`. It creates and stores nothing; a refusal (no AI account, a bad sentence, a model
     * reply that was not all seven answers) rejects with the server's words, so no partial charter is ever returned.
     */
    draftTeamCharter: async (sentence: string): Promise<TeamCharter> =>
        (
            await request<{ charter: TeamCharter }>('/v1/teams/charter-draft', {
                method: 'POST',
                body: JSON.stringify({ sentence }),
            })
        ).charter,
    team: (id: string) => request<TeamDetail>(`/v1/teams/${id}`),
    patchTeam: (
        id: string,
        body: {
            /** Merged over the stored charter, so the goal alone (`purpose`) can be sent (#1844). */
            charter?: Partial<TeamCharter>
            name?: string
            leadMemberId?: string | null
            concurrentRunLimit?: number | null
        },
    ) => request<TeamDetail>(`/v1/teams/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    /** Switch a team off or on in one request (#1810), as `setSpendEnabled` does for spend. Answers the stored state. */
    setTeamEnabled: (id: string, enabled: boolean) =>
        request<TeamDetail>(`/v1/teams/${id}/enabled`, { method: 'PATCH', body: JSON.stringify({ enabled }) }),
    deleteTeam: (id: string) => request<void>(`/v1/teams/${id}`, { method: 'DELETE' }),
    addTeamMember: (teamId: string, memberId: string, roleId: string | null = null) =>
        request<TeamDetail>(`/v1/teams/${teamId}/members`, {
            method: 'POST',
            body: JSON.stringify({ memberId, roleId }),
        }),
    removeTeamMember: (teamId: string, memberId: string) =>
        request<TeamDetail>(`/v1/teams/${teamId}/members/${memberId}`, { method: 'DELETE' }),
    members: () => request<Member[]>('/v1/members'),
    createMember: (body: { name: string; email?: string; persona?: string }) =>
        request<Member>('/v1/members', { method: 'POST', body: JSON.stringify(body) }),
    chats: (memberId?: string) => request<ChatSessionRow[]>(`/v1/chats${memberId ? qs({ memberId }) : ''}`),
    createChat: (memberId: string) =>
        request<ChatSessionRow>('/v1/chats', { method: 'POST', body: JSON.stringify({ memberId }) }),
    /**
     * A new conversation for one job (#1203), with the COO and the greeting already in it, such as setting up the
     * company (#1738). Never listed with the everyday threads: it is reached by its own id. Creating a task (#244)
     * answers with your own unfinished conversation instead when it was active within a day; `fresh` is Start over,
     * which ends that one and answers with a new one.
     */
    openChatForPurpose: (purpose: string, options: { fresh?: boolean } = {}) =>
        request<ChatDetail>('/v1/chats', {
            method: 'POST',
            body: JSON.stringify(options.fresh ? { purpose, fresh: true } : { purpose }),
        }),
    /** Every reply you have not read, across all your chats: the Chat badge in the nav. */
    chatUnread: () => request<{ count: number }>('/v1/chats/unread'),
    chat: (id: string) => request<ChatDetail>(`/v1/chats/${id}`),
    /**
     * Your own conversation with a team's lead (#430), or with the COO acting as lead when the team has none: the
     * everyday thread the Chat page shows for that member, opened (or reused) from the Executions board. Send in it
     * with `postChatMessage`. Refused with the code `team_has_no_lead` when nobody can act as the team's lead.
     */
    openTeamLeadChat: (teamId: string) =>
        request<ChatDetail>(`/v1/teams/${encodeURIComponent(teamId)}/lead-chat`, { method: 'POST' }),
    /** Sends a message; with `fileIds`, carrying the files attached for it (`attachChatFiles`) (#1732). */
    postChatMessage: (id: string, content: string, fileIds: string[] = []) =>
        request<ChatDetail>(`/v1/chats/${id}/messages`, {
            method: 'POST',
            body: JSON.stringify(fileIds.length > 0 ? { content, fileIds } : { content }),
        }),
    /**
     * Takes a waiting message back before the member reads it (#284), resolving with the thread without it. Refused
     * with `chat_message_picked_up` once the member has read it, and `chat_not_found` for a message you may not
     * withdraw.
     */
    withdrawChatMessage: (chatId: string, messageId: string) =>
        request<ChatDetail>(`/v1/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(messageId)}`, {
            method: 'DELETE',
        }),
    /**
     * Attaches files to the message you are composing in a chat (#1732), resolving with them as stored, whose ids
     * the message is then sent with. Refused with the server's own plain sentence: a kind chat does not take, a file
     * over the limits, or too many files waiting for one message.
     */
    attachChatFiles: async (chatId: string, files: File[]) => {
        const body = {
            files: await Promise.all(
                files.map(async file => ({ filename: file.name, contentBase64: await fileBase64(file) })),
            ),
        }
        const attached = await request<{ files: ChatAttachedFile[] }>(`/v1/chats/${chatId}/files`, {
            method: 'POST',
            body: JSON.stringify(body),
        })
        return attached.files
    },
    /** Takes an unsent file off the message being composed (#2085): deleted on the server, so it is never sent. */
    removeChatFile: async (chatId: string, fileId: string): Promise<void> => {
        await request<{ ok: boolean }>(`/v1/chats/${encodeURIComponent(chatId)}/files/${encodeURIComponent(fileId)}`, {
            method: 'DELETE',
        })
    },
    /**
     * A file attached in a chat, as a Blob of its bytes (#1734): what the thread's chips preview, open and save. With
     * `download`, the server sends it as an attachment whatever its kind. Refused with `chat_not_found` for a file
     * that is gone or that you may not read.
     */
    chatFile: (chatId: string, fileId: string, download = false): Promise<Blob> =>
        requestBlob(
            `/v1/chats/${encodeURIComponent(chatId)}/files/${encodeURIComponent(fileId)}${download ? '?download=1' : ''}`,
        ),
    closeChat: (id: string) => request<ChatDetail>(`/v1/chats/${id}/close`, { method: 'POST' }),
    /** Stop the member mid-work (#281). Acts at once, with no confirmation; the thread read then shows who stopped it. */
    stopChat: (id: string) => request<ChatDetail>(`/v1/chats/${id}/stop`, { method: 'POST' }),
    decideChatToolCall: (sessionId: string, callId: string, decision: ChatToolCallDecision) =>
        request<ChatDetail>(`/v1/chats/${sessionId}/tool-calls/${encodeURIComponent(callId)}/decision`, {
            method: 'POST',
            body: JSON.stringify({ decision }),
        }),
    chatToolGrants: (memberId: string) => request<ChatToolGrantRow[]>(`/v1/chats/tool-grants${qs({ memberId })}`),
    revokeChatToolGrant: (id: string) => request<{ ok: true }>(`/v1/chats/tool-grants/${id}`, { method: 'DELETE' }),
    roles: () => request<Role[]>('/v1/roles'),
    createRole: (body: { name: string; slug?: string; teamId?: string | null }) =>
        request<RoleDetail>('/v1/roles', { method: 'POST', body: JSON.stringify(body) }),
    role: (id: string) => request<RoleDetail>(`/v1/roles/${id}`),
    addRoleMember: (roleId: string, memberId: string) =>
        request<RoleDetail>(`/v1/roles/${roleId}/members`, { method: 'POST', body: JSON.stringify({ memberId }) }),
    removeRoleMember: (roleId: string, memberId: string) =>
        request<RoleDetail>(`/v1/roles/${roleId}/members/${memberId}`, { method: 'DELETE' }),
    addRoleTask: (roleId: string, taskId: string) =>
        request<RoleDetail>(`/v1/roles/${roleId}/tasks`, { method: 'POST', body: JSON.stringify({ taskId }) }),
    removeRoleTask: (roleId: string, taskId: string) =>
        request<RoleDetail>(`/v1/roles/${roleId}/tasks/${taskId}`, { method: 'DELETE' }),
    member: (id: string) => request<Member>(`/v1/members/${id}`),
    /**
     * The tools a member's roles' tasks need that no binding of theirs reaches, one entry per vendor with the roles and
     * tasks that need it (#637). Computed on read: empty when nothing is missing.
     */
    memberToolGaps: (id: string) => request<MemberToolGap[]>(`/v1/members/${encodeURIComponent(id)}/tool-gaps`),
    mailboxOwner: (id: string) => request<MailboxOwnership>(`/v1/members/${id}/mailbox-owner`),
    setMailboxOwner: (id: string, body: { ownerId: string; transferExisting?: boolean }) =>
        request<MailboxOwnershipChange>(`/v1/members/${id}/mailbox-owner`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
    memberEmails: (id: string, query: ListQuery = {}) =>
        request<MemberEmailList>(`/v1/members/${id}/emails${qs(query)}`),
    memberEmail: (memberId: string, emailId: string) =>
        request<MemberEmailDetail>(`/v1/members/${memberId}/emails/${emailId}`),
    memberEmailAttachmentUrl: (memberId: string, emailId: string, attachmentId: string) =>
        `/v1/members/${memberId}/emails/${emailId}/attachments/${attachmentId}`,
    /**
     * An enterprise owner acting on a member's mailbox: what is sent leaves as the member, at once, as a member's run
     * sends once the owner approves its gate. Anybody else is refused by the API, whatever their grant.
     */
    memberEmailReply: (memberId: string, emailId: string, body: MailReplyBody) =>
        request<MailSendOutcome>(`/v1/members/${memberId}/emails/${emailId}/reply`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    memberEmailForward: (memberId: string, emailId: string, body: MailForwardBody) =>
        request<MailSendOutcome>(`/v1/members/${memberId}/emails/${emailId}/forward`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    memberEmailSend: (memberId: string, body: MailSendBody) =>
        request<MailSendOutcome>(`/v1/members/${memberId}/emails`, { method: 'POST', body: JSON.stringify(body) }),
    memberEmailSetLabels: (memberId: string, emailId: string, labels: string[]) =>
        request<{ id: string; labels: string[] }>(`/v1/members/${memberId}/emails/${emailId}/labels`, {
            method: 'PATCH',
            body: JSON.stringify({ labels }),
        }),
    memberEmailSetHandled: (
        memberId: string,
        emailId: string,
        handledState: MemberEmailHandledState,
        snoozedUntil?: string,
    ) =>
        request<{ id: string; handledState: MemberEmailHandledState }>(
            `/v1/members/${memberId}/emails/${emailId}/handled`,
            {
                method: 'PATCH',
                body: JSON.stringify({ handledState, ...(snoozedUntil === undefined ? {} : { snoozedUntil }) }),
            },
        ),
    memberEmailDelete: (memberId: string, emailId: string) =>
        request<{ id: string }>(`/v1/members/${memberId}/emails/${emailId}`, { method: 'DELETE' }),
    /** Moves one mail of a member's, and the rest of its conversation, to another mailbox. Owner only. */
    memberEmailMove: (memberId: string, emailId: string, target: MailMoveTarget) =>
        request<MailMoveResult>(`/v1/members/${memberId}/emails/${emailId}/move`, {
            method: 'POST',
            body: JSON.stringify(target),
        }),
    /** A member's contacts (#1114), for an enterprise owner alone, as acting on its mail is. */
    memberMailContacts: async (memberId: string, query: MailContactQuery = {}) =>
        (await request<{ data: MailContact[] }>(`/v1/members/${memberId}/mail/contacts${qs(contactQuery(query))}`))
            .data,
    saveMemberMailContact: (memberId: string, body: { address: string; name?: string }) =>
        request<MailContact>(`/v1/members/${memberId}/mail/contacts`, { method: 'POST', body: JSON.stringify(body) }),
    renameMemberMailContact: (memberId: string, id: string, name: string) =>
        request<MailContact>(`/v1/members/${memberId}/mail/contacts/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify({ name }),
        }),
    removeMemberMailContact: (memberId: string, id: string) =>
        request<{ id: string }>(`/v1/members/${memberId}/mail/contacts/${encodeURIComponent(id)}`, {
            method: 'DELETE',
        }),
    /** Imports a CSV or vCard file into a member's contacts (#1120), for an enterprise owner alone. */
    importMemberMailContacts: (memberId: string, file: MailAttachmentUpload) =>
        request<MailContactsImportResult>(`/v1/members/${memberId}/mail/contacts/import`, {
            method: 'POST',
            body: JSON.stringify(file),
        }),
    /** Where a member's contacts download as a file (#1120), for an enterprise owner alone. */
    memberMailContactsExportUrl: (memberId: string, format: MailContactsFileFormat) =>
        `/v1/members/${memberId}/mail/contacts/export?format=${format}`,
    /** A member's signature (#1115), set by an enterprise owner alone; the OS adds it to everything the member sends. */
    memberMailSignature: (memberId: string) => request<MailSignature>(`/v1/members/${memberId}/mail/signature`),
    saveMemberMailSignature: (memberId: string, body: MailSignatureSave) =>
        request<MailSignature>(`/v1/members/${memberId}/mail/signature`, { method: 'PUT', body: JSON.stringify(body) }),
    clearMemberMailSignature: (memberId: string) =>
        request<MailSignature>(`/v1/members/${memberId}/mail/signature`, { method: 'DELETE' }),
    /** The one-click buttons a member's mailbox offers: the tasks declaring a mailbox action that run as a role it holds. */
    memberMailboxActions: async (memberId: string) =>
        (await request<{ actions: MailboxActionOffer[] }>(`/v1/members/${memberId}/mailbox-actions`)).actions,
    /**
     * "Pick names for me" (#1697 O15): `count` (1–50) first names picked server-side for the signed-in enterprise, from
     * the region of its timezone. No model call, so it works before an AI account is connected.
     */
    suggestMemberNames: (count: number) =>
        request<{ names: string[] }>(`/v1/members/name-suggestions?count=${encodeURIComponent(String(count))}`),
    patchMember: (
        id: string,
        body: {
            name?: string
            email?: string
            persona?: string | null
            description?: string | null
            concurrencyCap?: number
        },
    ) => request<Member>(`/v1/members/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    setMemberAvatar: (id: string, dataUrl: string) =>
        request<Member>(`/v1/members/${id}/avatar`, { method: 'PUT', body: JSON.stringify({ dataUrl }) }),
    clearMemberAvatar: (id: string) => request<void>(`/v1/members/${id}/avatar`, { method: 'DELETE' }),
    /** The signed-in enterprise's picture; it is read back from `me.enterprise.avatarUrl` and the map. */
    setEnterpriseAvatar: (dataUrl: string) =>
        request<{ avatarUrl: string | null }>('/v1/enterprise/avatar', {
            method: 'PUT',
            body: JSON.stringify({ dataUrl }),
        }),
    clearEnterpriseAvatar: () => request<void>('/v1/enterprise/avatar', { method: 'DELETE' }),
    assignRole: (memberId: string, roleId: string) =>
        request<void>(`/v1/members/${memberId}/roles`, { method: 'POST', body: JSON.stringify({ roleId }) }),
    removeRole: (memberId: string, roleId: string) =>
        request<void>(`/v1/members/${memberId}/roles/${roleId}`, { method: 'DELETE' }),
    tasks: (query: ListQuery = {}) => request<ListResponse<TaskRow>>(`/v1/tasks${qs(query)}`),
    createTask: (body: Record<string, unknown>) =>
        request<TaskDetail>('/v1/tasks', { method: 'POST', body: JSON.stringify(body) }),
    task: (id: string) => request<TaskDetail>(`/v1/tasks/${id}`),
    /**
     * Switches a task on or off (#1316) through its own single-field PATCH, never a general task update. Resolves
     * with the value the server saved; a refusal rejects with the server's code, as every other call does.
     */
    setTaskRunStatus: (id: string, runStatus: TaskRunStatus) =>
        request<{ runStatus: TaskRunStatus }>(`/v1/tasks/${id}/run-status`, {
            method: 'PATCH',
            body: JSON.stringify({ runStatus }),
        }),
    copyTask: (id: string, body: TaskCopyInput) =>
        request<TaskRow>(`/v1/tasks/${id}/copy`, { method: 'POST', body: JSON.stringify(body) }),
    /** Pulls the source's current version into a locked clone as its next version. Only ever called by an explicit action. */
    /** `baseVersionId`: the clone's version the page read. The API refuses the update when the clone has moved since. */
    updateTaskFromSource: (id: string, baseVersionId?: string | null) =>
        request<TaskDetail>(`/v1/tasks/${id}/update-from-source`, {
            method: 'POST',
            ...(baseVersionId ? { body: JSON.stringify({ baseVersionId }) } : {}),
        }),
    deleteTask: (id: string) => request<void>(`/v1/tasks/${id}`, { method: 'DELETE' }),
    setTaskSchedule: (id: string, schedule: TaskSchedule) =>
        request<TaskRow>(`/v1/tasks/${id}/schedule`, { method: 'PUT', body: JSON.stringify(schedule) }),
    clearTaskSchedule: (id: string) => request<TaskRow>(`/v1/tasks/${id}/schedule`, { method: 'DELETE' }),
    setReleaseTrigger: (id: string, trigger: { enabled: boolean; input: Record<string, unknown> | null }) =>
        request<TaskRow>(`/v1/tasks/${id}/release-trigger`, { method: 'PUT', body: JSON.stringify(trigger) }),
    clearReleaseTrigger: (id: string) => request<TaskRow>(`/v1/tasks/${id}/release-trigger`, { method: 'DELETE' }),
    /** Replaces this enterprise's own values for a task's inputs (F2). Refused naming the field when one is wrong. */
    setTaskInputValues: (id: string, values: TaskInputValues) =>
        request<TaskRow>(`/v1/tasks/${id}/input-values`, { method: 'PUT', body: JSON.stringify({ values }) }),
    clearTaskInputValues: (id: string) => request<TaskRow>(`/v1/tasks/${id}/input-values`, { method: 'DELETE' }),
    startRun: (taskId: string, body: Record<string, unknown> = {}) =>
        request<RunRow>(`/v1/tasks/${taskId}/runs`, { method: 'POST', body: JSON.stringify(body) }),
    runs: (query: ListQuery = {}) => request<ListResponse<RunRow>>(`/v1/runs${qs(query)}`),
    executions: (query: ListQuery = {}, signal?: AbortSignal) =>
        request<ListResponse<ExecutionSummary>>(`/v1/executions${qs(query)}`, { signal }),
    executionBoard: (query: ListQuery = {}, signal?: AbortSignal) =>
        request<ExecutionBoard>(`/v1/executions/board${qs(query)}`, { signal }),
    executionCounts: (signal?: AbortSignal) => request<ExecutionCounts>('/v1/executions/counts', { signal }),
    /** What waits on the signed-in person in each enterprise they hold, for the picker. */
    enterpriseAttention: (signal?: AbortSignal) =>
        request<EnterpriseAttention[]>('/v1/enterprises/attention', { signal }),
    /**
     * What waits on the signed-in person in the enterprise this session is in (#678): the nav's Chat, Gates, Plans and
     * Self-Improvement badges, the same row the picker shows for that enterprise.
     */
    currentEnterpriseAttention: (signal?: AbortSignal) =>
        request<EnterpriseAttention>('/v1/enterprises/attention/current', { signal }),
    /** The enterprises the signed-in person holds whose Billing needs them, for the picker's flags. */
    enterpriseBillingAlerts: (signal?: AbortSignal) =>
        request<EnterpriseBillingAlert[]>('/v1/enterprises/billing-alerts', { signal }),
    execution: (id: string, signal?: AbortSignal) =>
        request<ExecutionDetail>(`/v1/executions/${encodeURIComponent(id)}`, { signal }),
    /** Stops the whole execution — every running, queued, waiting and not-yet-started run in it (#348). */
    cancelExecution: (id: string) =>
        request<ExecutionDetail>(`/v1/executions/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),
    /** Holds the whole execution: the step in progress finishes, and what has not started waits (#809). */
    pauseExecution: (id: string) =>
        request<ExecutionDetail>(`/v1/executions/${encodeURIComponent(id)}/pause`, { method: 'POST' }),
    /** Lets a paused execution go on: the runs the pause held go back to waiting their turn. */
    resumeExecution: (id: string) =>
        request<ExecutionDetail>(`/v1/executions/${encodeURIComponent(id)}/resume`, { method: 'POST' }),
    /** Takes a finished execution off the board; refused, with the reason, while it still needs someone (#557). */
    closeExecution: (id: string) =>
        request<ExecutionDetail>(`/v1/executions/${encodeURIComponent(id)}/close`, { method: 'POST' }),
    /** Puts a closed execution back on the board in its lane. */
    reopenExecution: (id: string) =>
        request<ExecutionDetail>(`/v1/executions/${encodeURIComponent(id)}/reopen`, { method: 'POST' }),
    /** Closes every execution in the board's Complete lane under these board filters — the true total. */
    closeCompletedExecutions: (query: ListQuery = {}) =>
        request<ExecutionCloseCompletedResult>(`/v1/executions/close-completed${qs(query)}`, { method: 'POST' }),
    /** One work item of a team's list (#1830), and its history, oldest first. */
    workItem: (id: string, signal?: AbortSignal) =>
        request<WorkItemRow>(`/v1/work-items/${encodeURIComponent(id)}`, { signal }),
    /**
     * One page of a team's work list (#429), each item with its parent and the rollup of its children. `state` is one
     * state, or `open` for every open one (what the list gives when none is asked for).
     */
    workItems: (
        teamId: string,
        query: { state?: string; kind?: string; page?: number; limit?: number } = {},
        signal?: AbortSignal,
    ) => request<WorkItemListPage>(`/v1/teams/${encodeURIComponent(teamId)}/work-items${qs(query)}`, { signal }),
    /** Every child of an item, open ones in list order then closed ones, each with its holder's status. */
    workItemChildren: (id: string, signal?: AbortSignal) =>
        request<WorkItemRow[]>(`/v1/work-items/${encodeURIComponent(id)}/children`, { signal }),
    workItemHistory: (id: string, signal?: AbortSignal) =>
        request<WorkItemHistoryEntry[]>(`/v1/work-items/${encodeURIComponent(id)}/history`, { signal }),
    /** A person's actions on a team's work list (#1831): add, change (approve, hold, release), close (decline, drop), move. */
    addWorkItem: (teamId: string, body: Record<string, unknown>) =>
        request<WorkItemRow>(`/v1/teams/${encodeURIComponent(teamId)}/work-items`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    updateWorkItem: (id: string, patch: Record<string, unknown>) =>
        request<WorkItemRow>(`/v1/work-items/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(patch),
        }),
    closeWorkItem: (id: string, body: { state: 'done' | 'dropped'; reason?: string }) =>
        request<WorkItemRow>(`/v1/work-items/${encodeURIComponent(id)}/close`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    moveWorkItem: (id: string, to: 'top' | 'up' | 'down' | 'bottom') =>
        request<WorkItemRow>(`/v1/work-items/${encodeURIComponent(id)}/move`, {
            method: 'POST',
            body: JSON.stringify({ to }),
        }),
    /** Approves the team's proposed items named by `ids` (the ones the board shows), and no other. */
    approveAllWorkItems: (teamId: string, ids: string[]) =>
        request<{ approved: number }>(`/v1/teams/${encodeURIComponent(teamId)}/work-items/approve-all`, {
            method: 'POST',
            body: JSON.stringify({ ids }),
        }),
    taskVersion: (taskId: string, versionId: string) =>
        request<TaskVersionDetail>(`/v1/tasks/${taskId}/versions/${versionId}`),
    /**
     * `afterEventId` asks for only the events recorded since that one (and any sharing its second), for a page
     * that already holds the rest; everything else in the detail is sent in full.
     */
    run: (id: string, query: { afterEventId?: string } = {}, signal?: AbortSignal) =>
        request<RunDetail>(`/v1/runs/${id}${qs(query)}`, { signal }),
    /**
     * `GET /plans/:id` (#221, #227): one plan with its items, run, gates and the decision open on it (#1984). There is
     * no write under `/plans`: the plan page decides a plan through `decideGate`, the gate card's own endpoint.
     */
    getPlan: (id: string) => request<PlanDetail>(`/v1/plans/${id}`),
    /**
     * `GET /plans` (#227): the plans a chat session or a run owns, so the chat card and the run page can
     * link at `/plans/:id` instead of only at the run that produced it.
     */
    plans: (query: { chatSessionId?: string; producedByRunId?: string; kind?: string; status?: string } = {}) =>
        request<PlanRow[]>(`/v1/plans${qs(query)}`),
    cancelRun: (id: string) => request<RunRow>(`/v1/runs/${id}/cancel`, { method: 'POST' }),
    /**
     * A person takes a running computer-use session (#678): the run's next browser action waits until it is handed
     * back. On a run waiting on a sign-in (os#1498) the browser is already the person's, and this changes nothing.
     */
    computerUsePause: (runId: string) =>
        request<void>(`/v1/runs/${encodeURIComponent(runId)}/computer-use/pause`, { method: 'POST' }),
    /** One browser action by the person who holds the session: the same verbs the run's model uses. */
    computerUseAct: (runId: string, action: ComputerUseAction) =>
        request<ComputerUseActionResult>(`/v1/runs/${encodeURIComponent(runId)}/computer-use/actions`, {
            method: 'POST',
            body: JSON.stringify(action),
        }),
    /**
     * Gives the session back. On a run waiting on a sign-in (os#1498) this completes it: what the sign-in left is
     * saved for the run's member on that site, and the run carries on from the signed-in page.
     */
    computerUseHandBack: (runId: string) =>
        request<void>(`/v1/runs/${encodeURIComponent(runId)}/computer-use/hand-back`, { method: 'POST' }),
    /** Retry a stopped run; `input` corrects the inputs its task declares, merged over the stored ones (#1132). */
    retryRun: (id: string, input?: Record<string, unknown>) =>
        request<RunRow>(`/v1/runs/${id}/retry`, {
            method: 'POST',
            ...(input ? { body: JSON.stringify({ input }) } : {}),
        }),
    blockers: (query: { failedPage?: number; pageSize?: number } = {}) => request<Blockers>(`/v1/blockers${qs(query)}`),
    clearRun: (id: string, workItemState: 'held' | 'ready' | 'done' | 'dropped' = 'held') =>
        request<RunRow>(`/v1/runs/${id}/clear`, { method: 'POST', body: JSON.stringify({ workItemState }) }),
    /**
     * Clear a Blockers section: every failed run, or (#431) every finding raised to a person, once dealt with. Given
     * `id` (the `raised` section only), just that one finding.
     */
    clearBlockers: (section: 'failed' | 'raised', id?: string) =>
        request<{ cleared: number }>('/v1/blockers/clear', {
            method: 'POST',
            body: JSON.stringify(id === undefined ? { section } : { section, id }),
        }),
    /** Retry every run in a Blockers section; a run that cannot be retried is named in `refused`. */
    retryBlockers: (section: 'blocked') =>
        request<{ retried: number; queued: number; runIds: string[]; refused: { runId: string; error: string }[] }>(
            '/v1/blockers/retry',
            { method: 'POST', body: JSON.stringify({ section }) },
        ),
    /** Cancel every run in a Blockers section; a run that cannot be cancelled is named in `refused`. */
    cancelBlockers: (section: 'blocked') =>
        request<{ cancelled: number; refused: { runId: string; error: string }[] }>('/v1/blockers/cancel', {
            method: 'POST',
            body: JSON.stringify({ section }),
        }),
    /** The Waiting list (#1457): open work nothing is working on, and how many rows that is. */
    waiting: (signal?: AbortSignal) => request<WaitingList>('/v1/waiting', { signal }),
    /** Marks a Waiting row's work finished, OS-side only: the issue tracker is not touched. 409 when it is not listed. */
    finishWaiting: (pipeline: string) =>
        request<{ finished: boolean }>('/v1/waiting/finish', { method: 'POST', body: JSON.stringify({ pipeline }) }),
    /** Restarts a Waiting row's work at the step `task` (a task slug). 409 when the work is not listed. */
    restartWaiting: (pipeline: string, task: string) =>
        request<RunRow>('/v1/waiting/restart', { method: 'POST', body: JSON.stringify({ pipeline, task }) }),
    proposals: () => request<TaskProposal[]>('/v1/proposals'),
    proposal: (id: string) => request<TaskProposal>(`/v1/proposals/${id}`),
    decideProposal: (id: string, decision: 'apply' | 'reject') =>
        request<TaskProposal>(`/v1/proposals/${id}/decision`, {
            method: 'POST',
            body: JSON.stringify({ decision }),
        }),
    recommendations: (taskId?: string) =>
        request<PendingRecommendation[]>(`/v1/recommendations${taskId ? qs({ taskId }) : ''}`),
    taskRecommendations: (taskId: string) => request<TaskRecommendation[]>(`/v1/tasks/${taskId}/recommendations`),
    recommendation: (id: string) => request<TaskRecommendation>(`/v1/recommendations/${id}`),
    decidedRecommendations: () => request<PendingRecommendation[]>(`/v1/recommendations${qs({ view: 'decided' })}`),
    /** Decline needs a reason: the recommendation is kept with it, so self-improvement does not raise it again. */
    decideRecommendation: (id: string, decision: 'approve' | 'decline', reason?: string) =>
        request<TaskRecommendation>(`/v1/recommendations/${id}/decision`, {
            method: 'POST',
            body: JSON.stringify({ decision, reason }),
        }),
    spend: () => request<SpendSnapshot>('/v1/spend'),
    /** The enterprise's storage held against the plan's included GB, for Billing's storage meter (`billing:read`). */
    storageUsage: () => request<StorageUsage>('/v1/billing/storage'),
    /**
     * Each member mailbox's inbound mail today against the plan's daily cap, for Billing's Mail panel (#1623,
     * `billing:read`).
     */
    mailUsage: () => request<MailUsage>('/v1/billing/mail'),
    /** The signed-in enterprise's four plan allowances, behind the plan-limit banners. */
    planAllowances: () => request<PlanAllowance[]>('/v1/auth/me/plan-allowances'),
    /** Whether the plan's last payment failed, and the plan's name: what the past-due banner is about. */
    paymentNotice: () => request<PaymentNoticeState>('/v1/auth/me/payment-notice'),
    /**
     * The concurrent run slots and the ceiling they make, for Billing's Concurrent runs panel and its meter (#1622,
     * `billing:read`).
     */
    concurrentSlots: () => request<ConcurrentSlots>('/v1/billing/slots'),
    /** Asks for `slots` concurrent run slots: more apply now, fewer at the period end. Takes `billing:write`. */
    setConcurrentSlots: (slots: number) =>
        request<ConcurrentSlotChange>('/v1/billing/slots', { method: 'POST', body: JSON.stringify({ slots }) }),
    setSpendEnabled: (enabled: boolean) =>
        request<SpendSnapshot>('/v1/spend/enabled', { method: 'PATCH', body: JSON.stringify({ enabled }) }),
    patchSpendCap: (body: {
        layer: string
        layerId: string
        moneyCentsDay?: number | null
        moneyCentsMonth?: number | null
        moneyCentsRun?: number | null
        runCountDay?: number | null
        maxModelTier?: string | null
    }) => request<{ raised: boolean }>('/v1/spend/caps', { method: 'PATCH', body: JSON.stringify(body) }),
    // The Billing page (os#1615): reads under billing:read, writes under billing:write, and never a card number.
    billingOverview: () => request<BillingOverview>('/v1/billing/overview'),
    billingInvoices: () => request<BillingInvoice[]>('/v1/billing/invoices'),
    billingPaymentMethods: () => request<BillingPaymentMethod[]>('/v1/billing/payment-methods'),
    billingReconciliation: () => request<BillingReconciliationRow[]>('/v1/billing/reconciliation'),
    billingBusinessDetails: () => request<{ details: BillingBusinessDetails | null }>('/v1/billing/business-details'),
    billingFieldSession: () =>
        request<{ sessionToken: string }>('/v1/billing/payment-methods/field-session', { method: 'POST' }),
    /** The downgrade pending for the period end, or null (os#1616). */
    billingPlanChange: () => request<{ pending: PendingPlanChange | null }>('/v1/billing/plan-change'),
    /** What moving to a lower plan would shrink, and what happens to each. */
    billingPlanChangePreview: (planId: string) =>
        request<PlanChangePreview>(`/v1/billing/plan-change/preview${qs({ planId })}`),
    /** Up the ladder applies now; down at the period end; the plan on now withdraws a pending downgrade. */
    billingChangePlan: (planId: string) =>
        request<PlanChangeResult>('/v1/billing/plan-change', { method: 'POST', body: JSON.stringify({ planId }) }),
    /** Cancelling a paid plan: a downgrade to Free at the period end, with no refund. */
    billingCancelPlan: () => request<PlanChangeResult>('/v1/billing/plan-change/cancel', { method: 'POST' }),
    /** Who stays active on a plan that shrank, and who it holds paused (os#1624). */
    billingKeptMembers: () => request<KeptMembers>('/v1/billing/plan-change/kept-members'),
    /** Sets who stays active; the COO always does, named here or not. */
    billingKeepMembers: (memberIds: string[]) =>
        request<{ memberIds: string[] }>('/v1/billing/plan-change/kept-members', {
            method: 'PUT',
            body: JSON.stringify({ memberIds }),
        }),
    billingMakeDefault: (id: string) =>
        request<void>(`/v1/billing/payment-methods/${encodeURIComponent(id)}/default`, { method: 'POST' }),
    billingRemovePaymentMethod: (id: string) =>
        request<void>(`/v1/billing/payment-methods/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    billingCheckout: (body: BillingCheckoutBody) =>
        request<{ customerId: string; subscriptionId: string }>('/v1/billing/checkout', {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    agentConnections: () => request<AgentConnectionsResponse>('/v1/llm/connections'),
    agentConnectionUsage: (id: string) => request<AgentUsage>(`/v1/llm/connections/${encodeURIComponent(id)}/usage`),
    /**
     * Usage for several connections in one request, keyed by connection id (OS-PORTAL-2). At most
     * `AGENT_CONNECTION_USAGE_IDS_MAX` ids: a caller with more splits them. An id the enterprise does not hold is left
     * out of the answer.
     */
    agentConnectionsUsage: (ids: string[]) =>
        request<AgentUsageByConnection>(`/v1/llm/connections/usage?ids=${ids.map(encodeURIComponent).join(',')}`),
    labelAgentConnection: (
        id: string,
        body: {
            name?: string | null
            description?: string | null
            /** A seat plan; `monthlyUsdCents` left out takes the plan's list price. Null goes back to detection. */
            subscription?: { plan: AgentSubscriptionPlan; monthlyUsdCents?: number } | null
            priority?: number
        },
    ) =>
        request<AgentConnection>(`/v1/llm/connections/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
        }),
    reorderAgentConnections: (connectionIds: string[]) =>
        request<AgentConnection[]>('/v1/llm/connections/order', {
            method: 'PATCH',
            body: JSON.stringify({ connectionIds }),
        }),
    /** Whether the enterprise has an AI account its work can run on right now (#1716), read live each time. */
    aiAccount: () => request<{ connected: boolean }>('/v1/llm/ai-account'),
    /** Proves a key with one real provider call and stores it only when accepted (#1716). Never throws on a refusal. */
    connectAiAccount: (account: { provider: AiAccountProvider; apiKey: string }) => connectAiAccount(account),
    /** Records that this tab reached the AI account step (#1718). Never throws. */
    reachAiAccountStep: () => reachAiAccountStep(onboardingSession()),
    /** The founder leaving the AI account step says why, from its closed choice (#1770). Never throws. */
    leaveAiAccountStep: (reason: AiAccountExitReason) => leaveAiAccountStep(reason),
    /**
     * The setup state as the AI account step's own screen reads it (#1770): the server records the step shown to the
     * person reading, once for them. The screen is shared by both paths, so it names neither, and the read never logs
     * a resume.
     */
    onboardingAtAiAccountStep: () => request<OnboardingStateView>('/v1/enterprise/onboarding/ai-account'),
    /**
     * The enterprise's setup state (#1737), read from one path and session: a tab, or a chat conversation's id.
     * `quiet` (#1796) only looks: the read logs no resume and records no step shown, for the page load's one-time
     * landing, which reads `aiAccountStepReached` without being a visit to setup.
     */
    onboarding: (path: OnboardingPath, session: string, options: { quiet?: boolean } = {}) =>
        request<OnboardingStateView>(
            `/v1/enterprise/onboarding${qs({ path, session, quiet: options.quiet ? '1' : undefined })}`,
        ),
    /**
     * Saves one setup step's answers from a path and session (#1737). `work_profile` (#1738) takes the fixed
     * structure, kind of work first; the server refuses anything else with its own plain sentence.
     */
    saveOnboardingStep: (step: string, answers: Record<string, unknown>, path: OnboardingPath, session: string) =>
        request<OnboardingStateView>(`/v1/enterprise/onboarding/steps/${encodeURIComponent(step)}`, {
            method: 'PUT',
            body: JSON.stringify({ path, session, answers }),
        }),
    /**
     * Moves setup to `path` from a session (#1742): “Prefer step-by-step?” and “Set up with your COO instead”. Every
     * answer and the current step stay; the server records a real switch so the switch rate can be read.
     */
    switchOnboardingPath: (path: OnboardingPath, session: string) =>
        request<OnboardingStateView>('/v1/enterprise/onboarding/path', {
            method: 'PUT',
            body: JSON.stringify({ path, session }),
        }),
    /**
     * Skips setup for the dashboard (#1742), from the path and session the link was on. Every answer is kept; the
     * server refuses it before the AI account step is complete.
     */
    skipOnboarding: (path: OnboardingPath, session: string) =>
        request<OnboardingStateView>('/v1/enterprise/onboarding/skip', {
            method: 'PUT',
            body: JSON.stringify({ path, session }),
        }),
    /**
     * The assembly's events after `after` (#1743), or all of them with none: only what the server recorded once each
     * step had committed. Reading it tells the server a tab is still following the log.
     */
    assemblyEvents: (id: string, after?: number) =>
        request<AssemblyView>(`/v1/enterprise/onboarding/assembly/${encodeURIComponent(id)}/events${qs({ after })}`),
    /** Sets up one teammate of the assembly again, after its automatic retries ran out (#1743). */
    assemblyRetry: (id: string, teammate: string) =>
        request<AssemblyView>(`/v1/enterprise/onboarding/assembly/${encodeURIComponent(id)}/retry`, {
            method: 'POST',
            body: JSON.stringify({ teammate }),
        }),
    llmStatus: (filter?: { layer?: 'enterprise' | 'team' | 'member'; layerId?: string }) =>
        request<{
            bound: boolean
            providers?: {
                openrouter: boolean
                claude: boolean
                claudeAuth?: 'oauth' | 'api_key' | null
                openai?: boolean
                openaiAuth?: 'oauth' | 'api_key' | null
                grok?: boolean
                grokAuth?: 'oauth' | 'api_key' | null
            }
        }>(`/v1/llm/bindings${qs(filter)}`),
    bindLlm: (
        apiKey: string,
        provider: AgentProvider = 'openrouter',
        layer: 'enterprise' | 'team' | 'member' = 'enterprise',
        layerId?: string,
        options?: { connectionId?: string; create?: boolean },
    ) =>
        request<{ ok: boolean; connectionId: string }>('/v1/llm/bindings', {
            method: 'POST',
            body: JSON.stringify({ apiKey, provider, layer, layerId, ...options }),
        }),
    unbindLlm: (
        provider: AgentProvider,
        layer: 'enterprise' | 'team' | 'member' = 'enterprise',
        layerId?: string,
        connectionId?: string,
    ) => request<void>(`/v1/llm/bindings${qs({ provider, layer, layerId, connectionId })}`, { method: 'DELETE' }),
    startClaudeOAuth: (
        layer: 'enterprise' | 'team' | 'member' = 'enterprise',
        layerId?: string,
        options?: { connectionId?: string; create?: boolean },
    ) =>
        request<{ authorizationUrl: string; state: string }>('/v1/llm/claude/oauth/start', {
            method: 'POST',
            body: JSON.stringify({ layer, layerId, ...options }),
        }),
    completeClaudeOAuth: (code: string, state: string) =>
        request<{ ok: boolean; connectionId: string }>('/v1/llm/claude/oauth/complete', {
            method: 'POST',
            body: JSON.stringify({ code, state }),
        }),
    /**
     * Starts a subscription sign-in for any provider that has one (a Claude, ChatGPT or SuperGrok plan): the
     * provider's page to open, and the state the pasted answer is completed with.
     */
    startSubscriptionOAuth: (
        provider: Exclude<AgentProvider, 'openrouter'>,
        layer: 'enterprise' | 'team' | 'member' = 'enterprise',
        layerId?: string,
        options?: { connectionId?: string; create?: boolean },
    ) =>
        request<{ authorizationUrl: string; state: string }>(`/v1/llm/${provider}/oauth/start`, {
            method: 'POST',
            body: JSON.stringify({ layer, layerId, ...options }),
        }),
    completeSubscriptionOAuth: (provider: Exclude<AgentProvider, 'openrouter'>, code: string, state: string) =>
        request<{ ok: boolean; connectionId: string }>(`/v1/llm/${provider}/oauth/complete`, {
            method: 'POST',
            body: JSON.stringify({ code, state }),
        }),
    /** `GET /v1/llm/tiers` (#664 item 1): tier -> model per connected provider, for the picker to show. */
    llmTiers: () => request<LlmTierEntry[]>('/v1/llm/tiers'),
    /** `GET /v1/llm/models`: the provider directory a task can hand-pick from, and the model a task uses when it does not. */
    llmModels: () => request<LlmDirectory>('/v1/llm/models'),
    /** `POST /v1/llm/tiers/override` (#664 item 2): a per-tenant tier -> model override for one provider. */
    setLlmTierOverride: (body: { provider: AgentProvider; tier: string; model: string }) =>
        request<{ ok: boolean }>('/v1/llm/tiers/override', { method: 'POST', body: JSON.stringify(body) }),
    waitingGates: () => request<GateRow[]>('/v1/gates?status=waiting'),
    /** Needs you's plan-limit items beside the waiting gates (#2122): one per reached limit, not one per paused run. */
    planLimitItems: () => request<PlanLimitNeedsYouItem[]>('/v1/gates/plan-limits'),
    /**
     * Needs you's missing-binding notices beside the waiting gates (#637): one per member and vendor whose runs are
     * parked on `tool_not_bound`, naming the member and the tools, with a link to that member's Tools for the vendor.
     */
    toolNotBoundItems: () => request<ToolNotBoundNeedsYouItem[]>('/v1/gates/tool-not-bound'),
    /**
     * Needs you's KPIs waiting for sign-off: one item per owner (the company, a team, a role or a task) while any of its
     * KPIs waits, with the API's sentence and where to sign them off. They count in the Gates badge.
     */
    kpiSignOffItems: () => request<KpiSignOffNeedsYouItem[]>('/v1/gates/kpi-sign-offs'),
    /** Needs you's proposed work (#849): one decision per run that proposed work items still waiting on a person. */
    proposedWork: () => request<ProposedWorkBatch[]>('/v1/gates/proposed-work'),
    /**
     * A person's answer to a run's proposed work (#849): `choice` for the items `ids` names, or for every item still in
     * the batch when `ids` is left out (Approve all). Answers the batch as it now stands, null once it has closed.
     */
    decideProposedWork: (runId: string, choice: ProposedWorkChoice, ids?: string[]) =>
        request<{ decided: number; batch: ProposedWorkBatch | null }>(
            `/v1/gates/proposed-work/${encodeURIComponent(runId)}/decision`,
            { method: 'POST', body: JSON.stringify(ids ? { choice, ids } : { choice }) },
        ),
    decideGate: async (
        id: string,
        decision: GateDecision,
        note?: string,
        answers?: CeoQuestionAnswer[],
        /** The payload as the CEO edited it, sent only when they changed an editable field (os#911). */
        edit?: Record<string, unknown>,
        /** The key of the choice the decision was made through, on a gate that offers choices. */
        choice?: string,
    ) => {
        const gate = await request<GateRow>(`/v1/gates/${id}/decision`, {
            method: 'POST',
            body: JSON.stringify(decisionBody(decision, note, answers, undefined, edit, choice)),
        })
        clearGateDecisionDraft(id)
        return gate
    },
    /**
     * Answering a CEO question in the thread it was asked in (#229). Same decision, same single writer as
     * `decideGate` — the API resolves the gate from the thread that asked it, with `gateId` naming which of
     * that thread's questions is being answered — so chat and the gates page can never answer it twice.
     */
    decideChatGate: async (
        sessionId: string,
        gateId: string,
        decision: GateDecision,
        note?: string,
        answers?: CeoQuestionAnswer[],
        choice?: string,
    ) => {
        const gate = await request<GateRow>(`/v1/gates/chat/${sessionId}/decision`, {
            method: 'POST',
            body: JSON.stringify(decisionBody(decision, note, answers, gateId, undefined, choice)),
        })
        clearGateDecisionDraft(gateId)
        return gate
    },
    toolCatalog: (id: string) => request<Array<{ name: string; summary: string }>>(`/v1/tools/bindings/${id}/catalog`),
    builtInTaskTools: () =>
        request<Array<{ name: string; summary: string; effect: string; gate?: string; requires?: string[] }>>(
            '/v1/tools/built-in?callable=run',
        ),
    toolBindings: (filter?: { layer?: ToolLayer; layerId?: string }) =>
        request<ToolBindingRow[]>(`/v1/tools/bindings${qs(filter)}`),
    bindTool: (body: {
        toolType: string
        layer: ToolLayer
        layerId: string
        /** Required for `kind: 'mcp'`; a Computer-Use binding has no token to send. */
        token?: string
        mcpUrl?: string
        imageUrl?: string
        displayName?: string
        kind?: BindingKind
        sites?: string[]
        startUrl?: string
        /** A `web-credential` binding's exact hosts (#1406). */
        hosts?: string[]
        /** The request header a `web-credential` binding's secret is sent in (#1406). */
        header?: string
        /** How a `web-credential` binding's secret is sent (#1406). */
        scheme?: CredentialScheme
        /** Left out, whatever the binding already has stays. */
        props?: Record<string, string>
        /** The tool slots it fills. Left out, whatever the binding already fills stays. */
        slots?: string[]
        /** Its action map. Left out, a new binding starts from its directory entry's and a rebind keeps its own. */
        slotActions?: Record<string, string>
    }) => request<ToolBindingRow>('/v1/tools/bindings', { method: 'POST', body: JSON.stringify(body) }),
    patchTool: (
        id: string,
        body: {
            token?: string
            authorizeParams?: Record<string, string>
            mcpUrl?: string
            imageUrl?: string
            displayName?: string
            layer?: ToolLayer
            layerId?: string
            kind?: BindingKind
            sites?: string[]
            startUrl?: string
            /** A `web-credential` binding's hosts, whole (#1406). */
            hosts?: string[]
            header?: string
            scheme?: CredentialScheme
            /** The whole map: `{}` clears it. */
            props?: Record<string, string>
            /** The tool slots it fills, whole: `[]` clears them. */
            slots?: string[]
            /** Its action map, whole (#957): `''` for an action drops it, `{}` clears the map. */
            slotActions?: Record<string, string>
        },
    ) => request<ToolBindingRow>(`/v1/tools/bindings/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    unbindTool: (id: string) => request<void>(`/v1/tools/bindings/${id}`, { method: 'DELETE' }),
    /**
     * The saved computer-use sign-ins a Computer-Use binding has to revoke (#676) — who signed in, where, and
     * when, never the session itself. Empty for a binding nobody has signed in through yet.
     */
    savedSignIns: (bindingId: string) => request<SavedSignInRow[]>(`/v1/tools/bindings/${bindingId}/sign-ins`),
    /**
     * Forgets a member's saved sign-in across the sites this binding allows (#676), so their following run on
     * those sites meets the login wall again. Same access as editing or deleting the binding.
     */
    revokeSavedSignIn: (bindingId: string, memberId: string, site?: string) =>
        request<void>(`/v1/tools/bindings/${bindingId}/sign-ins/${memberId}${qs({ site })}`, { method: 'DELETE' }),
    oauthRedirectUri: () => request<{ redirectUri: string }>('/v1/tools/oauth/redirect-uri'),
    mcpOAuthPermissions: (mcpUrl: string) =>
        request<{ scopes: string[] }>('/v1/tools/oauth/permissions', {
            method: 'POST',
            body: JSON.stringify({ mcpUrl }),
        }),
    startMcpOAuth: (body: {
        toolType: string
        layer: ToolLayer
        layerId: string
        mcpUrl: string
        displayName?: string
        clientId?: string
        clientSecret?: string
        scopes?: string
        authorizeParams?: Record<string, string>
    }) =>
        request<{ authorizationUrl: string }>('/v1/tools/oauth/start', { method: 'POST', body: JSON.stringify(body) }),
    /** The Express tools on offer: systems set up by the platform, connected by signing in. */
    expressTools: () => request<ExpressTool[]>('/v1/tools/express'),
    /**
     * Starts an Express tool's sign-in at a layer and answers the address to send the browser to. `returnTo` (#1942)
     * brings the sign-in back to an allow-listed portal path, such as the onboarding tools step, instead of /tools.
     */
    connectExpressTool: (slug: string, body: { layer: ToolLayer; layerId: string; returnTo?: string }) =>
        request<{ authorizationUrl: string }>(`/v1/tools/express/${encodeURIComponent(slug)}/connect`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    /** The Staff area's Express tools. Only a platform admin reaches these four. */
    staffExpressTools: () => request<StaffExpressTool[]>('/v1/staff/express-tools'),
    createStaffExpressTool: (body: StaffExpressToolInput) =>
        request<StaffExpressTool>('/v1/staff/express-tools', { method: 'POST', body: JSON.stringify(body) }),
    updateStaffExpressTool: (id: string, body: StaffExpressToolInput) =>
        request<StaffExpressTool>(`/v1/staff/express-tools/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteStaffExpressTool: (id: string) => request<void>(`/v1/staff/express-tools/${id}`, { method: 'DELETE' }),
    /** The whole list, as a file to keep, edit and import again. */
    exportStaffExpressTools: () => request<{ tools: ExpressToolEntry[] }>('/v1/staff/express-tools/export'),
    /**
     * Brings part of a list in. A dry run writes nothing and says what would change. `publish` makes the new tools
     * that need no sign-in app live at once.
     */
    importStaffExpressTools: (body: { tools: ExpressToolEntry[]; dryRun?: boolean; publish?: boolean }) =>
        request<ExpressImportResult>('/v1/staff/express-tools/import', { method: 'POST', body: JSON.stringify(body) }),
    /** The permissions an MCP server advertises, for the Staff form: the same lookup a connection form makes. */
    staffExpressToolPermissions: (mcpUrl: string) =>
        request<{ scopes: string[] }>('/v1/staff/express-tools/permissions', {
            method: 'POST',
            body: JSON.stringify({ mcpUrl }),
        }),
    /** The Staff area's enterprises (#394). Only a platform admin reaches these; the API refuses everybody else. */
    staffEnterprises: () => request<StaffEnterpriseRow[]>('/v1/staff/enterprises'),
    staffEnterprise: (id: string) => request<StaffEnterpriseDetail>(`/v1/staff/enterprises/${encodeURIComponent(id)}`),
    staffReconciliation: () => request<StaffReconciliationRow[]>('/v1/staff/reconciliation'),
    /** The Staff Performance page for a window, with one enterprise broken down (the busiest when none is named). */
    staffPerformance: (window: '24h' | '7d', enterpriseId?: string | null) => {
        const query = new URLSearchParams({ window })
        if (enterpriseId) {
            query.set('enterpriseId', enterpriseId)
        }
        return request<StaffPerformanceReport>(`/v1/staff/performance?${query.toString()}`)
    },
    staffSetPlan: (id: string, body: StaffPlanInput) =>
        request<void>(`/v1/staff/enterprises/${encodeURIComponent(id)}/plan`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    staffExtendEnterprise: (id: string, body: { endsAt: string; reason?: string }) =>
        request<void>(`/v1/staff/enterprises/${encodeURIComponent(id)}/extend`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    staffPauseEnterprise: (id: string, body: { reason?: string }) =>
        request<void>(`/v1/staff/enterprises/${encodeURIComponent(id)}/pause`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    staffUnpauseEnterprise: (id: string) =>
        request<void>(`/v1/staff/enterprises/${encodeURIComponent(id)}/unpause`, { method: 'POST', body: '{}' }),
    staffCloseEnterprise: (id: string, body: { confirmName: string }) =>
        request<void>(`/v1/staff/enterprises/${encodeURIComponent(id)}/close`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    /** Puts an existing FloPay coupon on the enterprise's subscription (#399). The reason stays in the staff record. */
    staffApplyCoupon: (id: string, body: { couponId: string; reason: string }) =>
        request<void>(`/v1/staff/enterprises/${encodeURIComponent(id)}/coupons`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    staffRemoveCoupon: (id: string, couponId: string, body: { reason: string }) =>
        request<void>(
            `/v1/staff/enterprises/${encodeURIComponent(id)}/coupons/${encodeURIComponent(couponId)}/remove`,
            { method: 'POST', body: JSON.stringify(body) },
        ),
    /**
     * A one-off credit in cents of the subscription's currency, which FloPay takes off the next invoice (#399).
     * `requestId` is a UUID made once per Add credit dialog: a resend with it credits once.
     */
    staffApplyCredit: (id: string, body: { amountCents: number; reason: string; requestId?: string }) =>
        request<void>(`/v1/staff/enterprises/${encodeURIComponent(id)}/credits`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    staffRemoveCredit: (id: string, creditId: string, body: { reason: string }) =>
        request<void>(
            `/v1/staff/enterprises/${encodeURIComponent(id)}/credits/${encodeURIComponent(creditId)}/remove`,
            { method: 'POST', body: JSON.stringify(body) },
        ),
    /** Reopens a closed enterprise during its retention, on the lifecycle's own route. */
    staffCancelCloseEnterprise: (id: string) =>
        request<{ closed: false }>(`/v1/enterprises/${encodeURIComponent(id)}/cancel-close`, { method: 'POST' }),
    /** Server-paginated: the same page/limit/search/sort/order contract as `tasks`. */
    catalogRoles: (query: ListQuery = {}) => request<ListResponse<CatalogRole>>(`/v1/catalog/roles${qs(query)}`),
    catalogTeams: (query: ListQuery = {}) => request<ListResponse<CatalogTeam>>(`/v1/catalog/teams${qs(query)}`),
    cloneCatalogRole: (id: string, body: CatalogRoleCloneInput) =>
        request<{
            role: Role
            tasks: TaskRow[]
            /** The tasks the company already had, reused as they are rather than copied. Absent on an older API. */
            reusedTaskIds?: string[]
            member: { id: string } | null
        }>(`/v1/catalog/roles/${encodeURIComponent(id)}/clone`, { method: 'POST', body: JSON.stringify(body) }),
    /** Clones a team template. `kpis` is every KPI the clone created, none signed off yet (#1839). */
    cloneCatalogTeam: (id: string, body: CatalogTeamCloneInput) =>
        request<{
            team: Team
            roles: Role[]
            /** The roles the company already held, reused as they are. Absent on an older API. */
            reusedRoleIds?: string[]
            tasks: TaskRow[]
            /** The tasks the company already had, reused as they are rather than copied. Absent on an older API. */
            reusedTaskIds?: string[]
            members: { id: string }[]
            kpis?: CatalogClonedKpi[]
        }>(`/v1/catalog/teams/${encodeURIComponent(id)}/clone`, { method: 'POST', body: JSON.stringify(body) }),
    /**
     * Signs off every KPI a team holds (its own, its roles' and its tasks') in one request (#1839), after applying
     * `edits` to the team's and roles' targets and AMBER bands. A person's own session only: a token is refused.
     * Answers the ids it signed off; one with no source stays unsigned.
     */
    signOffTeamKpis: (teamId: string, body: { edits: TeamKpiSignOffEdit[] }) =>
        request<{ signedOff: string[] }>(`/v1/teams/${encodeURIComponent(teamId)}/kpis/sign-off`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    /** The team's current memory grant for each layer (#1841): who gave it at clone and when, and any withdrawal. */
    teamMemoryGrants: (teamId: string) =>
        request<TeamMemoryGrants>(`/v1/teams/${encodeURIComponent(teamId)}/memory-grants`),
    /** Withdraw one layer of a team's memory grant (#1841). Notes already written stay. Answers the new grants. */
    withdrawTeamMemoryGrant: (teamId: string, layer: 'team' | 'enterprise') =>
        request<TeamMemoryGrants>(
            `/v1/teams/${encodeURIComponent(teamId)}/memory-grants/${encodeURIComponent(layer)}`,
            { method: 'DELETE' },
        ),
    /**
     * Opens a team template's review: its tasks' missing tools, and the caller's schedule allowance (#1840). The
     * server records `clone.tool_mismatch`.
     */
    /** What sharing one of our teams would publish, and the entry it has (#1844). */
    teamShareReview: (teamId: string) =>
        request<TeamShareReview>(`/v1/catalog/share/teams/${encodeURIComponent(teamId)}`),
    /** Shares one of our teams: its roles where the catalog lacks them, then the team under its slug (#1844). */
    shareTeam: (teamId: string, body: TeamShareInput) =>
        request<{ entry: CatalogTeam; roles: Role[] }>(`/v1/catalog/share/teams/${encodeURIComponent(teamId)}`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    /** How one of our roles' tasks stand with its catalog listing, before it is published and after. */
    roleShareReview: (roleId: string) =>
        request<RoleShareReview>(`/v1/catalog/share/roles/${encodeURIComponent(roleId)}`),
    /** Publishes one of our roles to the catalog by its name and slug (#1844). Answers the public role. */
    publishRole: (sourceRoleId: string) =>
        request<Role>('/v1/catalog/roles', { method: 'POST', body: JSON.stringify({ sourceRoleId }) }),
    catalogTeamReview: (id: string) =>
        request<CatalogTeamCloneReview>(`/v1/catalog/teams/${encodeURIComponent(id)}/review`),
    /** Opens a role template's review for the destination team, when one is chosen. */
    catalogRoleReview: (id: string, teamId?: string) =>
        request<CatalogCloneReview>(
            `/v1/catalog/roles/${encodeURIComponent(id)}/review${teamId ? `?teamId=${encodeURIComponent(teamId)}` : ''}`,
        ),
    catalogTasks: (query: ListQuery = {}) => request<ListResponse<CatalogTask>>(`/v1/catalog/tasks${qs(query)}`),
    /** A linked clone of an entry. It names no team: its role's holders run it, and every team they sit on lists it. */
    cloneCatalogTask: (id: string) => request<TaskRow>(`/v1/catalog/tasks/${id}/clone`, { method: 'POST' }),
    /**
     * Brings this enterprise's linked clone of an entry up to date from the catalog: the source's current version
     * becomes a new version of that clone. No team: an enterprise holds one clone of an entry.
     */
    syncCatalogTask: (id: string) =>
        request<TaskRow>(`/v1/catalog/tasks/${encodeURIComponent(id)}/sync`, { method: 'POST' }),
    copyCatalogTask: (id: string, body: TaskCopyInput) =>
        request<TaskRow>(`/v1/catalog/tasks/${id}/copy`, { method: 'POST', body: JSON.stringify(body) }),
    deprecateCatalogTask: (id: string) => request<CatalogTask>(`/v1/catalog/tasks/${id}`, { method: 'DELETE' }),
    /** Stars a public entry for the caller's enterprise. Idempotent; resolves to the entry with its new counts. */
    starCatalogTask: (id: string) =>
        request<CatalogTask>(`/v1/catalog/tasks/${encodeURIComponent(id)}/star`, { method: 'POST' }),
    /** Removes the caller's enterprise's star; a no-op when it never starred. Resolves to the entry with its new counts. */
    unstarCatalogTask: (id: string) =>
        request<CatalogTask>(`/v1/catalog/tasks/${encodeURIComponent(id)}/star`, { method: 'DELETE' }),
    taskWorkflow: (id: string) =>
        request<{ skillMd: string; version: { version: number; goal: string } }>(`/v1/tasks/${id}/workflow`),
    saveTaskVersion: (id: string, body: Record<string, unknown>) =>
        request<{
            id: string
            version: number
            catalogPublication?: SavedCatalogPublication | null
            /** What the saved version still warns about (`tool_slot_action_undeclared`, #957), when the API says. */
            definitionIssues?: DefinitionIssue[]
        }>(`/v1/tasks/${id}/versions`, { method: 'POST', body: JSON.stringify(body) }),
    taskMemory: (id: string) => request<{ entries: MemoryEntry[] }>(`/v1/tasks/${id}/memory`),
    addTaskMemory: (id: string, text: string) =>
        request<MemoryEntry>(`/v1/tasks/${id}/memory`, { method: 'POST', body: JSON.stringify({ text }) }),
    tombstoneMemory: (id: string) => request<MemoryEntry>(`/v1/memory/${id}/tombstone`, { method: 'POST' }),
    runLog: (id: string) => request<{ key: string; body: string }>(`/v1/runs/${id}/log`),
    /** HQ's board: the company's own KPIs, and each team's key KPI (or none set yet). */
    kpiBoard: () => request<KpiBoard>('/v1/enterprise/kpis'),
    /** A KPI's readings, newest first. */
    kpiReadings: (kpiId: string) => request<KpiReadingRecord[]>(`/v1/kpis/${kpiId}/readings`),
    /** Record a reading by hand. The API records it as taken by the person signed in. */
    recordKpiReading: (kpiId: string, reading: { value: number; takenAt: string }) =>
        request<KpiReadingRecord>(`/v1/kpis/${kpiId}/readings`, { method: 'POST', body: JSON.stringify(reading) }),
    /** A team's page: all of its own KPIs, with its role and task KPIs nested beneath them. */
    teamKpis: (teamId: string) => request<TeamKpiTree>(`/v1/teams/${teamId}/kpis`),
}

/** One enterprise the signed-in person may authorize a connection for (#326). */
export type ConsentEnterpriseOption = { id: string; slug: string; name: string }

/** One AI team member the connection may act as, offered once an enterprise is chosen. */
export type ConsentMemberOption = { id: string; name: string }

export type ConsentApproveInput = {
    enterpriseId: string
    memberId: string
    scopes: string[]
    clientId: string
    clientName?: string | null
    /** Where the client asked to be sent back to. The server decides whether it may be honoured. */
    redirectUri?: string | null
    /** The PKCE S256 challenge and the MCP server the authorize request was bound to (#325). */
    codeChallenge?: string
    codeChallengeMethod?: string
    resource?: string
}

/** `redirectUri` comes back only when the server registered it: the screen redirects to that, or nowhere. */
export type ConsentApproval = { code: string; redirectUri?: string }

export type ConsentDecline = { error: string; clientId?: string; redirectUri?: string }

/** The consent screen's own calls (#326): kept apart from `api` above so that object stays untouched. */
export const consentApi = {
    enterprises: () => request<ConsentEnterpriseOption[]>('/v1/consent/enterprises'),
    members: (enterpriseId: string) => request<ConsentMemberOption[]>(`/v1/consent/members${qs({ enterpriseId })}`),
    approve: (body: ConsentApproveInput) =>
        request<ConsentApproval>('/v1/consent/approve', { method: 'POST', body: JSON.stringify(body) }),
    decline: (body: { clientId: string; redirectUri?: string | null }) =>
        request<ConsentDecline>('/v1/consent/decline', { method: 'POST', body: JSON.stringify(body) }),
}

/** The gate a signed link opens, as the Needs you card shows it (#1778). */
export type GateLinkGate = {
    id: string
    toolName: string
    summary: string | null
    payload: Record<string, unknown>
    createdAt: string
    askedBy: { name: string | null; role: string | null } | null
    /** The task the gate's run belongs to, as the Gates card titles it. Absent or null when the task is gone. */
    taskName?: string | null
    taskSlug?: string | null
}

/**
 * Why a signed link decided nothing: what both opening and posting answer with when they cannot go ahead. `used` is a
 * link already spent whose approval is still waiting: its decision never landed, so it is not "already decided".
 */
type GateLinkRefusal =
    | { state: 'already_decided'; decidedBy: string | null; decidedAt: string | null }
    | { state: 'used' }
    | { state: 'expired' }
    | { state: 'not_allowed' }
    | { state: 'invalid' }

/** What opening a signed Needs you link shows: the gate, or why nothing can be decided with it. */
export type GateLinkView = { state: 'open'; gate: GateLinkGate } | GateLinkRefusal

/** What posting Approve or Reject on a signed link did. */
export type GateLinkOutcome = { state: 'decided'; gate: { status: string; decidedAt: string | null } } | GateLinkRefusal

/** Where one signed link's endpoints live: the token is the credential, so no session is sent for or needed. */
function gateLinkPath(token: string): string {
    return `/v1/gate-links/${encodeURIComponent(token)}`
}

/**
 * The sign-in-free calls behind a Needs you email link (#1778). Opening the link is a read that decides nothing; only
 * `decide`, sent by the page's Approve or Reject, decides, once.
 */
export const gateLinkApi = {
    view: (token: string): Promise<GateLinkView> => request<GateLinkView>(gateLinkPath(token)),
    decide: (token: string, decision: 'approve' | 'reject'): Promise<GateLinkOutcome> =>
        request<GateLinkOutcome>(`${gateLinkPath(token)}/decision`, {
            method: 'POST',
            body: JSON.stringify({ decision }),
        }),
}
